// secure-checkout app entry (FET-2766)
// Extracted from the inline CMS body_js (variant 13585), served as a native
// ES module via mix.copy -> public/js/secure-checkout/app.js. Runtime config
// (google_places_api_key, enable_address_autofill) is read from data-* attrs
// on #checkout-app-new, populated by CMS shortcodes in the page markup.

    import { createApp } from 'petite-vue';
    // FET-2766: US states and Canadian provinces reuse the app-wide shared
    // fixtures in resources/data (webpack inlines the JSON into this bundle), so
    // there's a single source of truth instead of a secure-checkout-local copy.
    import states from '../../data/us-states.json';
    import provinces from '../../data/can-provinces.json';

    // FET-2766: shared checkout logic extracted into tested ES modules.
    // countries.js is kept local (not resources/data/iso3166-countries-3.json)
    // because country-codes.js needs the `alpha-2` field, which that shared
    // fixture omits.
    import countries from './data/countries.js';
    import { getAlpha2CountryCode, getAlpha3CountryCode } from './country-codes.js';
    import { remapForm } from './form.js';
    import {
        convertCurrency as convertCurrencyImpl,
        fetchExchangeRates,
        getCartAmountMinor as resolveCartAmountMinor,
    } from './lib/currency.js';
    import { mergeCartApiPayload } from './lib/cart-state.js';
    import { resolveCustomCheckoutOfferData } from './lib/product-display.js';
    import { resolveSkin } from './lib/skin.js';
    import {
        getDigitalGuaranteeDays,
        isCountdownTimerEnabled,
        resolveGuaranteeBadgeImageUrl,
        resolveGuaranteeBanner,
        resolveGuaranteeTrustImageUrl,
    } from './lib/guarantee-display.js';
    import {
        resolveCardBrand,
        resolveCardLastFour,
    } from './lib/card-display.js';
    import { resolvePaypalEnabled } from './lib/paypal.js';
    import { PayPalUpdateOrderErrors } from './lib/paypal-update-order-errors.js';
    import { extractAdyenBinValue } from './lib/adyen-preauth-bin.js';
    import { restoreScene1PaymentFieldTabOrder, syncPaymentFieldTabOrder } from './lib/payment-field-tab-order.js';
    import { syncNrWelcomeRootClass } from './lib/checkout-root-layout.js';
    import {
        extractPaypalSessionResult,
        buildAdyenWalletAddress,
        buildApplePayPaymentDetails,
        transformApplePayContactToFormAddress,
        transformAdyenWalletDeliveryToFormAddress,
        buildWalletPaymentFailureError,
    } from './lib/wallet.js';

    // Import Payment Gateway Scripts

    // FET-2766: the gateway SDK preload below uses top-level await. The whole app
    // body is wrapped in an async IIFE so this file bundles as a classic
    // (non-module) webpack entry, loaded with `defer` from the
    // empty-checkout-externalized template (deferred = runs after the inline
    // cart-hydration globals, preserving the original module load order).
    ;(async () => {
    // Same object the CMS page hung on window. Assigned here so classic
    // bootstrap.js does not need a second untested copy.
    window.PayPalUpdateOrderErrors = PayPalUpdateOrderErrors;

    const ADYEN_SDK_USER_MESSAGE = 'Payment form failed to load. Please refresh and try again.';

    /** Surface Adyen Web SDK load failures on checkoutAppScope (or defer until scope exists). */
    const setCheckoutAdyenSdkUnavailable = (logMessage, cause) => {
        console.error(logMessage, cause ?? '');
        const root = window.checkoutAppScope;
        if (root) {
            root.checkoutErrors = [ADYEN_SDK_USER_MESSAGE];
        } else {
            window.__pendingCheckoutAdyenSdkError = ADYEN_SDK_USER_MESSAGE;
        }
    };

    const gateway = window.activeGateway();
    if(gateway === 'Braintree' || gateway === 'Braintree_Testing') {
        const braintreeWebModule = await import(/* webpackIgnore: true */ '/js/braintree-3.102.0.js');
        window.braintreeWeb = braintreeWebModule.default || braintreeWebModule.braintreeWeb;
    } else if(['Adyen', 'Adyen_Testing'].includes(gateway)) {

        const adyenUrl = gateway === 'Adyen_Testing'
            ? 'https://checkoutshopper-test.cdn.adyen.com'
            : 'https://checkoutshopper-live-us.cdn.adyen.com';

        // Map globals from mix `/js/adyen.js` (AdyenCheckout + AdyenCard) or the CDN
        // UMD bundle onto the single AdyenWeb namespace checkout code expects.
        const syncAdyenWebNamespace = () => {
            if (window.AdyenWeb?.Card && window.AdyenWeb?.AdyenCheckout) {
                return;
            }
            // CDN bundle sets window.AdyenWeb directly (with Card on the namespace).
            if (window.AdyenWeb?.AdyenCheckout && !window.AdyenWeb?.Card) {
                window.AdyenWeb.Card = window.AdyenWeb.Card || window.AdyenCard;
                window.AdyenWeb.PayPal = window.AdyenWeb.PayPal || window.AdyenPayPal;
                window.AdyenWeb.ApplePay = window.AdyenWeb.ApplePay || window.ApplePay;
                return;
            }
            if (window.AdyenCheckout) {
                window.AdyenWeb = {
                    AdyenCheckout: window.AdyenCheckout,
                    Card: window.AdyenCard,
                    PayPal: window.AdyenPayPal,
                    ApplePay: window.ApplePay,
                };
                return;
            }
            if (window.Adyen) {
                window.AdyenWeb = window.Adyen;
            }
        };

        // Load Adyen CSS stylesheet
        const adyenCssUrl = adyenUrl + '/checkoutshopper/sdk/6.22.0/adyen.css';

        // Check if CSS is already loaded to avoid duplicates
        if (!document.querySelector(`link[href="${adyenCssUrl}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = adyenCssUrl;
            document.head.appendChild(link);
        }

        syncAdyenWebNamespace();

        const adyenJsUrl = adyenUrl + '/checkoutshopper/sdk/6.22.0/adyen.js';

        if (!window.AdyenWeb?.AdyenCheckout) {
            try {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = adyenJsUrl;
                    script.onload = () => {
                        syncAdyenWebNamespace();
                        resolve();
                    };
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
            } catch (error) {
                setCheckoutAdyenSdkUnavailable('[Adyen] Error loading Web SDK from CDN', error);
            }
        }

        if (!window.AdyenWeb?.AdyenCheckout) {
            setCheckoutAdyenSdkUnavailable('[Adyen] Adyen Web SDK is not available after load attempt');
        }
    }

    // --- Google Places Address Autofill (Programmatic AutocompleteSuggestion API) ---
    // Feature gate: reads from page_options via shortcode. Defaults to enabled if blank (not set).
    const _scConfigEl = document.getElementById('checkout-app-new');
    const GOOGLE_PLACES_API_KEY = (_scConfigEl && _scConfigEl.dataset.googlePlacesApiKey) || '';
    const _addressAutofillOption = (_scConfigEl && _scConfigEl.dataset.enableAddressAutofill != null) ? _scConfigEl.dataset.enableAddressAutofill : '';
    const ADDRESS_AUTOFILL_ENABLED = _addressAutofillOption === '' || _addressAutofillOption === '1' || _addressAutofillOption.toLowerCase() === 'true';

    // FET-2766: PayPal availability is page-option driven via the #checkout-app-new
    // `data-paypal-enabled` attribute (see lib/paypal.js). Defaults to ENABLED; set
    // data-paypal-enabled="false" to hide PayPal on a page/environment -- e.g. until
    // the BET-1274 backend fix (PR #3371) is deployed for that environment.
    const _paypalEnabledOption = (_scConfigEl && _scConfigEl.dataset.paypalEnabled != null) ? _scConfigEl.dataset.paypalEnabled : '';
    const PAYPAL_DISABLED = !resolvePaypalEnabled(_paypalEnabledOption);
    const filterDisabledPaymentMethods = (methods) => {
        if (!PAYPAL_DISABLED || !Array.isArray(methods)) {
            return methods;
        }
        return methods.filter((method) => method !== 'paypal');
    };

    // FET-2766: page-option skin overrides. The brand-level skin (logo, guarantee
    // imagery, legal links, input styling, countdown timer) is baked into the
    // bundle via lib/skin.js; the few values that vary per page can be supplied as
    // a JSON `data-checkout-skin` attribute populated by a CMS page-option shortcode.
    let _skinPageOptions = null;
    const _skinPageOptionsRaw = _scConfigEl && _scConfigEl.dataset.checkoutSkin;
    if (_skinPageOptionsRaw) {
        try {
            _skinPageOptions = JSON.parse(_skinPageOptionsRaw);
        } catch (e) {
            console.warn('[checkout] Ignoring malformed data-checkout-skin page option:', e);
        }
    }

    // Lazy-load Google Maps JS API on first use (not at page load).
    // Uses the Google-recommended bootstrap loader pattern which only fetches
    // the actual library code when importLibrary() is called.
    let _googlePlacesPromise = null;
    function loadGooglePlacesAPI() {
        if (!ADDRESS_AUTOFILL_ENABLED) return Promise.reject('Address autofill is disabled');
        if (!GOOGLE_PLACES_API_KEY) {
            console.warn('[checkout] Address autofill is enabled but google_places_api_key is missing on #checkout-app-new (data-google-places-api-key or page option).');
            return Promise.reject('Google Places API key is not configured');
        }
        if (window.google?.maps?.places?.AutocompleteSuggestion) return Promise.resolve();
        if (_googlePlacesPromise) return _googlePlacesPromise;

        _googlePlacesPromise = new Promise((resolve, reject) => {
            // Install the Google Maps bootstrap loader (tiny, sets up importLibrary)
            if (!window.google?.maps?.importLibrary) {
                ((g) => {
                    var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary",
                        q = "__ib__", m = document, b = window;
                    b = b[c] || (b[c] = {});
                    var d = b.maps || (b.maps = {}), r = new Set, e = new URLSearchParams,
                        u = () => h || (h = new Promise(async (f, n) => {
                            await (a = m.createElement("script"));
                            e.set("libraries", [...r] + "");
                            for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]);
                            e.set("callback", c + ".maps." + q);
                            a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
                            d[q] = f;
                            a.onerror = () => h = n(Error(p + " could not load."));
                            a.nonce = m.querySelector("script[nonce]")?.nonce || "";
                            m.head.append(a);
                        }));
                    d[l] ? console.warn(p + " only loads once. Ignoring:", g) :
                        d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n));
                })({ key: GOOGLE_PLACES_API_KEY, v: "weekly" });
            }

            // Load the places library on demand
            window.google.maps.importLibrary('places').then(resolve).catch((e) => {
                _googlePlacesPromise = null;
                reject(e);
            });
        });
        return _googlePlacesPromise;
    }

    /**
     * Initialize Google Places Autocomplete on an existing address input using the
     * programmatic AutocompleteSuggestion API. Attaches directly to the input,
     * builds a custom dropdown, and handles keyboard navigation.
     *
     * @param {string} inputId - The DOM id of the address input (e.g. 'billing_address')
     * @param {object} component - The Petite Vue component instance (BillingShipping)
     * @param {string} formPath - The form data path: 'billing_address' or 'visual_shipping'
     * @returns {object|null} Controller object with cleanup method
     */
    function initAddressAutocomplete(inputId, component, formPath) {
        const addressInput = document.getElementById(inputId);
        if (!addressInput || !window.google?.maps?.places?.AutocompleteSuggestion) return null;

        const { AutocompleteSuggestion, AutocompleteSessionToken } = google.maps.places;

        // State
        let sessionToken = new AutocompleteSessionToken();
        let debounceTimer = null;
        let newestRequestId = 0;
        let activeSuggestions = [];
        let highlightedIndex = -1;

        // Create the dropdown container
        const dropdown = document.createElement('ul');
        dropdown.className = 'address-autocomplete-dropdown';
        dropdown.setAttribute('role', 'listbox');
        dropdown.id = inputId + '_suggestions';
        addressInput.setAttribute('role', 'combobox');
        addressInput.setAttribute('aria-autocomplete', 'list');
        addressInput.setAttribute('aria-controls', dropdown.id);
        addressInput.setAttribute('aria-expanded', 'false');

        // Position the dropdown relative to the input's fieldset
        const fieldset = addressInput.closest('fieldset');
        if (fieldset) {
            fieldset.style.position = 'relative';
            fieldset.appendChild(dropdown);
        }

        function showDropdown() {
            dropdown.style.display = 'block';
            addressInput.setAttribute('aria-expanded', 'true');
        }

        function hideDropdown() {
            dropdown.style.display = 'none';
            addressInput.setAttribute('aria-expanded', 'false');
            highlightedIndex = -1;
        }

        function renderSuggestions(suggestions) {
            activeSuggestions = suggestions;
            dropdown.innerHTML = '';
            highlightedIndex = -1;

            if (suggestions.length === 0) {
                hideDropdown();
                return;
            }

            suggestions.forEach((suggestion, index) => {
                const pred = suggestion.placePrediction;
                if (!pred) return;

                const li = document.createElement('li');
                li.setAttribute('role', 'option');
                li.setAttribute('aria-selected', 'false');
                li.className = 'address-autocomplete-item';

                // Main text (bold) + secondary text
                const mainSpan = document.createElement('span');
                mainSpan.className = 'address-autocomplete-main';
                mainSpan.textContent = pred.mainText?.toString() || '';
                li.appendChild(mainSpan);

                if (pred.secondaryText) {
                    const secSpan = document.createElement('span');
                    secSpan.className = 'address-autocomplete-secondary';
                    secSpan.textContent = ' ' + pred.secondaryText.toString();
                    li.appendChild(secSpan);
                }

                li.addEventListener('mousedown', (e) => {
                    e.preventDefault(); // Prevent input blur
                    selectSuggestion(index);
                });

                li.addEventListener('mouseenter', () => {
                    setHighlight(index);
                });

                dropdown.appendChild(li);
            });

            showDropdown();
        }

        function setHighlight(index) {
            const items = dropdown.querySelectorAll('.address-autocomplete-item');
            items.forEach((item, i) => {
                if (i === index) {
                    item.classList.add('highlighted');
                    item.setAttribute('aria-selected', 'true');
                    addressInput.setAttribute('aria-activedescendant', item.id || '');
                } else {
                    item.classList.remove('highlighted');
                    item.setAttribute('aria-selected', 'false');
                }
            });
            highlightedIndex = index;
        }

        async function selectSuggestion(index) {
            const suggestion = activeSuggestions[index];
            if (!suggestion?.placePrediction) return;

            hideDropdown();

            try {
                const place = suggestion.placePrediction.toPlace();
                // ONLY fetch addressComponents -- controls billing (cheapest tier)
                await place.fetchFields({ fields: ['addressComponents'] });

                // Refresh session token after fetchFields ends the session
                sessionToken = new AutocompleteSessionToken();

                if (!place.addressComponents) return;

                // Parse address components (New API uses longText/shortText, types.includes())
                let streetNumber = '';
                let route = '';
                let city = '';
                let state = '';
                let country = '';
                let postalCode = '';

                for (const comp of place.addressComponents) {
                    if (comp.types.includes('street_number')) {
                        streetNumber = comp.longText;
                    }
                    if (comp.types.includes('route')) {
                        route = comp.shortText;
                    }
                    if (comp.types.includes('locality')) {
                        city = comp.longText;
                    }
                    if (!city && comp.types.includes('sublocality_level_1')) {
                        city = comp.longText; // NYC boroughs fallback
                    }
                    if (!city && comp.types.includes('postal_town')) {
                        city = comp.longText; // UK/Sweden fallback
                    }
                    if (comp.types.includes('administrative_area_level_1')) {
                        state = comp.shortText; // e.g. "CA", "NY", "ON"
                    }
                    if (comp.types.includes('country')) {
                        country = comp.shortText; // Alpha-2 code, e.g. "US", "CA"
                    }
                    if (comp.types.includes('postal_code')) {
                        postalCode = comp.longText;
                    }
                }

                const addressLine1 = streetNumber ? `${streetNumber} ${route}` : route;

                // Set country first so the state dropdown re-renders for US/CA
                component.form[formPath].country = country;

                // Allow Petite Vue to re-render state field (select vs input)
                setTimeout(() => {
                    component.form[formPath].address = addressLine1;
                    component.form[formPath].city = city;
                    component.form[formPath].state = state;
                    component.form[formPath].postal_code = postalCode;

                    // Trigger the existing address sync debounce
                    component.addressFieldUpdated();

                    // Move focus to address line 2 to encourage apt/suite entry
                    // const addr2Id = formPath === 'billing_address' ? 'billing_address_2' : 'shipping_address_2';
                    // const addr2Input = document.getElementById(addr2Id);
                    // if (addr2Input) addr2Input.focus();
                }, 50);
            } catch (e) {
                console.warn('Error processing place selection', e);
            }
        }

        async function fetchSuggestions(inputValue) {
            if (!inputValue || inputValue.length < 3) {
                hideDropdown();
                return;
            }

            const requestId = ++newestRequestId;

            try {
                const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: inputValue,
                    includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
                    sessionToken,
                });

                // Discard stale responses
                if (requestId !== newestRequestId) return;

                renderSuggestions(suggestions);
            } catch (e) {
                if (requestId === newestRequestId) hideDropdown();
                if (e && (e.name === 'RpcError' || String(e).includes('403'))) {
                    console.warn('[checkout] Google Places Autocomplete request failed (403 often means the API key is missing, Places API (New) is disabled, or HTTP referrer restrictions exclude this host).', e);
                }
            }
        }

        // Debounced input handler
        function onInput(e) {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                fetchSuggestions(e.target.value);
            }, 300);
        }

        // Keyboard navigation
        function onKeyDown(e) {
            if (dropdown.style.display === 'none') return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlight(Math.min(highlightedIndex + 1, activeSuggestions.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setHighlight(Math.max(highlightedIndex - 1, 0));
                    break;
                case 'Enter':
                    if (highlightedIndex >= 0) {
                        e.preventDefault();
                        selectSuggestion(highlightedIndex);
                    }
                    break;
                case 'Escape':
                    hideDropdown();
                    break;
            }
        }

        // Close dropdown when clicking outside
        function onDocClick(e) {
            if (!fieldset?.contains(e.target)) {
                hideDropdown();
            }
        }

        // Attach listeners
        addressInput.addEventListener('input', onInput);
        addressInput.addEventListener('keydown', onKeyDown);
        addressInput.addEventListener('focus', () => {
            if (activeSuggestions.length > 0 && addressInput.value.length >= 3) {
                showDropdown();
            }
        });
        document.addEventListener('click', onDocClick);

        // Return controller for cleanup
        return {
            destroy() {
                addressInput.removeEventListener('input', onInput);
                addressInput.removeEventListener('keydown', onKeyDown);
                document.removeEventListener('click', onDocClick);
                clearTimeout(debounceTimer);
                dropdown.remove();
            }
        };
    }
    // --- End Google Places Address Autofill ---

    const detectCountry = async (form, paypalOptions, geolocateEnabled) => {
        if (geolocateEnabled) {
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                form.billing_address.country = data.country_code;
                form.shipping_address.country = data.country_code;
                form.visual_shipping.country = data.country_code;
                paypalOptions.currency = data.currency;
                return data;
            } catch (error) {
                console.error('Failed to detect country', error);
            }
        }
    };

    const getCookie = function (name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    };

	const getMetaCsrfToken = function () {
		const meta = document.head && document.head.querySelector('meta[name="csrf-token"]');

		return meta && meta.content ? meta.content : '';
	};

	const getHeaders = function () {
		const headers = new Headers();
		headers.append('current-page', (window.location && window.location.pathname) || '');
		// change from OTT-2469
		headers.append('X-CSRF-TOKEN', document.head?.querySelector('meta[name="csrf-token"]')?.content ?? '');

		// change from OTT-2408
		const xsrf = getCookie('XSRF-TOKEN');
		if (xsrf) {
			headers.append('X-XSRF-TOKEN', decodeURIComponent(xsrf));
		} else {
			const metaToken = getMetaCsrfToken();
			if (metaToken) {
				headers.append('X-CSRF-TOKEN', metaToken);
			}
		}

		headers.append('Accept', 'application/json');
		headers.append('Content-Type', 'application/json;charset=UTF-8');
		headers.append('source', 'vshred');
		return headers;
	}

    // PayPal debug instrumentation (NO-OP STUBS) -------------------------
    //
    // The full FE diagnostic logging was used to root-cause the cart
    // resolution + 422 reconciliation bugs landed earlier on this branch.
    // Once the bugs were fixed the live console/Datadog noise (~30
    // addDDAction calls per PayPal flow + cart snapshots) became net
    // negative — it pollutes the user's devtools console, costs RUM
    // quota on every session, and ships extra KB to every visitor.
    //
    // The call sites scattered through this file (~80+) are intentionally
    // preserved so the instrumentation can be flipped back on in one
    // place if a related regression surfaces: just inline the original
    // console.info / addDDAction bodies back into these stubs.
    // BE counterpart is gated by env('PAYPAL_DEBUG_LOG_ENABLED').
    const startPaypalDebugFlow = function () {};
    const endPaypalDebugFlow = function () {};
    const paypalDebug = function () {};
    const snapshotCartForDebug = function () { return {}; };

    /**
     * Adyen PayPal Sessions express checkout: the SDK authorizes inside the
     * lightbox (onPaymentCompleted). Our app must only finalize the order via
     * /api/cart/pay with sessionResult — never fire a second authorization with
     * paymentMethod / getToken() payloads.
     */
    const resetAdyenPayPalCheckoutGuards = function (app) {
        if (!app) {
            return;
        }
        app._adyenPayPalCheckoutInFlight = false;
        app._adyenPayPalSessionAuthorized = false;
        app._adyenPayPalOrderFinalized = false;
    };

    const releaseAdyenPayPalCheckoutLock = function (app) {
        if (app) {
            app._adyenPayPalCheckoutInFlight = false;
        }
    };

    // Add a timeout wrapper for fetch requests to prevent hanging
    const fetchWithTimeout = async (url, options, timeoutMs = 10000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error('Request timed out. Please try again.');
            }
            throw error;
        }
    };

    const managed = (path) => {
        return true;
    };

    const ADYEN_SCHEME_ENCRYPTED_KEYS = [
        'encryptedCardNumber',
        'encryptedExpiryMonth',
        'encryptedExpiryYear',
        'encryptedSecurityCode',
    ];

    const syncApplePayVisualShipping = (form) => {
        if (form.shipping_same === true) {
            return;
        }

        form.visual_shipping = {
            ...form.shipping_address,
            name: form.shipping_address.name || form.user.name,
            email: form.user.email,
            phone: form.shipping_address.phone || form.user.phone,
        };
    };

    const applyApplePayWalletAddressesToForm = (form, {
        billingAddress = null,
        deliveryAddress = null,
        shippingContact = null,
        billingContact = null,
    } = {}) => {
        let billing = billingContact
            ? transformApplePayContactToFormAddress(billingContact, form.billing_address)
            : transformAdyenWalletDeliveryToFormAddress(billingAddress, form.billing_address);

        let shipping = shippingContact
            ? transformApplePayContactToFormAddress(shippingContact, form.shipping_address)
            : transformAdyenWalletDeliveryToFormAddress(deliveryAddress, form.shipping_address);

        if (!shipping?.postal_code && billing?.postal_code) {
            shipping = billing ? { ...billing } : shipping;
        }

        if (billing) {
            form.billing_address = billing;
        }
        if (shipping) {
            form.shipping_address = shipping;
        }

        form.shipping_same = JSON.stringify(form.billing_address) === JSON.stringify(form.shipping_address);
        syncApplePayVisualShipping(form);
    };

    // Thin wrapper: delegates the money math to the unit-tested
    // lib/currency.js module, forwarding the per-component state it needs.
    // Kept as a component-bound function so existing `this.convertCurrency(...)`
    // call sites and component-data registrations work unchanged.
    const convertCurrency = function (cart, conversionRates, countryCode) {
        return convertCurrencyImpl(cart, conversionRates, countryCode, {
            currencyConvertEnabled: this.currencyConvertEnabled,
            defaultCountry: this.defaultCountry,
            countriesList: this.countriesList,
        });
    }

    const applyCheckoutCartApiPayload = function (ctx, incomingCart) {
        if (!incomingCart || typeof incomingCart !== 'object') {
            return null;
        }

        const merged = mergeCartApiPayload(ctx.cartState ?? ctx.checkoutOffer, incomingCart);
        const countryCode = ctx.form?.billing_address?.country ?? ctx.defaultCountry;
        const processed = ctx.convertCurrency(merged, ctx.countriesList, countryCode);
        ctx.checkoutOffer = processed;
        ctx.cartState = processed;

        return processed;
    };

    // Fetch available Adyen payment methods and map to check out payment processors
    async function fetchAdyenPaymentMethods(component) {
        try {
            const result = await window.CheckoutAPI.get(component, '/api/adyen/payment/methods', {
                context: { operation: 'fetch_adyen_payment_methods' },
                headers: getHeaders()
            });

            if (!result.success) {
                throw new Error('Failed to fetch Adyen payment methods');
            }

            const paymentMethodsResponse = result.data;
            const availableMethods = [];

            // Map Adyen payment method types to check out payment processors
            if (paymentMethodsResponse.paymentMethods) {
                paymentMethodsResponse.paymentMethods.forEach(method => {
                    switch (method.type) {
                        case 'scheme':
                            availableMethods.push('credit_card');
                            break;
                        case 'paypal':
                            availableMethods.push('paypal');
                            break;
                        case 'applepay':
                            availableMethods.push('apple_pay');
                            break;
                    }
                });
            }

            // Enforce a deterministic display order. Card is pre-selected by default, so it
            // leads the list; Apple Pay and PayPal follow regardless of how Adyen returns them.
            const displayOrder = ['credit_card', 'apple_pay', 'paypal'];
            const unique = [...new Set(availableMethods)];
            const ordered = unique.sort((a, b) => displayOrder.indexOf(a) - displayOrder.indexOf(b));
            return {
                availableMethods: filterDisabledPaymentMethods(ordered),
                paymentMethodsData: paymentMethodsResponse,
            };
        } catch (error) {
            console.error('Error fetching Adyen payment methods:', error);
            // Return default methods if fetch fails
            return {
                availableMethods: filterDisabledPaymentMethods(['credit_card', 'paypal']),
                paymentMethodsData: null,
            };
        }
    }

    const ADYEN_SESSION_EXPIRED_PATTERN = /session has expired/i;
    function isAdyenSessionExpiredError(error) {
        if (typeof error === 'string') {
            return ADYEN_SESSION_EXPIRED_PATTERN.test(error);
        }
        if (!error || typeof error.message !== 'string') {
            return false;
        }
        return ADYEN_SESSION_EXPIRED_PATTERN.test(error.message);
    }
    async function runWithAdyenSessionRetry(callback, options = {}) {
        try {
            return await callback({ isRetry: false });
        } catch (error) {
            if (!isAdyenSessionExpiredError(error)) {
                throw error;
            }
            if (typeof options.onRetry === 'function') {
                options.onRetry(error);
            }
            return callback({ isRetry: true });
        }
    }

    // NEW VUE COMPONENTS START
    const CartOrder = function (props) {
        return {
            $template: '#cartorder-template',
            cart: props.cartState,
            checkoutOffer: props.cartState,
            visible: true,
            cartSpecificCart: props.cartState,
            get customCheckoutOfferData() {
                return resolveCustomCheckoutOfferDataForCartSticky(this.checkoutOffer);
            },
            mounted() {
            }
        }
    }
    const OrderSummaryV3 = function (props) {
        return {
            $template: '#ordersummary-template',
            cart: props.cartState,
            checkoutOffer: props.cartState,
            visible: true,
            tokenized: false,
            canSkip: false,
            cartSpecificCart: props.cartState,
            get customCheckoutOfferData() {
                return resolveCustomCheckoutOfferDataForCartSticky(this.checkoutOffer);
            },
            convertCurrency,
            remapForm,
            getHeaders,
            mounted() {
            },
            async next() {
                try {
                    this.validateContact();
                } catch (e) {
                    console.error('Error in validateContact:', e);
                }

                try {
                    this.validateCCFields(); // Ensure payment field validation runs
                } catch (e) {
                    console.error('Error in validateCCFields:', e);
                }

                this.tokenized = false;
                if (this.contactErrors.length > 0 || this.cardValidationErrors.length > 0) {
                    addDDAction(`contact-invalid`, { processor: this.payment_processor, errors: this.contactErrors });

                    requestAnimationFrame(() => {
                        const firstInvalidElement = document.querySelector('[aria-invalid="true"]');
                        if (firstInvalidElement) {
                            const yOffset = -50; // offset
                            const y = firstInvalidElement.getBoundingClientRect().top + window.scrollY + yOffset;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                    });

                    return;
                }

                try {
                    this.clearAllErrors();
                    this.ccTokenizing = true;
                    await window.CheckoutRequestController.runCheckoutStep('pre-authorization', async () => {
                        const headers = getHeaders();
                        const {
                            token,
                            gateway,
                            details
                        } = await this.getToken();
                        if (token === undefined && gateway === undefined) {
                            this.ccTokenizing = false;
                            await this.handleError({ code: 'INVALID_CARD_DETAILS', message: 'Please fill in all required payment details before continuing.' });
                            return;
                        }
                        this.form.payment.gateway = gateway;
                        this.form.payment.token = token;
                        this.form.payment.details = details;
                        if (this.isAdyenGateway && this.payment_processor === 'credit_card') {
                            const cardholderName = this.cardState?.data?.holderName ?? '';
                            this.setPreAuthIfComplete(cardholderName);
                            this.warnIfPreAuthIncomplete();
                        }
                        const result = await window.CheckoutAPI.post(this, '/api/cart/pre-auth', this.remapForm(this.form), {
                            context: { operation: 'payment_pre_auth' },
                            headers: headers,
                        });

                        if (result.success) {
                            this.tokenized = true;
                            this.ccTokenizing = false;
                            this.processing = false;
                        } else if (!result.handled) {
                            // Our system didn't handle error, throw for legacy compatibility
                            const firstError = this.paymentErrors[0] || this.checkoutErrors[0] || 'Payment tokenization failed';
                            throw new Error(firstError);
                        } else {
                            // The error was handled by CheckoutAPI, but payment failed
                            //  to Hide overlays and stop execution
                            this.ccTokenizing = false;
                            this.processing = false;

                            // Scroll to error
                            requestAnimationFrame(() => {
                                const errorElement = document.querySelector('.tokenization-errors, .checkout-errors, .payment-errors');
                                if (errorElement) {
                                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                            });
                        }
                    });
                } catch (error) {
                    addDDAction(`cc-tokenize-failed`, {
                        'detail': error
                    });
                    this.ccTokenizing = false;
                    this.processing = false;
                    console.error('Failed to tokenize payment', error);

                    // Display the error to the user
                    if (this.paymentErrors && this.paymentErrors.length > 0) {
                        // Errors already populated by CheckoutAPI error handler
                        console.log('Payment errors:', this.paymentErrors);
                    } else if (this.checkoutErrors && this.checkoutErrors.length > 0) {
                        // Errors already populated by CheckoutAPI error handler
                        console.log('Checkout errors:', this.checkoutErrors);
                    } else {
                        // Fallback error message
                        this.paymentErrors = [error.message || 'Payment validation failed. Please check your card details.'];
                    }

                    // Scroll to error
                    requestAnimationFrame(() => {
                        const errorElement = document.querySelector('.tokenization-errors, .checkout-errors, .payment-errors');
                        if (errorElement) {
                            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });

                    return; // Stop execution
                }

                if (this.contactErrors.length <= 0 && this.tokenized) {
                    addDDAction(`contact-form-validated`, {});
                    this.processing = true;
                    this.clearAllErrors();

                    const addressSyncResult = await this.syncAddresses();
                    if (addressSyncResult === false) {
                        this.processing = false;
                        return;
                    }

                    this.scene1CanTransition = true;
                    this.scene1Pos = "left";

                    if (this.canSkip) {
                        this.scene2CanTransition = true;
                        this.scene2Pos = "center";
                    } else {
                        this.scene2CanTransition = true;
                        this.scene2Pos = "center";
                    }
                    window.scrollTo(0, 0);
                    //STARTING CART TOKENIZATION
                    setTimeout(() => {
                        if (this.canSkip) {
                            this.currentScene = 2;
                        } else {
                            this.currentScene = 2;
                        }
                        syncPaymentFieldTabOrder(2);
                    }, 75);
                    setTimeout(() => {
                        this.scene1CanTransition = false;
                        this.scene1Top = true;
                        if (this.canSkip) {
                            this.scene2CanTransition = false;
                            this.scene2Top = false;
                        } else {
                            this.scene2CanTransition = false;
                            this.scene2Top = false;
                        }
                    }, 500);

                    addDDAction(`step-1-continue`, { processor: this.payment_processor });
                    addDDAction(`step-2-start`, { processor: this.payment_processor });
                    this.ccTokenizing = false;
                    this.processing = false;
                }
            },
            async handleError(error) {
                const validationErrors = [];
                this.contactDetailErrors.card.number = false;
                this.contactDetailErrors.card.expirationDate = false;
                this.contactDetailErrors.card.cvv = false;

                if (error.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
                    error.details.invalidFieldKeys.forEach(key => validationErrors.push(key));
                    error.details.invalidFieldKeys.forEach(key => {
                        if (key === "number") {
                            this.contactDetailErrors.card.number = { message: 'Enter a valid card number.' };
                        }
                        if (key === "expirationDate") {
                            this.contactDetailErrors.card.expirationDate = { message: 'Enter a valid expiration date (MM/YYYY).' };
                        }
                        if (key === "cvv") {
                            this.contactDetailErrors.card.cvv = { message: 'CVV should be 3-4 digits.' };
                        }
                    });
                } else if (error.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
                    validationErrors.push('number');
                    this.contactDetailErrors.card.number = { message: 'Please enter your card number.' };
                    validationErrors.push('expirationDate');
                    this.contactDetailErrors.card.expirationDate = { message: 'Please enter your card\'s expiration date.' };
                    validationErrors.push('cvv');
                    this.contactDetailErrors.card.cvv = { message: 'Please enter your card\'s security code (CVV).' };
                } else if (error.code === 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED') {
                    validationErrors.push('cvv');
                    this.contactDetailErrors.card.cvv = { message: 'CVV should be 3-4 digits.' };
                } else if (error.code === 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE') {
                    validationErrors.push('number');
                    this.contactDetailErrors.card.number = { message: 'Enter a valid card number.' };
                } else if (error.code === 'INVALID_CARD_DETAILS') {
                    validationErrors.push('number');
                    this.contactDetailErrors.card.number = { message: 'Please enter your card number.' };
                } else {
                    return window.location.reload();
                }
                this.cardValidationErrors = validationErrors;
            },
            async next_paypal() {
                // "Continue with PayPal" on Scene 1 advances the user to
                // Scene 2 — that's where the .adyen-paypal-container lives
                // and where the PayPal button is rendered. Mirrors the
                // Apple Pay flow above; PayPal also requires its own button
                // click on Scene 2 to complete payment.
                if (this.payment_processor !== 'paypal') {
                    return;
                }
                if (this.processing) {
                    return;
                }

                try {
                    this.validateContact();
                } catch (e) {
                    console.error('Error in validateContact (paypal):', e);
                }

                if (this.contactErrors.length <= 0) {
                    addDDAction(`contact-form-valid`, {});
                    this.processing = true;
                    // Phone lock: snapshot the user-typed phone the
                    // moment validation passes so we can guarantee it
                    // reaches the backend even if anything in the
                    // PayPal wallet flow later clears it (see
                    // enforcePhoneLock helper for the full rationale).
                    if (this.form.user.phone && this.form.user.phone.trim()) {
                        this._lockedUserPhone = this.form.user.phone;
                    }
                    this.phoneStateSnapshot('post-validation-paypal');
                } else {
                    addDDAction(`contact-invalid`, {
                        processor: this.payment_processor,
                        errors: this.contactErrors,
                    });
                    requestAnimationFrame(() => {
                        const firstInvalidElement = document.querySelector('[aria-invalid="true"]');
                        if (firstInvalidElement) {
                            const yOffset = -50;
                            const y = firstInvalidElement.getBoundingClientRect().top + window.scrollY + yOffset;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                    });
                    return;
                }

                // IMPORTANT: do NOT call syncAddresses() here on the 2-step
                // checkout. The BillingShipping form is intentionally hidden
                // (PayPal collects the wallet's address inside the
                // lightbox) so syncAddresses() would PATCH /api/cart with
                // an empty shipping_address — that wipes the cart's
                // shipping_address_id, blanks the subtotal, and ultimately
                // produces a $1 Adyen session with no line items. The
                // shipping address is set later from the PayPal authorized
                // payload inside onAuthorized / onSubmit, and the cart is
                // pre-conditioned for the lightbox by
                // ensureCartShippingForPaypalLightbox().
                paypalDebug('next_paypal.entry', { has_user_name: !!(this.form.user && this.form.user.name) });

                this.processing = false;

                // Mirror billing name from the contact form so the backend
                // has a complete billing_address record before tokenization
                // (PayPal will later overwrite this with the wallet's
                // billing contact if the user has one).
                if (this.form.user.name) {
                    this.form.billing_address.name = this.form.user.name;
                    this.form.billing_address.first_name = this.form.user.name.split(' ')[0];
                    this.form.billing_address.last_name = this.form.user.name.split(' ')[1];
                }

                this.clearAllErrors();

                this.scene1CanTransition = true;
                this.scene1Pos = "left";
                this.scene2CanTransition = true;
                this.scene2Pos = "center";
                window.scrollTo(0, 0);

                setTimeout(() => {
                    this.currentScene = 2;
                    syncPaymentFieldTabOrder(2);
                }, 75);
                setTimeout(() => {
                    this.scene1CanTransition = false;
                    this.scene1Top = true;
                    this.scene2CanTransition = false;
                    this.scene2Top = false;
                }, 500);

                addDDAction(`step-1-continue`, { processor: this.payment_processor });
                addDDAction(`step-2-start`, { processor: this.payment_processor });

                // Mount the PayPal component now that Scene 2's
                // .adyen-paypal-container is being rendered. createAdyenPayPalComponent
                // awaits ~250ms internally so the v-if container has time to mount.
                if (this.isAdyenGateway) {
                    await this.createAdyenPayPalComponent();
                } else {
                    await this.createPaypalInstance();
                }
            },

            async next_apple_pay() {
                // "Continue with Apple Pay" on Scene 1 advances the user to
                // Scene 2 — that's where the .adyen-applepay-container lives
                // and where the actual Apple Pay button is rendered. Apple
                // Pay must come from a real Apple Pay button click (Apple
                // requires the user gesture), so the wallet sheet opens from
                // Scene 2, not from this button.
                if (this.processing) {
                    return;
                }

                try {
                    this.validateContact();
                } catch (e) {
                    console.error('Error in validateContact (apple_pay):', e);
                }

                if (this.contactErrors.length <= 0) {
                    addDDAction(`contact-form-valid`, {});
                    this.processing = true;
                    // Phone lock: snapshot the user-typed phone the
                    // moment validation passes so we can guarantee it
                    // reaches the backend even if anything in the
                    // Apple Pay wallet flow later clears it (see
                    // enforcePhoneLock helper for the full rationale).
                    if (this.form.user.phone && this.form.user.phone.trim()) {
                        this._lockedUserPhone = this.form.user.phone;
                    }
                    this.phoneStateSnapshot('post-validation-apple_pay');
                } else {
                    addDDAction(`contact-invalid`, {
                        processor: this.payment_processor,
                        errors: this.contactErrors,
                    });
                    requestAnimationFrame(() => {
                        const firstInvalidElement = document.querySelector('[aria-invalid="true"]');
                        if (firstInvalidElement) {
                            const yOffset = -50;
                            const y = firstInvalidElement.getBoundingClientRect().top + window.scrollY + yOffset;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                    });
                    return;
                }

                // Pre-sync the cart's shipping address from the form before
                // the Adyen Apple Pay session is created. The working control
                // (page 12168) does this and without it /api/cart/pay later
                // hangs because the cart has no shipping_address_id when the
                // Apple Pay token is posted.
                const addressSyncResult = await this.syncAddresses();
                if (addressSyncResult === false) {
                    this.processing = false;
                    return;
                }

                this.processing = false;

                // Mirror billing name from the contact form so the backend
                // has a complete billing_address record before tokenization
                // (Apple Pay's wallet will later overwrite this with the
                // wallet's billing contact if the user has one).
                if (this.form.user.name) {
                    this.form.billing_address.name = this.form.user.name;
                    this.form.billing_address.first_name = this.form.user.name.split(' ')[0];
                    this.form.billing_address.last_name = this.form.user.name.split(' ')[1];
                }

                this.clearAllErrors();

                this.scene1CanTransition = true;
                this.scene1Pos = "left";
                this.scene2CanTransition = true;
                this.scene2Pos = "center";
                window.scrollTo(0, 0);

                setTimeout(() => {
                    this.currentScene = 2;
                    syncPaymentFieldTabOrder(2);
                }, 75);
                setTimeout(() => {
                    this.scene1CanTransition = false;
                    this.scene1Top = true;
                    this.scene2CanTransition = false;
                    this.scene2Top = false;
                }, 500);

                addDDAction(`step-1-continue`, { processor: this.payment_processor });
                addDDAction(`step-2-start`, { processor: this.payment_processor });

                // Mount the Adyen Apple Pay component now that Scene 2's
                // .adyen-applepay-container is being rendered. The function
                // already awaits ~250ms internally, which is enough time for
                // Vue to flush the currentScene state change above and for
                // the v-if container to appear in the DOM.
                if (this.isAdyenGateway) {
                    await this.createAdyenApplePayComponent();
                }
            },

            async handleApplePayClick(event) {
                // Prevent double-clicks
                if (this.processing) {
                    return;
                }

                // For Adyen gateway, ensure an Apple Pay component is initialized
                if (this.isAdyenGateway) {
                    try {
                        // Find the visible container (there may be multiple due to desktop/mobile order summary instances)
                        const containers = document.querySelectorAll('.adyen-applepay-container');
                        let container = null;
                        for (const el of containers) {
                            if (el.offsetParent !== null) {
                                container = el;
                                break;
                            }
                        }
                        if (!container) container = containers[0] || null;
                        if (!container) {
                            console.error('[Apple Pay] Container not found');
                            return;
                        }

                        // If the component doesn't exist or the container is empty, create it
                        if (!this.adyenApplePayComponent || !container.querySelector('button')) {
                            await this.createAdyenApplePayComponent();
                            // Wait a bit for the component to render
                            await new Promise(resolve => setTimeout(resolve, 100));
                        }

                        // The Adyen component should handle the click via its own button
                        // Find and trigger the Adyen button
                        const adyenButton = container.querySelector('button');
                        if (adyenButton && !adyenButton.disabled) {
                            // Stop event propagation to prevent the wrapper button from interfering
                            if (event) {
                                event.stopPropagation();
                                event.preventDefault();
                            }
                            // Trigger click on the Adyen button
                            adyenButton.click();
                        } else {
                            // Fallback: try to create a component again
                            console.warn('[Apple Pay] Adyen button not found, recreating component');
                            await this.createAdyenApplePayComponent();
                        }
                    } catch (error) {
                        console.error('[Apple Pay] Error handling click:', error);
                        this.checkoutErrors = [error.message || 'Failed to initialize Apple Pay'];
                        this.processing = false;
                    }
                } else {
                    // For non-Adyen gateways, handle differently if needed
                    console.warn('[Apple Pay] Non-Adyen gateway not fully implemented for scene 2');
                }
            },

            async syncAddresses() {
                return window.CheckoutRequestController.runExclusive('address-sync', () => this._performAddressSync());
            },

            async _performAddressSync() {
                this.clearAllErrors();
                this.addressSyncing = true;

                try {

                    if (this.payment_processor === 'paypal' || this.payment_processor === 'apple_pay') {
                        // Wallet express flows populate shipping_address from the
                        // payment sheet; do not rebuild it from billing_address
                        // or visual_shipping.
                        this.form.shipping_address.name = this.form.shipping_address.name || this.form.user.name;
                        this.form.shipping_address.email = this.form.shipping_address.email || this.form.user.email;
                        if (!this.form.shipping_address.phone) {
                            this.form.shipping_address.phone = this.form.user.phone;
                        }
                    } else if (this.form.shipping_same === true) {
                        this.form.shipping_address = { ...this.form.billing_address };
                        this.form.shipping_address.name = this.form.user.name;
                        this.form.shipping_address.email = this.form.user.email;
                        this.form.shipping_address.phone = this.form.user.phone;
                    } else {
                        this.form.shipping_address = { ...this.form.visual_shipping };
                        this.form.shipping_address.name = this.form.visual_shipping.name;
                        this.form.shipping_address.email = this.form.visual_shipping.email;
                        this.form.shipping_address.phone = this.form.visual_shipping.phone;
                    }

                    // Convert country code from alpha-2 to alpha-3 for backend
                    const shippingAddressForAPI = {
                        ...this.form.shipping_address,
                        country: getAlpha3CountryCode(this.form.shipping_address.country)
                    };

                    const result = await window.CheckoutAPI.patch(this, '/api/cart', { shipping_address: shippingAddressForAPI }, {
                        context: { operation: 'address_sync_update' },
                        headers: getHeaders(),
                        timeout: 15000
                    });

                    if (result.success) {
                        const processedResult = applyCheckoutCartApiPayload(this, result.data.data);
                        this.addressSynced = true;

                        // Auto-select least expensive shipping option on first load only.
                        // Skip for wallet express flows — shipping is chosen inside the payment sheet.
                        if (this.payment_processor !== 'paypal'
                            && this.payment_processor !== 'apple_pay'
                            && processedResult?.available_shipping_categories?.length > 0) {
                            const sorted = [...processedResult.available_shipping_categories].sort(
                                (a, b) => a.price_base.amount.amount - b.price_base.amount.amount
                            );
                            // Force-select cheapest option the first time shipping methods load;
                            // after that, only re-select if the current selection is no longer valid
                            if (!this._shippingAutoSelected) {
                                this._shippingAutoSelected = true;
                                if (sorted[0]) {
                                    setTimeout(() => {
                                        this.selectShippingOption(sorted[0]);
                                    }, 200);
                                }
                            } else if (this.selectedShippingOption && !processedResult.available_shipping_categories.find(s => s.id === this.selectedShippingOption)) {
                                // Previous selection no longer available, re-select cheapest
                                if (sorted[0]) {
                                    setTimeout(() => {
                                        this.selectShippingOption(sorted[0]);
                                    }, 200);
                                }
                            }
                        }

                    } else if (!result.handled) {
                        // Our system didn't handle error, throw for legacy compatibility
                        throw new Error('Failed to update shipping address');
                    }

                    if (this.bumperActive) {
                        this.reloadComponents();
                    }

                    return true;
                } catch (error) {
                    // Enhanced error logging with monitoring
                    window.CheckoutErrorMonitor.log(error, {
                        operation: 'address_sync_failed',
                        currentScene: this.currentScene,
                        paymentProcessor: this.payment_processor,
                        errorStatus: error.status
                    });

                    console.error('Error syncing addresses:', error);

                    // Clear any previous errors
                    this.clearAllErrors();

                    // Enhanced error handling with specific user guidance
                    if (error.status === 422 && error.errors) {
                        // Validation errors - map to specific fields
                        this.mapBackendErrorsToFormFields(error.errors);

                        const specificErrors = [];
                        Object.keys(error.errors).forEach(key => {
                            if (Array.isArray(error.errors[key])) {
                                specificErrors.push(...error.errors[key]);
                            } else {
                                specificErrors.push(error.errors[key]);
                            }
                        });
                        this.checkoutErrors = specificErrors.length > 0 ? specificErrors : ['Please check your address information and try again.'];

                    } else if (error.message && error.message.includes('postal')) {
                        this.setFieldError('shipping_postal_code', 'Please enter a valid postal code for your shipping address.');
                        this.checkoutErrors = ['Please enter a valid postal code for your shipping address.'];

                    } else if (error.message && error.message.includes('timeout')) {
                        this.checkoutErrors = ['Connection timeout. Please check your internet connection and try again.'];

                    } else if (error.status >= 500) {
                        this.checkoutErrors = ['Server error. Please try again in a moment or contact support if this persists.'];

                    } else if (error.message && error.message.includes('address')) {
                        this.setFieldError('shipping_address', 'Please enter a complete and valid shipping address.');
                        this.checkoutErrors = ['Please enter a complete and valid shipping address.'];

                    } else {
                        this.checkoutErrors = [error.message || 'Failed to update shipping address. Please try again.'];
                    }

                    if (this.currentScene === 2) {
                        this.scene1CanTransition = true;
                        this.scene1Pos = "center";
                        this.scene2CanTransition = true;
                        this.scene2Pos = "right";
                        window.scrollTo(0, 0);
                        setTimeout(() => {
                            this.currentScene = 1;
                            restoreScene1PaymentFieldTabOrder();
                        }, 75);
                        setTimeout(() => {
                            document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                            this.currentScene = 1;
                            this.scene1CanTransition = false;
                            this.scene2CanTransition = false;
                            this.scene1Top = false;
                            this.scene2Top = true;
                        }, 500);
                    }

                    return false;
                } finally {
                    this.addressSyncing = false;
                }
            },
            async computeHash(inputString) {
                const encoder = new TextEncoder();
                const data = encoder.encode(inputString);
                const hashBuffer = await crypto.subtle.digest('SHA-1', data);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
            },
            async getToken() {
                // Handle Adyen PayPal
                if (this.isAdyenGateway && this.payment_processor === 'paypal' && this.adyenPayPalState) {
                    try {
                        const { isValid, data } = this.adyenPayPalState;
                        if (!isValid || !data?.paymentMethod) {
                            throw new Error('PayPal payment method not ready');
                        }

                        const token = await this.computeHash(JSON.stringify(data.paymentMethod));
                        addDDAction(`paypal-tokenize-success`, {});

                        return {
                            token: token,
                            gateway: this.gateway,
                            details: {
                                type: 'paypal',
                                paymentMethod: data.paymentMethod,
                                browserInfo: data.browserInfo,
                            }
                        };
                    } catch (error) {
                        await this.handleError(error);
                        addDDAction(`paypal-tokenize-failed`, {
                            detail: error
                        });
                        throw error;
                    }
                }

                // Handle Adyen Apple Pay
                if (this.isAdyenGateway && this.payment_processor === 'apple_pay' && this.adyenApplePayState) {
                    try {
                        const { isValid, data } = this.adyenApplePayState;
                        if (!isValid || !data?.paymentMethod) {
                            throw new Error('Apple Pay payment method not ready');
                        }

                        const token = await this.computeHash(JSON.stringify(data.paymentMethod));
                        addDDAction(`applepay-tokenize-success`, {});

                        return {
                            token: token,
                            gateway: this.gateway,
                            details: {
                                type: 'applepay',
                                paymentMethod: data.paymentMethod,
                                browserInfo: data.browserInfo,
                            }
                        };
                    } catch (error) {
                        await this.handleError(error);
                        addDDAction(`applepay-tokenize-failed`, {
                            detail: error
                        });
                        throw error;
                    }
                }

                if (this.hostedFieldsInstance) {
                    try {
                        const response = await this.hostedFieldsInstance.tokenize();
                        this.cardValidationErrors = [];
                        const urlParams = new URLSearchParams(window.location.search);
                        const isQa = urlParams.get('qa_test');
                        if (isQa && isQa === 'VSQAT') {
                            this.gateway = "Braintree_Testing";
                            this.form.payment.gateway = 'Braintree_Testing';
                        } else {
                            this.gateway = "Braintree";
                            this.form.payment.gateway = 'Braintree';
                        }
                        addDDAction(`cc-tokenize-success`, {});
                        return {
                            token: response.nonce, gateway: this.gateway, details: response.details
                        };
                    } catch (error) {
                        await this.handleError(error);
                        addDDAction(`cc-tokenize-failed`, {
                            detail: error
                        });
                        throw error;
                    }
                } else {
                    const checkoutRoot = window.checkoutAppScope || this;
                    const adyenCardComponent = checkoutRoot.adyenCardComponent || window.cardComponent;
                    const cardState = checkoutRoot.cardState ?? window.cardState;
                    if (!adyenCardComponent) {
                        addDDAction(`cc-tokenize-failed`, {
                            detail: '[Adyen] Card component not ready'
                        });
                        throw new Error('Payment form not ready');
                    }
                    if (!cardState) {
                        adyenCardComponent.showValidation?.();
                        addDDAction(`cc-tokenize-failed`, {
                            detail: '[Adyen] Card state not ready (enter card details)'
                        });
                        return false;
                    }

                    const { isValid, data } = cardState;

                    if (!isValid || !data?.paymentMethod) {
                        adyenCardComponent.showValidation();
                        addDDAction(`cc-tokenize-failed`, {
                            detail: '[Adyen] Invalid card details:',
                            cardState
                        });
                        return false;
                    }

                    const token = await this.computeHash(JSON.stringify(data.paymentMethod)).then(hashValue => { return hashValue });
                    addDDAction(`cc-tokenize-success`, {});

                    const tokenScope = checkoutRoot;
                    return {
                        token: token,
                        gateway: this.gateway,
                        details: {
                            type: 'card',
                            paymentMethod: data.paymentMethod,
                            browserInfo: data.browserInfo,
                            returnUrl: window.location.href,
                            origin: window.location.origin,
                            cardType: resolveCardBrand(tokenScope.form, tokenScope.preAuthFields),
                            lastFour: resolveCardLastFour(tokenScope.form, tokenScope.preAuthFields),
                        }
                    };

                }
            },

            async changePaymentMethod() {
                this.processing = false;
                this.full_processing = false;
                this.checkingOut = false;
                this.clearAdyen3dsOverlay();
                this.canSkip = true;
                this.scene1CanTransition = true;
                this.scene1Pos = "center";
                this.scene2CanTransition = true;
                this.scene2Pos = "right";
                window.scrollTo(0, 0);
                addDDAction(`change-payment-method-clicked`, {
                    detail: this.payment_processor
                });
                setTimeout(async() => {
                    this.currentScene = 1;
                    restoreScene1PaymentFieldTabOrder();
                    if (this.isAdyenGateway && this.payment_processor === 'credit_card') {
                        await this.restoreAdyenCardComponent();
                    }
                }, 75);
                setTimeout(() => {
                    document.querySelector('.form_col .payment')?.scrollIntoView({ behavior: 'smooth' });
                    this.scene1CanTransition = false;
                    this.scene2CanTransition = false;
                    this.scene1Top = false;
                    this.scene2Top = true;
                }, 500);
            },
            async changeAddress() {
                this.canSkip = true;
                this.scene1CanTransition = true;
                this.scene1Pos = "center";
                this.scene2CanTransition = true;
                this.scene2Pos = "right";
                window.scrollTo(0, 0);
                addDDAction(`change-address-clicked`, {});
                setTimeout(() => {
                    this.currentScene = 1;
                    restoreScene1PaymentFieldTabOrder();
                }, 75);
                setTimeout(() => {
                    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                    this.currentScene = 1;
                    this.scene1CanTransition = false;
                    this.scene2CanTransition = false;
                    this.scene1Top = false;
                    this.scene2Top = true;
                }, 500);
            },
            async submitOrder() {
                if (!this.processing) {
                    this.processing = true;
                    this.errors = [];
                    addDDAction(`submit-order`, {});

                    const validationResult = this.validateAllRequiredFields();
                    if (!validationResult.valid) {
                        this.processing = false;
                        this.checkoutErrors = [validationResult.errors];
                        const errorElement = document.querySelector('.checkout-errors') || document.querySelector('#checkout-app-new');
                        if (errorElement) {
                            errorElement.scrollIntoView({ behavior: 'smooth' });
                        }
                        return;
                    }

                    this.validateContact();
                    if (this.contactErrors.length > 0) {
                        document.getElementById('contact-form')?.scrollIntoView()
                        this.processing = false;
                        return;
                    }

                    await this.processOrder(this.form);
                }
            }
        }
    }
    const Processor = function (props) {
        return {
            $template: '#processor-template',
            cart: props.cartState,
            checkoutOffer: props.cartState,
            visible: true,
            cartSpecificCart: props.cartState,
            mounted() {
            },
            async paymentProcessorChange(paymentProcessor) {
                console.log(`paymentProcessorChange paymentProcessor: ${paymentProcessor}`);
                this.payment_processor = paymentProcessor;

                // Clean up previous payment components when switching payment methods
                if (this.isAdyenGateway) {
                    this.destroyAdyenComponents();
                } else {
                    // Clean up BrainTree PayPal container when switching away
                    this.destroyBraintreePayPalComponent();
                }

                setTimeout(() => {
                    if (typeof window.segmentedBoxSetup === 'function') {
                        window.segmentedBoxSetup();
                    }
                }, 50);

                // Create payment components based on gateway.
                //
                // IMPORTANT: Apple Pay and PayPal components mount onto
                // containers that only exist on Scene 2 (.adyen-applepay-container
                // / .adyen-paypal-container are inside `v-if="currentScene === 2"`).
                // The payment-method selector lives on Scene 1, so we must
                // defer those mounts until the user clicks "Continue with
                // Apple Pay" / "Continue with PayPal" (which advances them
                // to Scene 2 in next_apple_pay / next_paypal). If we mount
                // here while still on Scene 1 the SDK logs "[Adyen] Apple Pay
                // container not found" and the wallet button never appears.
                if (this.isAdyenGateway) {
                    try {
                        switch(paymentProcessor) {
                            case 'apple_pay':
                            case 'paypal':
                                // Deferred to next_apple_pay / next_paypal on Scene 2.
                                break;
                            case 'credit_card':
                            default: {
                                const checkoutRoot = window.checkoutAppScope || this;
                                await checkoutRoot.createAdyenCardComponent();
                                break;
                            }
                        }
                    } catch (error) {
                        console.error('[Adyen] Error creating payment component:', error);
                    }
                } else {
                    // BrainTree gateway — PayPal historically rendered its
                    // own button on Scene 1, but to keep behaviour consistent
                    // with Adyen we now defer it to next_paypal as well.
                }

                addDDAction(`payment-method-changed`, {
                    detail: paymentProcessor
                });
            },
        }
    }
    const ContactDetails = function (props) {
        return {
            $template: '#contactdetails-template',
            cart: props.cartState,
            checkoutOffer: props.cartState,
            visible: true,
            cartSpecificCart: props.cartState,
            validateOnBlur(field) {
                const errors = this.validateField(this.form.user, field);
                this.contactDetailErrors[field] = errors.length > 0 ? { message: errors[0].message } : false;
            },
            mounted() {

            }
        }
    }

    const BillingShipping = function (props) {
        return {
            $template: '#billingshipping-template',
            convertCurrency,
            getFieldError(field) {
                return this.contactErrors.filter(error => error.field === field)?.[0];
            },
            get nameError() {
                return this.contactErrors.filter(error => error.field === 'name')?.[0];
            },
            get nameShippingError() {
                return this.contactErrors.filter(error => error.field === 'shipping_name')?.[0];
            },
            get emailError() {
                return this.contactErrors.filter(error => error.field === 'email')?.[0];
            },
            get phoneError() {
                return this.contactErrors.filter(error => error.field === 'phone')?.[0];
            },
            get phoneShippingError() {
                return this.contactErrors.filter(error => error.field === 'shipping_phone')?.[0];
            },
            get addressLine1Error() {
                return this.contactErrors.filter(error => error.field === 'address')?.[0];
            },
            get addressShippingLine1Error() {
                return this.contactErrors.filter(error => error.field === 'shipping_address')?.[0];
            },
            get addressLine2Error() {
                return this.contactErrors.filter(error => error.field === 'address2')?.[0];
            },
            get cityError() {
                return this.contactErrors.filter(error => error.field === 'city')?.[0];
            },
            get cityShippingError() {
                return this.contactErrors.filter(error => error.field === 'shipping_city')?.[0];
            },
            get stateError() {
                return this.contactErrors.filter(error => error.field === 'state')?.[0];
            },
            get stateShippingError() {
                return this.contactErrors.filter(error => error.field === 'shipping_state')?.[0];
            },
            get countryError() {
                return this.contactErrors.filter(error => error.field === 'country')?.[0];
            },
            get countryShippingError() {
                return this.contactErrors.filter(error => error.field === 'shipping_country')?.[0];
            },
            get zipError() {
                return this.contactErrors.filter(error => error.field === 'postal_code')?.[0];
            },
            get zipShippingError() {
                return this.contactErrors.filter(error => error.field === 'shipping_postalCode')?.[0];
            },
            validateOnBlur(field) {
                if (field.includes('shipping_')) {
                    if (this.form.shipping_same) {
                        this.contactDetailErrors.visual_shipping[field.replace('shipping_', '')] = false;
                        return;
                    }
                    const errors = this.validateField(this.form.visual_shipping, field.replace('shipping_', ''));
                    this.contactDetailErrors.visual_shipping[field.replace('shipping_', '')] = errors.length > 0 ? { message: errors[0].message } : false;
                } else if (field.includes('billing_')) {
                    const errors = this.validateField(this.form.billing_address, field.replace('billing_', ''));
                    this.contactDetailErrors.billing_address[field.replace('billing_', '')] = errors.length > 0 ? { message: errors[0].message } : false;
                    if (errors.length > 0) {
                        addDDAction(`input-error-on-blur`, {
                            detail: field,
                            errors: errors
                        });
                    }
                } else {
                    const errors = this.validateField(this.form.billing_address, field);
                    this.contactDetailErrors.billing_address[field] = errors.length > 0 ? { message: errors[0].message } : false;
                    if (errors.length > 0) {
                        addDDAction(`input-error-on-blur`, {
                            detail: field,
                            errors: errors
                        });
                    }
                }
            },

            async syncAddresses() {
                return window.CheckoutRequestController.runExclusive('address-sync', () => this._performAddressSync());
            },

            async _performAddressSync() {
                this.clearAllErrors();
                this.addressSyncing = true;

                try {


                    if (this.payment_processor === 'paypal' || this.payment_processor === 'apple_pay') {
                        // Wallet express flows populate shipping_address from the
                        // payment sheet; do not rebuild it from billing_address
                        // or visual_shipping.
                        this.form.shipping_address.name = this.form.shipping_address.name || this.form.user.name;
                        this.form.shipping_address.email = this.form.shipping_address.email || this.form.user.email;
                        if (!this.form.shipping_address.phone) {
                            this.form.shipping_address.phone = this.form.user.phone;
                        }
                    } else if (this.form.shipping_same === true) {
                        this.form.shipping_address = { ...this.form.billing_address };
                        this.form.shipping_address.name = this.form.user.name;
                        this.form.shipping_address.email = this.form.user.email;
                        this.form.shipping_address.phone = this.form.user.phone;
                    } else {
                        this.form.shipping_address = { ...this.form.visual_shipping };
                        this.form.shipping_address.name = this.form.visual_shipping.name;
                        this.form.shipping_address.email = this.form.visual_shipping.email;
                        this.form.shipping_address.phone = this.form.visual_shipping.phone;
                    }

                    // Convert country code from alpha-2 to alpha-3 for backend
                    const shippingAddressForAPI = {
                        ...this.form.shipping_address,
                        country: getAlpha3CountryCode(this.form.shipping_address.country)
                    };

                    const result = await window.CheckoutAPI.patch(this, '/api/cart', { shipping_address: shippingAddressForAPI }, {
                        context: { operation: 'address_sync_update' },
                        headers: getHeaders(),
                        timeout: 15000
                    });

                    if (result.success) {
                        const processedResult = applyCheckoutCartApiPayload(this, result.data.data);
                        this.addressSynced = true;
                        this._lastSyncedAddressSnapshot = this._getAddressSnapshot();

                        // Auto-select least expensive shipping option on first load only.
                        // Skip for wallet express flows — shipping is chosen inside the payment sheet.
                        if (this.payment_processor !== 'paypal'
                            && this.payment_processor !== 'apple_pay'
                            && processedResult.available_shipping_categories?.length > 0) {
                            const sorted = [...processedResult.available_shipping_categories].sort(
                                (a, b) => a.price_base.amount.amount - b.price_base.amount.amount
                            );
                            if (!this._shippingAutoSelected) {
                                this._shippingAutoSelected = true;
                                if (sorted[0]) {
                                    setTimeout(() => {
                                        this.selectShippingOption(sorted[0]);
                                    }, 200);
                                }
                            } else if (this.selectedShippingOption && !processedResult.available_shipping_categories.find(s => s.id === this.selectedShippingOption)) {
                                if (sorted[0]) {
                                    setTimeout(() => {
                                        this.selectShippingOption(sorted[0]);
                                    }, 200);
                                }
                            }
                        }

                        if (this.bumperActive && this.payment_processor === 'paypal') {
                            this.createPaypalInstance();
                        }

                        return true;
                    } else if (!result.handled) {
                        // Our system didn't handle error, throw for legacy compatibility
                        throw new Error('Failed to update shipping address');
                    }

                    return false;
                } catch (error) {
                    // Enhanced error logging with monitoring
                    window.CheckoutErrorMonitor.log(error, {
                        operation: 'address_sync_failed_comp2',
                        currentScene: this.currentScene,
                        paymentProcessor: this.payment_processor,
                        errorStatus: error.status
                    });

                    console.error('Error syncing addresses:', error);
                    this.clearAllErrors();

                    // Enhanced error messages based on error type
                    if (error.status === 422 && error.errors) {
                        const specificErrors = [];
                        Object.keys(error.errors).forEach(key => {
                            if (Array.isArray(error.errors[key])) {
                                specificErrors.push(...error.errors[key]);
                            } else {
                                specificErrors.push(error.errors[key]);
                            }
                        });
                        this.checkoutErrors = specificErrors.length > 0 ? specificErrors : ['Please check your address information and try again.'];
                    } else if (error.message && error.message.includes('timeout')) {
                        this.checkoutErrors = ['Connection timeout. Please check your internet connection and try again.'];
                    } else if (error.status >= 500) {
                        this.checkoutErrors = ['Server error. Please try again in a moment or contact support if this persists.'];
                    } else {
                        this.checkoutErrors = [error.message || 'Failed to update shipping address. Please try again.'];
                    }

                    return false;
                } finally {
                    this.addressSyncing = false;
                }
            },
            async next() {
                const {
                    token,
                    gateway,
                    details
                } = await this.getToken();
                this.validateContact();
                if (this.contactErrors.length <= 0) {
                    this.scene1CanTransition = true;
                    this.scene1Pos = "left";
                    this.scene2CanTransition = true;
                    this.scene2Pos = "center";
                    window.scrollTo(0, 0);
                    this.clearAllErrors();
                    await this.syncAddresses();
                    setTimeout(() => {
                        this.scene1CanTransition = false;
                        this.scene2CanTransition = false;
                        this.scene1Top = true;
                        this.scene2Top = false;
                    }, 500);
                } else {
                    const firstInvalidElement = document.querySelector('[aria-invalid="true"]');
                    if (firstInvalidElement) {
                        firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            },
            async countryUpdated(scope) {
                // FET-2667: state values are country-specific (US states / CA provinces / free text).
                // Reset the corresponding state field + its validation error so the swapped dropdown
                // doesn't render with a stale value that doesn't match any option.
                if (scope === 'billing') {
                    this.form.billing_address.state = '';
                    if (this.contactDetailErrors?.billing_address) {
                        this.contactDetailErrors.billing_address.state = false;
                    }
                } else if (scope === 'shipping') {
                    this.form.visual_shipping.state = '';
                    if (this.contactDetailErrors?.visual_shipping) {
                        this.contactDetailErrors.visual_shipping.state = false;
                    }
                }

                const currentSnapshot = this._getAddressSnapshot();
                if (this.addressSynced && currentSnapshot === this._lastSyncedAddressSnapshot) {
                    return;
                }

                this.addressSynced = false;
                clearTimeout(this.countryUpdateTimeout);
                this.countryUpdateTimeout = setTimeout(async () => {
                    if (window.CheckoutRequestController.isCheckoutPipelineBusy()) {
                        return;
                    }
                    if (this.isAddressComplete()) {
                        this.clearAllErrors();
                        await this.syncAddresses();
                    }
                }, 1000);
            },
            isAddressComplete() {
                const addr = this.form.shipping_same ? this.form.billing_address : this.form.visual_shipping;
                return !!(addr.address && addr.city && addr.country && addr.postal_code && (addr.state || addr.province));
            },
            _lastSyncedAddressSnapshot: null,
            _getAddressSnapshot() {
                const addr = this.form.shipping_same ? this.form.billing_address : this.form.visual_shipping;
                return JSON.stringify({
                    address: addr.address,
                    address2: addr.address2,
                    city: addr.city,
                    state: addr.state,
                    province: addr.province,
                    postal_code: addr.postal_code,
                    country: addr.country,
                    shipping_same: this.form.shipping_same,
                });
            },
            addressFieldUpdated() {
                const currentSnapshot = this._getAddressSnapshot();
                if (this.addressSynced && currentSnapshot === this._lastSyncedAddressSnapshot) {
                    return;
                }

                this.addressSynced = false;
                clearTimeout(this.addressSyncTimer);
                this.addressSyncTimer = setTimeout(async () => {
                    if (window.CheckoutRequestController.isCheckoutPipelineBusy()) {
                        return;
                    }
                    if (this.isAddressComplete()) {
                        this.clearAllErrors();
                        await this.syncAddresses();
                    }
                }, 1000);
            },

            // --- Google Places Address Autofill (lazy init on first focus) ---
            billingAutocomplete: null,
            shippingAutocomplete: null,

            async initBillingAutocompleteOnce() {
                if (this.billingAutocomplete || !ADDRESS_AUTOFILL_ENABLED) return;
                try {
                    await loadGooglePlacesAPI();
                    this.billingAutocomplete = initAddressAutocomplete(
                        'billing_address', this, 'billing_address'
                    );
                } catch (e) {
                    console.warn('Google Places failed to load for billing', e);
                }
            },

            async initShippingAutocompleteOnce() {
                if (this.shippingAutocomplete || !ADDRESS_AUTOFILL_ENABLED) return;
                try {
                    await loadGooglePlacesAPI();
                    this.shippingAutocomplete = initAddressAutocomplete(
                        'shipping_address', this, 'visual_shipping'
                    );
                } catch (e) {
                    console.warn('Google Places failed to load for shipping', e);
                }
            },
        }
    }

    const BraintreeV3 = function (props) {
        return {
            $template: '#braintreev3-template',
            gateway: props.gateway,
            showPostalCode: props.showPostalCode,
            view: props.view ?? 'mobile',
            clientInstance: null,
            rendered: false,
            bid: props.bid,
            managed,
            getHeaders,
            remapForm,
            detectCountry,
            fetchExchangeRates,
            convertCurrency,
            async mounted() {
                this.processing = false;
                this.full_processing = false;
                this.checkingOut = false;
                this.ccTokenizing = false;

                this.gateway = activeGateway();
                if (['Braintree', 'Braintree_Testing'].includes(this.gateway)) {
                    this.braintreeActive = true;
                    await this.createInstance();
                }
                const overlay = document.querySelector(".init.full_load_overlay");
                if (overlay) {
                    overlay.style.display = "none";
                }
            },
            async createInstance() {
                try {
                    this.clientInstance = await this.createClient();
                    this.clientInstanceField = this.clientInstance;
                    this.hostedFieldsInstance = await this.createHostedFields();
                    syncPaymentFieldTabOrder(this.currentScene);
                    // Braintree may inject iframes asynchronously after create().
                    setTimeout(() => syncPaymentFieldTabOrder(this.currentScene), 100);

                    this.hostedFieldsInstance.on('focus', event => {
                        this.ccFields[event.emittedBy].isFocused = true;

                    });

                    this.hostedFieldsInstance.on('blur', event => {
                        this.ccFields[event.emittedBy].isFocused = false;
                    });

                    this.hostedFieldsInstance.on('validityChange', event => {
                        const field = event.fields[event.emittedBy];
                        this.ccFieldStatus[event.emittedBy] = field.isValid;
                        if (event.fields[event.emittedBy].isValid) {
                            this.cardValidationErrors = this.cardValidationErrors.filter(validationError => validationError !== event.emittedBy);
                        } else if (!this.cardValidationErrors.includes(event.emittedBy)) {
                            this.cardValidationErrors.push(event.emittedBy);
                        }

                        // Update ccReady based on all fields being valid
                        this.ccReady = Object.values(this.ccFieldStatus).every(Boolean);
                    });
                } catch (error) {
                    console.error('Failed to create Braintree instance!', error);
                }
            },

            // eslint-disable-next-line consistent-return
            async createClient() {
                const authorization = window.braintreeClientToken || '';
                const maxAttempts = 3;

                for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                    try {
                        return await braintreeWeb.client.create({ authorization });
                    } catch (error) {
                        console.error(`Failed to create Braintree client (attempt #${attempt})`, error);
                        if (attempt === maxAttempts) {
                            throw new Error('Failed to create Braintree client after 3 attempts.');
                        }
                    }
                }
            },

            // eslint-disable-next-line consistent-return
            async createHostedFields() {

                const styles = {
                    input: {
                        'font-size': '16px',
                        'line-height': '56px',
                        'padding-top': '12px',
                        'padding-bottom': '12px',
                        'font-family': 'inter, arial',
                        'font-weight': '400'
                    }
                };

                try {
                    return await braintreeWeb.hostedFields.create({ client: this.clientInstance, styles, fields: this.ccFields });
                } catch (error) {
                    console.error('Failed to create hosted fields instance!', error);
                }
            },

            // eslint-disable-next-line consistent-return
            async getToken() {
                if (this.hostedFieldsInstance) {
                    try {
                        const response = await this.hostedFieldsInstance.tokenize();
                        this.cardValidationErrors = [];
                        return {
                            token: response.nonce, gateway: this.gateway, details: response.details
                        };
                    } catch (error) {
                        this.handleError(error);
                        throw error;
                    }
                }
            },
            // eslint-disable-next-line consistent-return
            handleError(error) {
                const cardValidationErrors = [];

                if (error.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
                    error.details.invalidFieldKeys.forEach(key => cardValidationErrors.push(key));
                } else if (error.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
                    cardValidationErrors.push('number');
                    cardValidationErrors.push('expirationDate');
                    cardValidationErrors.push('cvv');
                } else if (error.code === 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED') {
                    cardValidationErrors.push('cvv');
                } else if (error.code === 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE') {
                    cardValidationErrors.push('number');
                } else {
                    return window.location.reload();
                }

                this.cardValidationErrors = cardValidationErrors;
            }
        }

    }

    const Adyen = function (props) {
        return {
            $template: '#adyen-template',
            gateway: props.gateway,
            showPostalCode: props.showPostalCode,
            processing: false,
            paymentErrors: [],
            validationErrors: [],
            managed,
            getHeaders,
            remapForm,
            async mounted() {
                this.gateway = activeGateway();
                if (this.isAdyenGateway) {
                    this.adyenActive = true;
                    const checkoutRoot = window.checkoutAppScope;
                    if (checkoutRoot) {
                        const tryMountCard = async (attempt = 0) => {
                            if (checkoutRoot.adyenCardComponent || window.cardComponent) {
                                return;
                            }
                            if (checkoutRoot.adyenCheckout && (window.AdyenWeb?.Card || window.AdyenCard)) {
                                await checkoutRoot.createAdyenCardComponent();
                                return;
                            }
                            if (attempt < 40) {
                                setTimeout(() => tryMountCard(attempt + 1), 250);
                            } else {
                                console.error('[Adyen] Timed out waiting to mount the card component');
                                checkoutRoot.checkoutErrors = [ADYEN_SDK_USER_MESSAGE];
                            }
                        };
                        tryMountCard();
                    }
                }
                const overlay = document.querySelector(".init.full_load_overlay");
                setTimeout(() => {
                    if (overlay) {
                        overlay.style.display = "none";
                    }
                }, 500);
            },
        }
    };

    const BumperV3 = function (props) {
        return {
            $template: '#bumper-template-v3',
            cart: props.cartState,
            getHeaders,
            convertCurrency,
            async mounted() {
                const urlParams = new URLSearchParams(window.location.search);
                const offerSkuItem = urlParams.get('offer_skus') || false;
                if (offerSkuItem) {
                    const offerSkusArray = offerSkuItem.split(',');
                    this.bumpers = this.bumpers.filter(bumper =>
                        !bumper.options.available_for || bumper.options.available_for.some(option => offerSkusArray.includes(option))
                    );
                    this.bumpers.forEach(bumper => {
                        if (bumper.options.offer_skin) {
                            bumper.options.offer_skin.forEach(skin => {
                                if (skin.sku === offerSkuItem) {
                                    if (skin.title && skin.title.highlight) { bumper.options.title.highlight = skin.title.highlight; }
                                    if (skin.title && skin.title.text) { bumper.options.title.text = skin.title.text; }
                                    if (skin.title && skin.title.context) { bumper.options.title.context = skin.title.context; }
                                    if (skin.title && skin.title.contextColor) { bumper.options.title.highlight = skin.title.contextColor; }
                                    if (skin.product && skin.product.name) { bumper.options.product.name = skin.product.name; }
                                    if (skin.product && skin.product.regular_price) { bumper.options.product.regular_price = skin.product.regular_price; }
                                    if (skin.product && skin.product.price) { bumper.options.product.price = skin.product.price; }
                                    if (skin.product && skin.product.subscription) { bumper.options.product.name = skin.product.subscription; }
                                    if (skin.body && skin.body.paragraphs) { bumper.options.body.paragraphs = skin.body.paragraphs; }
                                    if (skin.image) { bumper.options.image = skin.image; }
                                }
                            });
                        }
                    });
                }
            },
            async reject_offer() {
                addDDAction(`bumper-rejected`, {});
                // Redirect to the stored next URL after declining upsell
                if (this.nextUrl) {
                    window.location = this.nextUrl;
                }
            },
            async accept_offer(offerId) {
                if (!this.processing) {
                    addDDAction(`bumper-accepted`, {
                        detail: offerId
                    });
                    this.processing = true;

                    const result = await window.CheckoutAPI.post(this, '/api/user/order', {
                        offers: [offerId]
                    }, {
                        context: { operation: 'accept_bumper_offer' },
                        headers: getHeaders(),
                        timeout: 15000
                    });

                    if (result.success && result.data) {
                        this.specialOffersSelected.push(offerId);
                        const convertedResult = this.convertCurrency(result.data, this.countriesList, this.form.billing_address.country);
                        this.checkoutOffer = convertedResult;
                        this.cartState = convertedResult;
                    }

                    this.processing = false;

                    // Redirect to next URL after accepting the upsell
                    if (this.nextUrl) {
                        window.location = this.nextUrl;
                    }
                }
            },
            async next() {

            }
        }
    }

    // NEW VUE COMPONENTS END

    // Function to build customCheckoutOfferData from checkoutOffer when the product is not in customProductData
    function buildCustomCheckoutOfferDataFromCheckoutOffer(checkoutOffer) {
        if (!checkoutOffer || !checkoutOffer.order_offers) {
            return null;
        }

        const productOffer = checkoutOffer.order_offers.find(o => o && o.type === 'product');
        if (!productOffer || !productOffer.offer) {
            return null;
        }

        const offer = productOffer.offer;
        const offerSnapshot = productOffer.offer_snapshot || {};

        // Extract price information
        const currentPrice = productOffer.amount_local || productOffer.amount_base || offer.price_local || offer.price_base;
        const originalPrice = offer.original_price_local || offer.original_price_base;

        // Calculate price values - handle both string and number formats
        let priceAmount = 0;
        if (currentPrice) {
            if (currentPrice.amount && typeof currentPrice.amount.amount !== 'undefined') {
                priceAmount = parseFloat(currentPrice.amount.amount) / 100;
            } else if (currentPrice.decimal) {
                priceAmount = parseFloat(currentPrice.decimal);
            } else if (typeof currentPrice === 'number') {
                priceAmount = currentPrice / 100;
            }
        }

        let originalPriceAmount = 0;
        if (originalPrice) {
            if (originalPrice.amount && typeof originalPrice.amount !== 'undefined') {
                originalPriceAmount = parseFloat(originalPrice.amount) / 100;
            } else if (originalPrice.decimal) {
                originalPriceAmount = parseFloat(originalPrice.decimal);
            } else if (typeof originalPrice === 'number') {
                originalPriceAmount = originalPrice / 100;
            }
        }

        const discountAmount = originalPriceAmount > 0 ? originalPriceAmount - priceAmount : 0;
        const discountPercent = originalPriceAmount > 0 ? Math.round((discountAmount / originalPriceAmount) * 100) : 0;

        // Get image URL from offer_snapshot thumbnail or offer media
        let imageUrl = null;
        if (offerSnapshot.thumbnail) {
            imageUrl = offerSnapshot.thumbnail;
        } else if (offer.media && offer.media.length > 0 && offer.media[0].url) {
            imageUrl = offer.media[0].url;
        }

        // Build description from the products list or use offer description
        let description = '';
        if (offer.products && Array.isArray(offer.products) && offer.products.length > 0) {
            description = '<ul>';
            offer.products.forEach(product => {
                if (product && product.name) {
                    description += `<li>${product.name}</li>`;
                }
            });
            description += '</ul>';
        } else if (offer.description) {
            description = offer.description.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>');
        }

        // Build custom_line_items for order summary - only include the main product, not included products
        const customLineItems = [];
        if (offerSnapshot.title || offer.title) {
            customLineItems.push({
                title: offerSnapshot.title || offer.title,
                price: priceAmount
            });
        }

        // Extract included products for inventory display (not as free items)
        const includedProducts = [];
        if (offer.products && Array.isArray(offer.products) && offer.products.length > 0) {
            offer.products.forEach(product => {
                if (product && product.name) {
                    includedProducts.push({
                        name: product.name,
                        sku: product.sku || null
                    });
                }
            });
        }

        return {
            sku: offer.sku || offerSnapshot.sku || '',
            qty: productOffer.quantity || 1,
            frequency: 'onetime',
            product: null, // No custom product slug for fallback products
            title: offerSnapshot.title || offer.title || 'Product',
            normal_price: originalPriceAmount,
            discount_percent: discountPercent,
            coupon_text: discountPercent > 0 ? `${discountPercent}%OFF` : '',
            discount_dollars: discountAmount,
            price: priceAmount,
            image_url: imageUrl,
            description: description,
            custom_line_items: customLineItems.length > 0 ? customLineItems : null,
            included_products: includedProducts.length > 0 ? includedProducts : null // Products to show as inventory (indented, no price)
        };
    }

    // Resolve the product's checkout display data for a given cart (see
    // lib/product-display.js), in priority order:
    //   1. inline CMS `customProductData` entry for this SKU (whole-entry override),
    //   2. else offer-managed data riding on the cart (order_offers[].offer.checkout_display),
    //   3. else the client-derived default built from the live cart (universal safety net).
    // `customProductData` is read behind a typeof guard because not every page defines it.
    //
    // Exposed to components as a getter (not a frozen page-load value) so that when the
    // cart mutates -- `checkoutOffer` is reassigned on shipping / bumper / address-sync
    // changes -- the tier-2 offer-managed display re-reads the fresh
    // `order_offers[].offer.checkout_display` instead of the stale initial payload.
    const resolveCustomCheckoutOfferDataForCart = (cart) => {
        if (!cart) {
            return null;
        }

        const resolved = resolveCustomCheckoutOfferData({
            checkoutOffer: cart,
            inlineProductData: (typeof customProductData !== 'undefined' ? customProductData : []),
        });

        return resolved ?? buildCustomCheckoutOfferDataFromCheckoutOffer(cart);
    };

    let lastResolvedCustomCheckoutOfferData = resolveCustomCheckoutOfferDataForCart(
        typeof cartHydration !== 'undefined' ? cartHydration : (typeof checkoutOffer !== 'undefined' ? checkoutOffer : null),
    );

    const resolveCustomCheckoutOfferDataForCartSticky = (cart) => {
        const resolved = resolveCustomCheckoutOfferDataForCart(cart);
        if (resolved) {
            lastResolvedCustomCheckoutOfferData = resolved;

            return resolved;
        }

        return lastResolvedCustomCheckoutOfferData;
    };

    // FET-2766: resolve the page skin from baked brand defaults + page-option
    // overrides + an optional inline `customSkin` (read behind a typeof guard so
    // pages no longer need to define the array). Per-product hero overrides that
    // the old `customSkin.product[]` carried are intentionally dropped.
    const skin = resolveSkin({
        inlineSkin: (typeof customSkin !== 'undefined' ? customSkin : null),
        pageOptions: _skinPageOptions,
    });
    const AppWrapper = function (props) {
        return {
            template: "#app-wrapper-template",
            available_payment_methods: filterDisabledPaymentMethods(['credit_card', 'paypal']),
            isSculptnation: window.location.hostname.includes('sculptnation'),
            paymentMethodConfig: {
                'credit_card': { label: 'Card', class: 'credit' },
                'paypal': { label: 'Paypal', class: 'paypal' },
                'apple_pay': { label: 'Apple Pay', class: 'apple-pay' },
            },
            centsToDollars(cents, options = { format: false }) {
                // Treat null / undefined / NaN as 0
                if (cents == null || Number.isNaN(cents)) {
                    return options.format ? '0.00' : 0;
                }

                // Ensure it's a number
                const value = Number(cents);

                // Explicit zero handling
                if (value === 0) {
                    return options.format ? '0.00' : 0;
                }

                const dollars = value / 100;

                return options.format ? dollars.toFixed(2) : dollars;
            },
            async mounted() {
                this.gateway = activeGateway();

                if (this.isAdyenGateway) {
                    try {
                        // Fetch available payment methods dynamically from Adyen.
                        // FET-2707: restore the dynamic list so `paypal` is included when the Adyen
                        // merchant has PayPal configured. The previous hardcoded
                        // ['credit_card', 'apple_pay'] stripped PayPal out entirely, which is why
                        // the tab never rendered.
                        const { availableMethods, paymentMethodsData } = await fetchAdyenPaymentMethods(this);
                        this.available_payment_methods = availableMethods.length > 0
                            ? availableMethods
                            : filterDisabledPaymentMethods(['credit_card', 'paypal', 'apple_pay']);

                        const thisCart = this.cartState || this.checkoutOffer;
                        const thisAmount = thisCart.amount_base ? thisCart.amount_base.amount : thisCart.amount.amount;
                        const thisCurrency = thisCart.amount_base ? thisCart.amount_base.currency : thisCart.amount.currency;

                        const payload = {
                            amount: this.centsToDollars(thisAmount, { format: true }),
                            currency: thisCurrency ?? 'USD',
                            orderId: thisCart.id,
                        };
                        const query = new URLSearchParams(payload).toString();

                        if (paymentMethodsData && window.AdyenWeb.AdyenCheckout) {
                            const rootApp = window.checkoutAppInstance || this.$root;
                            if (rootApp) {
                                rootApp.adyenPaymentMethodsResponse = paymentMethodsData;
                            }
                            const checkoutRoot = window.checkoutAppScope || this;
                            checkoutRoot.adyenCheckout = await runWithAdyenSessionRetry(
                                async () => {
									// Replaced sessionResponse in OTT-2377
									// Fetch Adyen session (serialized to prevent concurrent duplicate session creation)
									const sessionResponse = await window.CheckoutRequestController.runExclusive(
										'adyen-card-session',
										() => window.CheckoutAPI.get(this, `/api/adyen/payment/session?${query}`, {
											context: { operation: 'adyen_session_init' },
											headers: getHeaders()
										})
									);
									if (!sessionResponse.success || !sessionResponse.data) {
										throw new Error(sessionResponse.message || 'Failed to fetch Adyen session');
									}
                                    const configuration = {
                                        locale: 'en_US',
                                        countryCode: 'US',
                                        amount: {
                                            value: thisAmount,
                                            currency: thisCurrency
                                        },
                                        environment: sessionResponse.data.environment || 'test',
                                        clientKey: sessionResponse.data.clientKey,
                                        showPayButton: false,
                                        session: sessionResponse.data.session,
                                        paymentMethodsResponse: paymentMethodsData,
                                        onEnterKeyPressed: ({ component }) => {component?.showValidation?.();},
                                        onError: (error) => {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                            console.error('[Adyen] Checkout error:', error);
                                            if (checkoutRoot.adyen3dsActive) {
                                                checkoutRoot.handleAdyen3dsFailure({
                                                    message: error?.message || window.Adyen3dsErrorGuard?.userMessage,
                                                    phase: 'checkout_on_error',
                                                    errorCode: error?.errorCode,
                                                });
                                                return;
                                            }
                                            checkoutRoot.checkoutErrors = [error.message || 'Payment initialization failed'];
                                        },
                                    };
                                    checkoutRoot.adyenSession = sessionResponse.data.session;
                                    return await window.AdyenWeb.AdyenCheckout(configuration);
                                },
                                {
                                    onRetry: () => {
                                        console.warn('[Adyen] Session expired during checkout initialization. Retrying with a new session.');
                                    }
                                }
                            );
                            await checkoutRoot.createAdyenCardComponent();
                        } else if (paymentMethodsData) {
                            setCheckoutAdyenSdkUnavailable('[Adyen] Cannot initialize checkout: Web SDK missing');
                        }
                    } catch (error) {
                        console.error('[Adyen] Error initializing checkout:', error);
                        const checkoutRoot = window.checkoutAppScope || this;
                        checkoutRoot.checkoutErrors = [
                            error.message || 'Payment initialization failed. Please refresh and try again.',
                        ];
                        // Fallback to default methods
                        this.available_payment_methods = filterDisabledPaymentMethods(['credit_card', 'paypal', 'apple_pay']);
                    }
                } else {
                    this.available_payment_methods = filterDisabledPaymentMethods(['credit_card', 'paypal']);
                }

                window.addEventListener("resize", () => {
                    this.isMobileDevice = window.innerWidth < 768;
                    this.deviceInit = true;
                });
                // Manually fire resize action to initially set the isMobileDevice value
                requestAnimationFrame(() => {
                    window.dispatchEvent(new Event('resize'));
                });

                // Async country detection from IP - runs in background, won't block page load
                // Only set if the user hasn't already changed the country from the default
                this._detectCountryFromIP();
            }
        }
    }

    const ShippingMethods = function (props) {
        return {
            $template: '#shipping-methods',
            get offerRequiresShipping() {
                // Show the shipping section shell if the offer has a shipping_category (even if not yet populated with rates)
                // or if available_shipping_categories exist
                return this.checkoutOffer?.shipping_category !== null
                    || (this.checkoutOffer?.available_shipping_categories && this.checkoutOffer.available_shipping_categories.length > 0);
            },
            get sortedShippingOptions() {
                return [...this.checkoutOffer.available_shipping_categories].sort(
                    (a, b) => a.price_base.amount.amount - b.price_base.amount.amount
                );
            },
            getHeaders,
            convertCurrency,
            mounted() {
                // Auto-selection is now handled by _performAddressSync after address is synced
            },
        };
    };


    // FET-2641: detect whether the cart contains at least one physical product.
    // `available_shipping_categories` is only populated by the `RequiresPhysicalProducts`
    // pipe when the order contains physical products, so a non-empty array is a reliable
    // proxy for "this checkout needs a lifetime guarantee badge instead of a 30-day one".
    function cartHasPhysicalProduct(cart) {
        if (!cart) return false;
        if (Array.isArray(cart.available_shipping_categories) && cart.available_shipping_categories.length > 0) {
            return true;
        }
        if (cart.shipping_category && typeof cart.shipping_category === 'object' && cart.shipping_category.id) {
            return true;
        }
        return false;
    }

    let defaultCheckoutAppNew = {
        CartOrder,
        Processor,
        ContactDetails,
        BraintreeV3,
        Adyen,
        BillingShipping,
        ShippingMethods,
        remapForm,
        convertCurrency,
        currentScene: 1,
        scene1CanTransition: false,
        scene1Pos: "center",
        scene1Top: false,
        scene2CanTransition: false,
        scene2Pos: "right",
        scene2Top: true,
        adyen3dsActive: false,
        adyen3dsOriginalParent: null,
        restoringCardComponent: false,
        canSkip: false,
        gateway: activeGateway(),
        BumperV3,
        contactDetailErrors: {
            name: false,
            email: false,
            phone: false,
            card: {
                number: false,
                expirationDate: false,
                cvv: false,
            },
            billing_address: {
                country: false,
                address: false,
                address2: false,
                city: false,
                state: false,
                postal_code: false,
            },
            shipping_address: {
                country: false,
                address: false,
                address2: false,
                phone: false,
                city: false,
                state: false,
                postal_code: false,
            },
            visual_shipping: {
                country: false,
                address: false,
                address2: false,
                phone: false,
                city: false,
                state: false,
                postal_code: false,
            }
        },
        OrderSummaryV3,
        specialOffersSelected: [],
        phoneEnabled: true,
        AppWrapper,
        isMobileDevice: true,
        deviceInit: false,
        cartState: cartHydration ?? {},
        // FET-2766: re-resolves from the live `checkoutOffer` on every read (getter)
        // so cart mutations surface fresh offer-managed display data -- see
        // resolveCustomCheckoutOfferDataForCart above.
        get customCheckoutOfferData() {
            return resolveCustomCheckoutOfferDataForCartSticky(this.checkoutOffer);
        },
        funnelOverridePrimePromise: null,
        primedFunnelOverrideValue: null,
        skin,
        checkoutOffer,
        // FET-2641: 'lifetime' for physical-product carts, 'default' (30-day) otherwise.
        // Reactive via the `guaranteeBanner` getter below so bumper/upsell/shipping-category
        // changes that flip cart physicality are reflected immediately.
        get guaranteeBanner() {
            const hasPhysical = cartHasPhysicalProduct(this.checkoutOffer) || cartHasPhysicalProduct(this.cartState);
            return resolveGuaranteeBanner({
                cartHasPhysicalProduct: hasPhysical,
                guaranteeDays: this.guaranteeDays,
            });
        },
        get guaranteeDays() {
            return getDigitalGuaranteeDays(this.customCheckoutOfferData);
        },
        get countdownTimerEnabled() {
            return isCountdownTimerEnabled(this.customCheckoutOfferData, this.skin);
        },
        get guaranteeBadgeImageUrl() {
            return resolveGuaranteeBadgeImageUrl(this.guaranteeBanner);
        },
        get guaranteeTrustImageUrl() {
            return resolveGuaranteeTrustImageUrl(this.guaranteeBanner);
        },
        get displayCardBrand() {
            return resolveCardBrand(this.form, this.preAuthFields);
        },
        get displayCardLastFour() {
            return resolveCardLastFour(this.form, this.preAuthFields);
        },
        contactActive: true,
        shippingActive: false,
        braintreeActive: false,
        adyenActive: false,
        bumperActive: false,
        specialOfferSelected: false,
        bumpers,
        ccTokenized: false,
        ccTokenizing: false,
        ccReady: false,
        checkingOut: false,
        addressSynced: false,
        addressSyncing: false,
        addressSyncTimer: null,
        applePayCartSyncing: false,
        _applePaySheetTotalMinor: null,
        _applePayCartSyncGeneration: 0,
        _shippingAutoSelected: false,
        nextUrl: null,
        get orderReady() {
            // Order is ready when address is synced (tax + shipping resolved),
            // a shipping method is selected (or no physical shipping needed),
            // and the payment method is prepared
            if (!this.addressSynced) {
                //console.log('order not ready because of: addressSynced', this.addressSynced);
                return false;
            }
            if (this.processing) {
                //console.log('order not ready because of: processing', this.processing);
                return false;
            }
            // Shipping must be selected if available
            if (this.cartState.available_shipping_categories?.length > 0 && !this.selectedShippingOption) {
                //console.log('order not ready because of: selectedShippingOption', this.selectedShippingOption);
                return false;
            }
            // For credit card, CC fields must be ready
            if (this.payment_processor === 'credit_card' && !this.ccReady) {
                //console.log('order not ready because of: ccReady', this.ccReady);
                return false;
            }
            return true;
        },
        typingTimer: null,
        debounceDelay: 500,
        adyenCardComponent: null,
        adyenPaymentMethodsResponse: null,
        adyenPayPalCheckout: null,
        adyenPayPalSession: null,
        adyenPayPalComponent: null,
        onFieldChange() {
            // Debounce delay for auto-tokenization of CC once we have all the required info.
            // We wait this.debounceDelay milliseconds after the user inputs data to ensure they are done typing before we tokenize.
            clearTimeout(this.typingTimer);
            this.typingTimer = setTimeout(() => {
                this.typingTimer = null;
            }, this.debounceDelay);
        },
        readyToTokenize() {
            if (this.typingTimer) return false; // still typing
            if (this.ccTokenized) return false; // already tokenized
            if (!this.contactValid()) return false;
            if (!this.ccReady) return false;
            if (this.ccTokenizing) return false;
            return this.paymentErrors.length <= 0;
        },
        countries: cartHydration.shipping_category === null ? countries : countries.filter((c) => c['alpha-3'] === "USA" || c['alpha-3'] === "CAN"),
        states,
        checkoutErrors: [],
        cardValidationErrors: [],
        provinces,
        validationErrors: [],
        paymentErrors: [],
        userErrors: [],
        showPostalCode: true,
        nameError: false,
        emailError: false,
        processing: false,
        full_processing: false,
        payment_processor: "credit_card",
        available_payment_methods: filterDisabledPaymentMethods(['credit_card', 'paypal']),
        selectedShippingOption: checkoutOffer?.shipping_category?.id,
        ccFieldStatus: {
            number: false,
            expirationDate: false,
            cvv: false,
            postalCode: false
        },
        contactErrors: [],
        initializingPaypal: false,
        clientInstanceField: null,
        geoInfo: [],
        paypalEnabled: true,
        currencyConvertEnabled: false,
        geolocateEnabled: false,
        defaultCountry: 'US',
        tenant: 'sculptnation',
        countriesList: [{ "code": "AFN", "countries": ["Afghanistan"], "name": "Afghanistan Afghani", "symbol": "؋", "country_code": "AFG" }, { "code": "ARS", "countries": ["Argentina"], "name": "Argentine Peso", "symbol": "$", "country_code": "ARG" }, { "code": "AWG", "countries": ["Aruba"], "name": "Aruban florin", "symbol": "ƒ", "country_code": "ABW" }, { "code": "AUD", "countries": ["Australia"], "name": "Australian Dollar", "symbol": "A$", "country_code": "AUS" }, { "code": "AZN", "countries": ["Azerbaijan"], "name": "Azerbaijani Manat", "symbol": "₼", "country_code": "AZE" }, { "code": "BSD", "countries": ["The Bahamas"], "name": "Bahamas Dollar", "symbol": "B$", "country_code": "BHS" }, { "code": "BBD", "countries": ["Barbados"], "name": "Barbados Dollar", "symbol": "Bds$", "country_code": "BRB" }, { "code": "BDT", "countries": ["People's Republic of Bangladesh"], "name": "Bangladeshi taka", "symbol": "৳", "country_code": "BGD" }, { "code": "BYN", "countries": ["Belarus"], "name": "Belarus Ruble", "symbol": "Br", "country_code": "BLR" }, { "code": "BZD", "countries": ["Belize"], "name": "Belize Dollar", "symbol": "BZ$", "country_code": "BLZ" }, { "code": "BMD", "countries": ["British Overseas Territory of Bermuda"], "name": "Bermudian Dollar", "symbol": "BD$", "country_code": "BMU" }, { "code": "BOP", "countries": ["Bolivia"], "name": "Boliviano", "symbol": "Bs", "country_code": "BOL" }, { "code": "BAM", "countries": ["Bosnia"], "name": "Bosnia-Herzegovina Convertible Marka", "symbol": "KM", "country_code": "BIH" }, { "code": "BAM", "countries": ["Herzegovina"], "name": "Bosnia-Herzegovina Convertible Marka", "symbol": "KM", "country_code": "BIH" }, { "code": "BWP", "countries": ["Botswana"], "name": "Botswana pula", "symbol": "P", "country_code": "BWA" }, { "code": "BGN", "countries": ["Bulgaria"], "name": "Bulgarian lev", "symbol": "лв", "country_code": "BGR" }, { "code": "BRL", "countries": ["Brazil"], "name": "Brazilian real", "symbol": "R$", "country_code": "BRA" }, { "code": "BND", "countries": ["Sultanate of Brunei"], "name": "Brunei dollar", "symbol": "B$", "country_code": "BRN" }, { "code": "KHR", "countries": ["Cambodia"], "name": "Cambodian riel", "symbol": "៛", "country_code": "KHM" }, { "code": "CAD", "countries": ["Canada"], "name": "Canadian dollar", "symbol": "C$", "country_code": "CAN" }, { "code": "KYD", "countries": ["Cayman Islands"], "name": "Cayman Islands dollar", "symbol": "$", "country_code": "CYM" }, { "code": "CLP", "countries": ["Chile"], "name": "Chilean peso", "symbol": "$", "country_code": "CHL" }, { "code": "CNY", "countries": ["China"], "name": "Chinese Yuan Renminbi", "symbol": "¥", "country_code": "CHN" }, { "code": "COP", "countries": ["Colombia"], "name": "Colombian peso", "symbol": "$", "country_code": "COL" }, { "code": "CRC", "countries": ["Costa Rica"], "name": "Costa Rican colón", "symbol": "₡", "country_code": "CRI" }, { "code": "HRK", "countries": ["Croatia"], "name": "Croatian kuna", "symbol": "kn", "country_code": "HRV" }, { "code": "CUP", "countries": ["Cuba"], "name": "Cuban peso", "symbol": "₱", "country_code": "CUB" }, { "code": "CZK", "countries": ["Czech Republic"], "name": "Czech koruna", "symbol": "Kč", "country_code": "CZE" }, { "code": "DKK", "countries": ["Denmark"], "name": "Danish krone", "symbol": "kr", "country_code": "DNK" }, { "code": "DKK", "countries": ["Greenland"], "name": "Danish krone", "symbol": "kr", "country_code": "GRL" }, { "code": "DKK", "countries": ["The Faroe Islands"], "name": "Danish krone", "symbol": "kr", "country_code": "FRO" }, { "code": "DOP", "countries": ["Dominican Republic"], "name": "Dominican peso", "symbol": "RD$", "country_code": "DOM" }, { "code": "XCD", "countries": ["Antigua and Barbuda"], "name": "Eastern Caribbean dollar", "symbol": "$", "country_code": "ATG" }, { "code": "XCD", "countries": ["Commonwealth of Dominica"], "name": "Eastern Caribbean dollar", "symbol": "$", "country_code": "DMA" }, { "code": "XCD", "countries": ["Grenada"], "name": "Eastern Caribbean dollar", "symbol": "$", "country_code": "GRD" }, { "code": "XCD", "countries": ["Montserrat"], "name": "Eastern Caribbean dollar", "symbol": "$", "country_code": "MSR" }, { "code": "XCD", "countries": ["St. Kitts and Nevis"], "name": "Eastern Caribbean dollar", "symbol": "$", "country_code": "KNA" }, { "code": "XCD", "countries": ["Saint Lucia"], "name": "Eastern Caribbean dollar", "symbol": "$", "country_code": "LCA" }, { "code": "XCD", "countries": ["St. Vincent and The Grenadines"], "name": "Eastern Caribbean dollar", "symbol": "$", "country_code": "VCT" }, { "code": "EGP", "countries": ["Egypt"], "name": "Egyptian pound", "symbol": "£", "country_code": "EGY" }, { "code": "SVC", "countries": ["El Salvador"], "name": "Salvadoran colón", "symbol": "$", "country_code": "SLV" }, { "code": "EEK", "countries": ["Estonia"], "name": "Estonian kroon", "symbol": "Kr", "country_code": "EST" }, { "code": "EUR", "countries": ["European Union"], "name": "Euro", "symbol": "€", "country_code": "EU" }, { "code": "EUR", "countries": ["Italy"], "name": "Euro", "symbol": "€", "country_code": "ITA" }, { "code": "EUR", "countries": ["Belgium"], "name": "Euro", "symbol": "€", "country_code": "BEL" }, { "code": "EUR", "countries": ["Bulgaria"], "name": "Euro", "symbol": "€", "country_code": "BGR" }, { "code": "EUR", "countries": ["Croatia"], "name": "Euro", "symbol": "€", "country_code": "HRV" }, { "code": "EUR", "countries": ["Cyprus"], "name": "Euro", "symbol": "€", "country_code": "CYP" }, { "code": "EUR", "countries": ["Czechia"], "name": "Euro", "symbol": "€", "country_code": "CZE" }, { "code": "EUR", "countries": ["Denmark"], "name": "Euro", "symbol": "€", "country_code": "DNK" }, { "code": "EUR", "countries": ["Estonia"], "name": "Euro", "symbol": "€", "country_code": "EST" }, { "code": "EUR", "countries": ["Finland"], "name": "Euro", "symbol": "€", "country_code": "FIN" }, { "code": "EUR", "countries": ["France"], "name": "Euro", "symbol": "€", "country_code": "FRA" }, { "code": "EUR", "countries": ["Germany"], "name": "Euro", "symbol": "€", "country_code": "DEU" }, { "code": "EUR", "countries": ["Greece"], "name": "Euro", "symbol": "€", "country_code": "GRC" }, { "code": "EUR", "countries": ["Hungary"], "name": "Euro", "symbol": "€", "country_code": "HUN" }, { "code": "EUR", "countries": ["Ireland"], "name": "Euro", "symbol": "€", "country_code": "IRL" }, { "code": "EUR", "countries": ["Latvia"], "name": "Euro", "symbol": "€", "country_code": "LVA" }, { "code": "EUR", "countries": ["Lithuania"], "name": "Euro", "symbol": "€", "country_code": "LTU" }, { "code": "EUR", "countries": ["Luxembourg"], "name": "Euro", "symbol": "€", "country_code": "LUX" }, { "code": "EUR", "countries": ["Malta"], "name": "Euro", "symbol": "€", "country_code": "MLT" }, { "code": "EUR", "countries": ["Netherlands"], "name": "Euro", "symbol": "€", "country_code": "NLD" }, { "code": "EUR", "countries": ["Poland"], "name": "Euro", "symbol": "€", "country_code": "POL" }, { "code": "EUR", "countries": ["Portugal"], "name": "Euro", "symbol": "€", "country_code": "PRT" }, { "code": "EUR", "countries": ["Romania"], "name": "Euro", "symbol": "€", "country_code": "ROU" }, { "code": "EUR", "countries": ["Slovakia"], "name": "Euro", "symbol": "€", "country_code": "SVK" }, { "code": "EUR", "countries": ["Spain"], "name": "Euro", "symbol": "€", "country_code": "ESP" }, { "code": "EUR", "countries": ["Sweden"], "name": "Euro", "symbol": "€", "country_code": "SWE" }, { "code": "FKP", "countries": ["Falkland Islands"], "name": "Falkland Islands (Malvinas) Pound", "symbol": "FK£", "country_code": "FLK" }, { "code": "FJD", "countries": ["Fiji"], "name": "Fijian dollar", "symbol": "FJ$", "country_code": "FJI" }, { "code": "GHC", "countries": ["Ghana"], "name": "Ghanaian cedi", "symbol": "GH¢", "country_code": "GHA" }, { "code": "GIP", "countries": ["Gibraltar"], "name": "Gibraltar pound", "symbol": "£", "country_code": "GIB" }, { "code": "GTQ", "countries": ["Guatemala"], "name": "Guatemalan quetzal", "symbol": "Q", "country_code": "GTM" }, { "code": "GGP", "countries": ["Guernsey"], "name": "Guernsey pound", "symbol": "Q", "country_code": "GGY" }, { "code": "GYD", "countries": ["Guyana"], "name": "Guyanese dollar", "symbol": "GY$", "country_code": "GUY" }, { "code": "HNL", "countries": ["Honduras"], "name": "Honduran lempira", "symbol": "L", "country_code": "HND" }, { "code": "HKD", "countries": ["Hong Kong"], "name": "Hong Kong dollar", "symbol": "HK$", "country_code": "HKG" }, { "code": "HUF", "countries": ["Hungary"], "name": "Hungarian forint", "symbol": "Ft", "country_code": "HUN" }, { "code": "ISK", "countries": ["Iceland"], "name": "Icelandic króna", "symbol": "íkr", "country_code": "ISL" }, { "code": "INR", "countries": ["India"], "name": "Indian rupee", "symbol": "₹", "country_code": "IND" }, { "code": "IDR", "countries": ["Indonesia"], "name": "Indonesian rupiah", "symbol": "Rp", "country_code": "IDN" }, { "code": "IRR", "countries": ["Iran"], "name": "Iranian rial", "symbol": "﷼", "country_code": "IRN" }, { "code": "IMP", "countries": ["Isle of Man"], "name": "Manx pound", "symbol": "£", "country_code": "IMN" }, { "code": "ILS", "countries": ["Israel"], "name": "Israeli Shekel", "symbol": "₪", "country_code": "ISR" }, { "code": "ILS", "countries": ["Palestinian territories of the West Bank"], "name": "Israeli Shekel", "symbol": "₪", "country_code": "PSE" }, { "code": "ILS", "countries": ["The Gaza Strip"], "name": "Israeli Shekel", "symbol": "₪", "country_code": "PSE" }, { "code": "JMD", "countries": ["Jamaica"], "name": "Jamaican dollar", "symbol": "J$", "country_code": "JAM" }, { "code": "JPY", "countries": ["Japan"], "name": "Japanese yen", "symbol": "¥", "country_code": "JPN" }, { "code": "JEP", "countries": ["Jersey"], "name": "Jersey pound", "symbol": "£", "country_code": "JEY" }, { "code": "KZT", "countries": ["Kazakhstan"], "name": "Kazakhstani tenge", "symbol": "₸", "country_code": "KAZ" }, { "code": "KPW", "countries": ["North Korea"], "name": "North Korean won", "symbol": "₩", "country_code": "PRK" }, { "code": "KPW", "countries": ["South Korea"], "name": "South Korean won", "symbol": "₩", "country_code": "KOR" }, { "code": "KGS", "countries": ["Kyrgyz Republic"], "name": "Kyrgyzstani som", "symbol": "лв", "country_code": "KGZ" }, { "code": "LAK", "countries": ["Laos"], "name": "Lao kip", "symbol": "₭", "country_code": "LAO" }, { "code": "LBP", "countries": ["Lebanon"], "name": "Lebanese pound", "symbol": "ل.ل", "country_code": "LBN" }, { "code": "LRD", "countries": ["Liberia"], "name": "Liberian dollar", "symbol": "L$", "country_code": "LBR" }, { "code": "LTL", "countries": ["Lithuania"], "name": "Lithuanian litas", "symbol": "€", "country_code": "LTU" }, { "code": "MKD", "countries": ["North Macedonia"], "name": "Macedonian denar", "symbol": "ден", "country_code": "MKD" }, { "code": "MYR", "countries": ["Malaysia"], "name": "Malaysian ringgit", "symbol": "RM", "country_code": "MYS" }, { "code": "MUR", "countries": ["Mauritius"], "name": "Mauritian rupee", "symbol": "Rs", "country_code": "MUS" }, { "code": "MXN", "countries": ["Mexico"], "name": "Mexican peso", "symbol": "Mex$", "country_code": "MEX" }, { "code": "MNT", "countries": ["Mongolia"], "name": "Mongolian tögrög", "symbol": "₮", "country_code": "MNG" }, { "code": "MZN", "countries": ["Mozambique"], "name": "Mozambican metical", "symbol": "MT", "country_code": "MOZ" }, { "code": "NAD", "countries": ["Namibia"], "name": "Namibian dollar", "symbol": "N$", "country_code": "NAM" }, { "code": "NPR", "countries": ["Federal Democratic Republic of Nepal"], "name": "Nepalese rupee", "symbol": "Rs.", "country_code": "NPL" }, { "code": "ANG", "countries": ["Curaçao"], "name": "Netherlands Antillean guilder", "symbol": "ƒ", "country_code": "CUW" }, { "code": "ANG", "countries": ["Sint Maarten"], "name": "Netherlands Antillean guilder", "symbol": "ƒ", "country_code": "SXM" }, { "code": "NZD", "countries": ["New Zealand"], "name": "New Zealand Dollar", "symbol": "$", "country_code": "NZL" }, { "code": "NZD", "countries": ["The Cook Islands"], "name": "New Zealand Dollar", "symbol": "$", "country_code": "COK" }, { "code": "NZD", "countries": ["Niue"], "name": "New Zealand Dollar", "symbol": "$", "country_code": "NIU" }, { "code": "NZD", "countries": ["The Ross Dependency"], "name": "New Zealand Dollar", "symbol": "$", "country_code": "ATA" }, { "code": "NZD", "countries": ["Tokelau"], "name": "New Zealand Dollar", "symbol": "$", "country_code": "TKL" }, { "code": "NZD", "countries": ["The Pitcairn Islands"], "name": "New Zealand Dollar", "symbol": "$", "country_code": "PCN" }, { "code": "NIO", "countries": ["Nicaragua"], "name": "Nicaraguan córdoba", "symbol": "C$", "country_code": "NIC" }, { "code": "NGN", "countries": ["Nigeria"], "name": "Nigerian Naira", "symbol": "₦", "country_code": "NGA" }, { "code": "NOK", "countries": ["Norway"], "name": "Norwegian krone", "symbol": "kr", "country_code": "NOR" }, { "code": "OMR", "countries": ["Oman"], "name": "Omani rial", "symbol": "﷼", "country_code": "OMN" }, { "code": "PKR", "countries": ["Pakistan"], "name": "Pakistani rupee", "symbol": "Rs", "country_code": "PAK" }, { "code": "PAB", "countries": ["Panama"], "name": "Panamanian balboa", "symbol": "B/.", "country_code": "PAN" }, { "code": "PYG", "countries": ["Paraguay"], "name": "Paraguayan Guaraní", "symbol": "₲", "country_code": "PRY" }, { "code": "PEN", "countries": ["Peru"], "name": "Sol", "symbol": "S/.", "country_code": "PER" }, { "code": "PHP", "countries": ["Philippines"], "name": "Philippine peso", "symbol": "₱", "country_code": "PHL" }, { "code": "PLN", "countries": ["Poland"], "name": "Polish złoty", "symbol": "zł", "country_code": "POL" }, { "code": "QAR", "countries": ["State of Qatar"], "name": "Qatari Riyal", "symbol": "﷼", "country_code": "QAT" }, { "code": "RON", "countries": ["Romania"], "name": "Romanian leu (Leu românesc)", "symbol": "L", "country_code": "ROU" }, { "code": "RUB", "countries": ["Russian Federation"], "name": "Russian ruble", "symbol": "₽", "country_code": "RUS" }, { "code": "RUB", "countries": ["Abkhazia and South Ossetia"], "name": "Russian ruble", "symbol": "₽", "country_code": "RUS" }, { "code": "RUB", "countries": ["Donetsk and Luhansk"], "name": "Russian ruble", "symbol": "₽", "country_code": "RUS" }, { "code": "SHP", "countries": ["Saint Helena"], "name": "Saint Helena pound", "symbol": "£", "country_code": "SHN" }, { "code": "SHP", "countries": ["Ascension"], "name": "Saint Helena pound", "symbol": "£", "country_code": "SHN" }, { "code": "SHP", "countries": ["Tristan da Cunha"], "name": "Saint Helena pound", "symbol": "£", "country_code": "SHN" }, { "code": "SAR", "countries": ["Saudi Arabia"], "name": "Saudi riyal", "symbol": "﷼", "country_code": "SAU" }, { "code": "RSD", "countries": ["Serbia"], "name": "Serbian dinar", "symbol": "дин", "country_code": "SRB" }, { "code": "SCR", "countries": ["Seychelles"], "name": "Seychellois rupee", "symbol": "Rs", "country_code": "SYC" }, { "code": "SGD", "countries": ["Singapore"], "name": "Singapore dollar", "symbol": "S$", "country_code": "SGP" }, { "code": "SBD", "countries": ["Solomon Islands"], "name": "Solomon Islands dollar", "symbol": "SI$", "country_code": "SLB" }, { "code": "SOS", "countries": ["Somalia"], "name": "Somali shilling", "symbol": "Sh.So.", "country_code": "SOM" }, { "code": "ZAR", "countries": ["South Africa"], "name": "South African rand", "symbol": "R", "country_code": "ZAF" }, { "code": "LKR", "countries": ["Sri Lanka"], "name": "Sri Lankan rupee", "symbol": "Rs", "country_code": "LKA" }, { "code": "SEK", "countries": ["Sweden"], "name": "Swedish krona", "symbol": "kr", "country_code": "SWE" }, { "code": "CHF", "countries": ["Switzerland"], "name": "Swiss franc", "symbol": "CHF", "country_code": "CHE" }, { "code": "SRD", "countries": ["Suriname"], "name": "Suriname Dollar", "symbol": "Sr$", "country_code": "SUR" }, { "code": "SYP", "countries": ["Syria"], "name": "Syrian pound", "symbol": "£S", "country_code": "SYR" }, { "code": "TWD", "countries": ["Taiwan"], "name": "New Taiwan dollar", "symbol": "NT$", "country_code": "TWN" }, { "code": "THB", "countries": ["Thailand"], "name": "Thai baht", "symbol": "฿", "country_code": "THA" }, { "code": "TTD", "countries": ["Trinidad", "Tobago"], "name": "Trinidad and Tobago dollar", "symbol": "TT$", "country_code": "TTO" }, { "code": "TRY", "countries": ["Turkey", "Turkish Republic of Northern Cyprus"], "name": "Turkey Lira", "symbol": "₺", "country_code": "TUR" }, { "code": "TVD", "countries": ["Tuvalu"], "name": "Tuvaluan dollar", "symbol": "TV$", "country_code": "TUV" }, { "code": "UAH", "countries": ["Ukraine"], "name": "Ukrainian hryvnia", "symbol": "₴", "country_code": "UKR" }, { "code": "GBP", "countries": ["United Kingdom"], "name": "Pound sterling", "symbol": "£", "country_code": "GBR" }, { "code": "GBP", "countries": ["Jersey"], "name": "Pound sterling", "symbol": "£", "country_code": "JEY" }, { "code": "GBP", "countries": ["Guernsey"], "name": "Pound sterling", "symbol": "£", "country_code": "GGY" }, { "code": "GBP", "countries": ["The Isle of Man"], "name": "Pound sterling", "symbol": "£", "country_code": "IMN" }, { "code": "GBP", "countries": ["Gibraltar"], "name": "Pound sterling", "symbol": "£", "country_code": "GIB" }, { "code": "GBP", "countries": ["South Georgia"], "name": "Pound sterling", "symbol": "£", "country_code": "SGS" }, { "code": "GBP", "countries": ["The South Sandwich Islands"], "name": "Pound sterling", "symbol": "£", "country_code": "SGS" }, { "code": "GBP", "countries": ["The British Antarctic Territory"], "name": "Pound sterling", "symbol": "£", "country_code": "BVT" }, { "code": "GBP", "countries": ["Tristan da Cunha"], "name": "Pound sterling", "symbol": "£", "country_code": "SHN" }, { "code": "UGX", "countries": ["Uganda"], "name": "Ugandan shilling", "symbol": "USh", "country_code": "UGA" }, { "code": "USD", "countries": ["United States"], "name": "United States dollar", "symbol": "$", "country_code": "USA" }, { "code": "UYU", "countries": ["Uruguayan"], "name": "Peso Uruguayolar", "symbol": "$U", "country_code": "URY" }, { "code": "UZS", "countries": ["Uzbekistan"], "name": "Uzbekistani soʻm", "symbol": "лв", "country_code": "UZB" }, { "code": "VEF", "countries": ["Venezuela"], "name": "Venezuelan bolívar", "symbol": "Bs", "country_code": "VEN" }, { "code": "VND", "countries": ["Vietnam"], "name": "Vietnamese dong (Đồng)", "symbol": "₫", "country_code": "VNM" }, { "code": "YER", "countries": ["Yemen"], "name": "Yemeni rial", "symbol": "﷼", "country_code": "YEM" }, { "code": "ZWD", "countries": ["Zimbabwe"], "name": "Zimbabwean dollar", "symbol": "Z$", "country_code": "ZWE" }],
        paypalOrderOptions: {
            requestBillingAgreement: true,
            flow: 'checkout',
            amount: 0,
            currency: 'USD',
            enableShippingAddress: false,
            shippingAddressEditable: false,
        },
        paypalDeliveryAddress: {
            street: '',
            city: '',
            stateOrProvince: '',
            postalCode: '',
            countryCode: 'US',
        },
        paypalAuthorizedEvent: {
            id: null,
            payer_id: null,
            email_address: null,
        },
        get cartOffers() {
            return this.checkoutOffer?.order_offers?.filter(offer => offer.type === 'product');
        },
        get cartProductCount() {
            return this.checkoutOffer.order_offers.filter(offer => offer.type === 'product').length;
        },
        get sortedShippingOptions() {
            if (!this.checkoutOffer?.available_shipping_categories) return [];
            return [...this.checkoutOffer.available_shipping_categories].sort(
                (a, b) => a.price_base.amount.amount - b.price_base.amount.amount
            );
        },
        async selectShippingOption(methodId) {
            if (this.processing) return;
            this.clearAllErrors();
            this.processing = true;
            try {
                const result = await window.CheckoutAPI.post(this, `/api/cart/shipping-categories/${methodId.id}`, {}, {
                    context: { operation: 'select_shipping_method' },
                    headers: getHeaders(),
                    timeout: 15000
                });
                if (!result.success) {
                    if (!result.handled) {
                        throw new Error(result.message || 'Failed to update shipping method');
                    }
                    return;
                }
                const processedResult = this.convertCurrency(result.data.data, this.countriesList, this.form.billing_address.country);
                this.checkoutOffer = processedResult;
                this.cartState = processedResult;
                this.selectedShippingOption = methodId.id;
                this.reloadComponents();
            } catch (error) {
                console.error('Error selecting shipping option:', error);
                this.clearAllErrors();
                this.checkoutErrors = [error.message || 'Failed to update shipping method. Please try again.'];
            } finally {
                this.processing = false;
            }
        },
        async _detectCountryFromIP() {
            try {
                const response = await fetch('https://ipapi.co/json/');
                if (!response.ok) return;
                const data = await response.json();
                if (data && data.country_code) {
                    const detectedCountry = data.country_code; // alpha-2, e.g. "US", "CA", "GB"
                    // Only set if the user hasn't manually changed from the default
                    if (this.form.billing_address.country === 'US' || this.form.billing_address.country === '') {
                        this.form.billing_address.country = detectedCountry;
                    }
                    if (this.form.visual_shipping.country === 'US' || this.form.visual_shipping.country === '') {
                        this.form.visual_shipping.country = detectedCountry;
                    }
                    if (this.form.shipping_address.country === 'US' || this.form.shipping_address.country === '') {
                        this.form.shipping_address.country = detectedCountry;
                    }
                    // Update paypal options currency if available
                    if (data.currency && this.paypalOrderOptions) {
                        this.paypalOrderOptions.currency = data.currency;
                    }
                    this.geoInfo = data;
                    console.log('[GeoIP] Detected country:', detectedCountry);
                }
            } catch (error) {
                // Silently fail - country detection is a nice-to-have, not critical
                console.warn('[GeoIP] Country detection failed:', error.message);
            }
        },
        get shipping_amount() {
            return this.checkoutOffer?.shipping_category?.price_local?.amount;
        },
        get tax_amount() {
            return this.checkoutOffer?.tax_amount_local;
        },
        get card_validation_errors() {
            return this.cardValidationErrors.reduce((obj, field) => {
                obj[field] = true;
                return obj;
            }, {});
        },
        get cc_tokenizing() {
            return this.ccTokenizing;
        },
        get subtotal_amount() {
            const total = this.cartOffers.reduce((acc, offer) => {
                return acc + (parseFloat(offer.amount?.amount) || 0);
            }, 0);
            // If the total is in cents:
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(total / 100);
        },
        get isAdyenGateway() {
            return this.gateway === 'Adyen' || this.gateway === 'Adyen_Testing';
        },
        get isBraintreeGateway() {
            return this.gateway === 'Braintree' || this.gateway === 'Braintree_Testing';
        },
        form: {
            user: {
                email: '',
                name: '',
                phone: '',
            },
            billing_address: {
                name: '',
                company_name: '',
                country: 'US',
                address: '',
                address2: '',
                city: '',
                state: '',
                postal_code: '',
            },
            shipping_same: true,
            shipping_address: {
                first_name: '',
                last_name: '',
                name: '',
                phone: '',
                company_name: '',
                country: 'US',
                address: '',
                address2: '',
                city: '',
                state: '',
                postal_code: '',
            },
            visual_shipping: {
                first_name: '',
                last_name: '',
                name: '',
                phone: '',
                company_name: '',
                country: 'US',
                address: '',
                address2: '',
                city: '',
                state: '',
                postal_code: '',
            },
            payment: {},
            reset_session: true,
            mobile: '',
            form_time: Math.floor(Date.now() / 1000)
        },
        fieldRules: {
            name: {
                required: true,
                max: 255,
                message: 'Please enter your full name',
                regexMessage: 'Enter a valid name'
            },
            email: {
                required: true,
                regex: /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/,
                max: 255,
                message: 'Please enter your email address',
                regexMessage: 'Enter a valid email'
            },
            phone: {
                required: false,
                regex: /^\d{10,14}$/,
                replace: /[()-\s]/g,
                max: 255,
                message: 'Please enter your phone number',
                regexMessage: 'Enter your phone number'
            },
            address: {
                required: true,
                min: 3,
                max: 255,
                message: 'Please enter your billing address',
                regexMessage: 'Please enter a complete street address'
            },
            city: {
                required: true,
                min: 1,
                max: 255,
                message: 'Please enter your city',
                regexMessage: 'Please enter a valid city name'
            },
            state: {
                required: true,
                max: 255,
                message: 'Please enter your state / province / region'
            },
            postal_code: {
                required: true,
                min: 1,
                max: 15,
                message: 'Please enter your zip or postal code',
                regexMessage: 'Please enter a valid postal code'
            },
            country: {
                required: true,
                max: 255,
                message: 'Please enter your country'
            }
        },
        ccFields: {
            number: {
                selector: '#braintree_field_credit_card_v3',
                placeholder: 'Card number',
                braintreeField: 'number',
                isFocused: false,
            },
            expirationDate: {
                selector: '#braintree_field_date_v3',
                placeholder: 'Exp. (MM/YYYY)',
                braintreeField: 'expirationDate',
                isFocused: false,
            },
            cvv: {
                selector: '#braintree_field_cvv_v3',
                placeholder: 'CVV',
                braintreeField: 'cvv',
                isFocused: false,
            },
            postalCode: {
                selector: '#braintree_field_postal_code_v3',
                placeholder: 'Postal Code',
                braintreeField: 'postalCode',
                isFocused: false,
            }
        },
        contactValid() {
            // return this.validateContact(false).length <= 0;
        },
        get productOffer() {
            // Find the first product-type offer
            if (!this.cart || !this.cart.order_offers) return null;
            return this.cart.order_offers.find(o => o && o.type === 'product') || null;
        },
        validateFieldLive(obj, field, addErrors = true) {
            this.onFieldChange();
            const errors = this.validateField(obj, field);
            if (errors.length > 0 && addErrors) {
                this.contactErrors = this.contactErrors.filter(error => error.field !== field);
                this.contactErrors.push(...errors);
            } else {
                this.contactErrors = this.contactErrors.filter(error => error.field !== field);
            }
        },
        validateField(obj, field) {
            const errors = [];
            const rules = this.fieldRules[field]; // e.g. 'name', 'email', etc.
            if (!rules) return errors;

            // Safely extract the actual value from the given object
            const value = (obj[field] || '').trim();

            // Required
            if (rules.required && !value) {
                errors.push({ field, message: rules.message });
            }
            // Min length
            if (rules.min && value.length < rules.min) {
                errors.push({ field, message: rules.regexMessage || `Please enter at least ${rules.min} characters.` });
            }
            // Max length
            if (rules.max && value.length > rules.max) {
                errors.push({ field, message: `Please enter fewer than ${rules.max} characters.` });
            }
            // Regex
            if (rules.regex && !rules.regex.test(value)) {
                errors.push({ field, message: rules.regexMessage });
            }

            return errors;
        },

        validateContact(addErrors = true) {
            const errors = [];

            const contactBillingFields = [
                'name',
                'email',
                'billing_address',
                'billing_city',
                'billing_state',
                'billing_postal_code',
                'billing_country',
                'shipping_address',
                'shipping_city',
                'shipping_state',
                'shipping_postal_code',
                'shipping_country',
            ];

            // For PayPal users, only validate name and email.
            // PayPal itself handles address fields
            if (this.payment_processor === 'paypal' || this.payment_processor === 'apple_pay') {
                // Validate only user fields (name, email)
                errors.push(
                    ...this.validateField(this.form.user, 'name'),
                    ...this.validateField(this.form.user, 'email')
                );

                // Populate contactDetailErrors for UI display
                const nameErrors = this.validateField(this.form.user, 'name');
                const emailErrors = this.validateField(this.form.user, 'email');

                this.contactDetailErrors.name = nameErrors.length > 0 ? { message: nameErrors[0].message } : false;
                this.contactDetailErrors.email = emailErrors.length > 0 ? { message: emailErrors[0].message } : false;

                if (addErrors) {
                    this.contactErrors = errors;
                }
                return errors;
            }

            // Validate user
            errors.push(
                ...this.validateField(this.form.user, 'name'),
                ...this.validateField(this.form.user, 'email')
            );

            // Validate billing address - country-aware validation
            errors.push(
                ...this.validateField(this.form.billing_address, 'address'),
                ...this.validateField(this.form.billing_address, 'city'),
                ...this.validateField(this.form.billing_address, 'country')
            );

            // State/Province/Region - use existing fieldRules validation
            errors.push(
                ...this.validateField(this.form.billing_address, 'state')
            );

            // Postal code - most countries need this
            errors.push(
                ...this.validateField(this.form.billing_address, 'postal_code')
            );

            // Validate shipping if shipping_same is false
            if (!this.form.shipping_same) {
                errors.push(
                    ...this.validateField(this.form.visual_shipping, 'address'),
                    ...this.validateField(this.form.visual_shipping, 'city'),
                    ...this.validateField(this.form.visual_shipping, 'country')
                );

                // State/Province for shipping - use existing fieldRules validation
                errors.push(
                    ...this.validateField(this.form.visual_shipping, 'state')
                );

                errors.push(
                    ...this.validateField(this.form.visual_shipping, 'postal_code')
                );
            }

            contactBillingFields.forEach(field => {
                if (field.includes('billing')) {
                    const errors = this.validateField(this.form.billing_address, field.replace('billing_', ''));
                    this.contactDetailErrors.billing_address[field.replace('billing_', '')] = errors.length > 0 ? { message: errors[0].message } : false;
                } else if (field.includes('shipping')) {
                    if (this.form.shipping_same) {
                        this.contactDetailErrors.visual_shipping[field.replace('shipping_', '')] = false;
                        return;
                    }
                    const errors = this.validateField(this.form.visual_shipping, field.replace('shipping_', ''));
                    this.contactDetailErrors.visual_shipping[field.replace('shipping_', '')] = errors.length > 0 ? { message: errors[0].message } : false;
                } else {
                    const errors = this.validateField(this.form.user, field);
                    this.contactDetailErrors[field] = errors.length > 0 ? { message: errors[0].message } : false;
                }
            });


            if (addErrors) {
                this.contactErrors = errors;
            }

            return errors;
        },

        // Add comprehensive validation before any API call
        validateAllRequiredFields() {
            const errors = [];

            // For PayPal users, only validate name and email
            // Address fields are handled by PayPal itself
            if (this.payment_processor === 'paypal') {
                // User fields only
                if (!this.form.user.name?.trim()) errors.push('Name is required');
                if (!this.form.user.email?.trim()) errors.push('Email is required');

                return {
                    valid: errors.length === 0,
                    errors: errors
                };
            }

            // User fields
            if (!this.form.user.name?.trim()) errors.push('Name is required');
            if (!this.form.user.email?.trim()) errors.push('Email is required');

            // Billing address - country-aware validation
            if (!this.form.billing_address.address?.trim()) errors.push('Billing address is required');
            if (!this.form.billing_address.city?.trim()) errors.push('Billing city is required');
            if (!this.form.billing_address.country?.trim()) errors.push('Billing country is required');

            // State/Province/Region - use existing fieldRules validation
            const billingStateErrors = this.validateField(this.form.billing_address, 'state');
            if (billingStateErrors.length > 0) {
                errors.push(billingStateErrors[0].message);
            }

            // Postal code - most countries need this but be flexible
            if (!this.form.billing_address.postal_code?.trim()) {
                errors.push('Billing postal code is required');
            }

            // Shipping address (required if different from billing)
            if (!this.form.shipping_same) {
                if (!this.form.visual_shipping.address?.trim()) errors.push('Shipping address is required');
                if (!this.form.visual_shipping.city?.trim()) errors.push('Shipping city is required');
                if (!this.form.visual_shipping.country?.trim()) errors.push('Shipping country is required');

                // State/Province for shipping - use existing fieldRules validation
                const shippingStateErrors = this.validateField(this.form.visual_shipping, 'state');
                if (shippingStateErrors.length > 0) {
                    errors.push(shippingStateErrors[0].message);
                }

                if (!this.form.visual_shipping.postal_code?.trim()) {
                    errors.push('Shipping postal code is required');
                }
            }

            // Payment method validation
            if (this.payment_processor === 'credit_card') {
                // For Braintree, check ccReady flag (Adyen handles its own validation during tokenization)
                if (this.gateway === 'Braintree' || this.gateway === 'Braintree_Testing') {
                    if (!this.ccReady) errors.push('Please complete credit card information');
                }
            }

            return {
                valid: errors.length === 0,
                errors: errors
            };
        },

        preAuthFields: {
            endDigits: null,
            issuerBin: null,
            expiryDate: null,
            cardBrand: null,
        },

        buildPreAuth(cardholderName = '') {
            const [expirationMonth = '', expirationYear = ''] = (this.preAuthFields.expiryDate || '').split('/');
            return {
                cardholderName,
                issuerBin: String(this.preAuthFields.issuerBin),
                endDigits: this.preAuthFields.endDigits,
                expiryDate: this.preAuthFields.expiryDate,
                expirationMonth,
                expirationYear,
                cardBrand: this.preAuthFields.cardBrand,
            };
        },

        isPreAuthComplete() {
            return this.preAuthFields.endDigits !== null
                && this.preAuthFields.expiryDate !== null
                && this.preAuthFields.cardBrand !== null
                && this.preAuthFields.issuerBin !== null
                && String(this.preAuthFields.issuerBin).length >= 6;
        },

        setPreAuthIfComplete(cardholderName = '') {
            if (!this.isPreAuthComplete()) {
                if (this.form?.payment) {
                    delete this.form.payment.preAuth;
                }
                return;
            }

            this.form.payment.preAuth = this.buildPreAuth(cardholderName);
        },

        warnIfPreAuthIncomplete() {
            const missing = Object.keys(this.preAuthFields).filter((key) => {
                if (key === 'issuerBin') {
                    return this.preAuthFields.issuerBin === null || String(this.preAuthFields.issuerBin).length < 6;
                }

                return this.preAuthFields[key] === null;
            });

            if (missing.length > 0) {
                console.warn('[Adyen] preAuth incomplete before submit; missing fields:', missing, this.preAuthFields);
            }
        },

        // Clear all error arrays
        clearAllErrors() {
            this.checkoutErrors = [];
            this.paymentErrors = [];
            this.userErrors = [];
            // Also clear card-specific errors that are used by the UI
            if (this.contactDetailErrors && this.contactDetailErrors.card) {
                this.contactDetailErrors.card.number = false;
                this.contactDetailErrors.card.expirationDate = false;
                this.contactDetailErrors.card.cvv = false;
            }

            // Clear field-specific errors
            this.clearFieldErrors();
        },

        // Robust error handling system that can handle any backend error gracefully
        _handleBackendError(response, errorResponse, context = {}) {
            // Log all errors to a monitoring system
            const backendError = new Error(errorResponse.message || `Backend error (${response.status})`);
            backendError.status = response.status;
            backendError.errors = errorResponse.errors;
            backendError.response = errorResponse;

            window.CheckoutErrorMonitor.log(backendError, {
                operation: context.operation || 'unknown',
                currentScene: this.currentScene,
                paymentProcessor: this.payment_processor,
                errorStatus: response.status,
                backendMessage: errorResponse.message,
                url: response.url
            });

            // Clear previous errors
            this.clearAllErrors();

            // Handle different error types with priority order
            if (this._handleStructuredErrors(errorResponse)) {
                return; // Structured errors handled
            }

            if (this._handleStatusCodeErrors(response, errorResponse)) {
                return; // Status-based errors handled
            }

            if (this._handleMessageErrors(errorResponse)) {
                return; // Message-based errors handled
            }

            // Final fallback
            this._handleFallbackError(response, errorResponse);
        },

        // Handle Laravel validation errors and structured error responses
        _handleStructuredErrors(errorResponse) {
            if (!errorResponse.errors || typeof errorResponse.errors !== 'object') {
                return false;
            }

            // Priority order for error types
            const errorPriority = [
                { key: 'gateway', target: 'paymentErrors' },
                { key: 'payment', target: 'paymentErrors' },
                { key: 'address', target: 'checkoutErrors' },
                { key: 'shipping_address', target: 'checkoutErrors' },
                { key: 'billing_address', target: 'checkoutErrors' },
                { key: 'user', target: 'userErrors' },
                { key: 'email', target: 'userErrors' },
                { key: 'phone', target: 'userErrors' }
            ];

            for (const { key, target } of errorPriority) {
                if (errorResponse.errors[key]) {
                    const errors = Array.isArray(errorResponse.errors[key])
                        ? errorResponse.errors[key]
                        : [errorResponse.errors[key]];
                    this[target] = errors.map(err => this._makeErrorUserFriendly(err)).filter(Boolean);
                    return true;
                }
            }

            const dotNotationUserErrors = [];
            const dotNotationCheckoutErrors = [];
            Object.keys(errorResponse.errors).forEach(key => {
                if (!key.includes('.')) {
                    return;
                }
                const fieldErrors = Array.isArray(errorResponse.errors[key])
                    ? errorResponse.errors[key]
                    : [errorResponse.errors[key]];
                const friendly = fieldErrors.map(err => this._makeErrorUserFriendly(err)).filter(Boolean);
                if (key.startsWith('user.')) {
                    dotNotationUserErrors.push(...friendly);
                } else {
                    dotNotationCheckoutErrors.push(...friendly);
                }
            });
            if (dotNotationUserErrors.length > 0) {
                this.userErrors = dotNotationUserErrors;
                return true;
            }
            if (dotNotationCheckoutErrors.length > 0) {
                this.checkoutErrors = dotNotationCheckoutErrors;
                return true;
            }

            // Handle any other structured errors
            const errorMessages = [];
            Object.keys(errorResponse.errors).forEach(key => {
                const fieldErrors = Array.isArray(errorResponse.errors[key])
                    ? errorResponse.errors[key]
                    : [errorResponse.errors[key]];
                errorMessages.push(...fieldErrors.map(err => this._makeErrorUserFriendly(err)).filter(Boolean));
            });

            if (errorMessages.length > 0) {
                this.checkoutErrors = errorMessages;
                return true;
            }

            return false;
        },

        // Handle errors based on HTTP status codes
        _handleStatusCodeErrors(response, errorResponse) {
            switch (true) {
                case response.status === 422: // Validation errors
                    if (errorResponse.message) {
                        this.checkoutErrors = [this._makeErrorUserFriendly(errorResponse.message)];
                        return true;
                    }
                    break;

                case response.status === 429: // Rate limiting
                    this.checkoutErrors = [
                        'Too many requests. Please wait a moment and try again.',
                        'If this continues, please contact support.'
                    ];
                    return true;

                case response.status >= 500: // Server errors
                    this.checkoutErrors = [
                        'We\'re experiencing temporary server issues.',
                        'Please try again in a moment. If this persists, contact support.'
                    ];
                    return true;

                case response.status === 404: // Not found
                    this.checkoutErrors = [
                        'The requested service is temporarily unavailable.',
                        'Please try again or contact support.'
                    ];
                    return true;

                case response.status === 403: // Forbidden
                    this.checkoutErrors = [
                        'Access denied. Please refresh the page and try again.',
                        'If this continues, please contact support.'
                    ];
                    return true;

                case response.status === 401: // Unauthorized
                    this.checkoutErrors = [
                        'Your session has expired. Please refresh the page.',
                        'If this continues, please contact support.'
                    ];
                    return true;
            }

            return false;
        },

        // Handle errors based on message content
        _handleMessageErrors(errorResponse) {
            if (!errorResponse.message) {
                return false;
            }

            const userFriendlyMessage = this._makeErrorUserFriendly(errorResponse.message);
            this.checkoutErrors = [userFriendlyMessage];
            return true;
        },

        // Final fallback error handler
        _handleFallbackError(response, errorResponse) {
            this.checkoutErrors = [
                'An unexpected error occurred during checkout.',
                'Please try again or contact support if the problem persists.'
            ];
        },

        // Enhanced user-friendly message converter with comprehensive pattern matching
        _makeErrorUserFriendly(technicalMessage) {
            if (!technicalMessage || typeof technicalMessage !== 'string') {
                return 'An unexpected error occurred. Please try again.';
            }

            const message = technicalMessage.toLowerCase();

            // Comprehensive error pattern matching
            const errorPatterns = [
                // Laravel/PHP specific errors
                {
                    pattern: /argument.*must be of type.*string.*null given/i,
                    handler: (msg) => {
                        if (msg.includes('countrycode') || msg.includes('country')) {
                            return 'Please enter a valid postal/zip code. The postal code format may not be recognized.';
                        }
                        return 'Please check your form information and try again.';
                    }
                },
                {
                    pattern: /call to.*method.*on null/i,
                    message: 'Please check your form information and try again.'
                },
                {
                    pattern: /undefined.*index|undefined.*offset/i,
                    message: 'Some required information is missing. Please check your form and try again.'
                },
                {
                    pattern: /class.*not found|method.*does not exist/i,
                    message: 'We\'re experiencing temporary technical difficulties. Please try again in a moment.'
                },

                // Database/Connection errors
                {
                    pattern: /sql.*error|database.*error|connection.*refused|connection.*timeout/i,
                    message: 'We\'re experiencing temporary technical difficulties. Please try again in a moment.'
                },
                {
                    pattern: /deadlock|lock wait timeout|too many connections/i,
                    message: 'Our servers are busy right now. Please wait a moment and try again.'
                },

                // Validation errors
                {
                    pattern: /validation.*failed|the given data was invalid/i,
                    message: 'Please check your information and correct any errors.'
                },
                {
                    pattern: /required.*field|field.*required/i,
                    message: 'Please fill in all required fields.'
                },

                // Network/Timeout errors (more specific patterns)
                {
                    pattern: /connection.*timeout|request.*timeout|network.*timeout|fetch.*timeout/i,
                    message: 'Connection timeout. Please check your internet connection and try again.'
                },
                {
                    pattern: /timeoutId is not defined/i,
                    message: 'A technical error occurred. Please try again.'
                },
                {
                    pattern: /service.*unavailable|server.*unavailable/i,
                    message: 'Our service is temporarily unavailable. Please try again in a few minutes.'
                },

                // Payment processing errors
                {
                    pattern: /payment.*failed|card.*declined|transaction.*failed|payment.*error/i,
                    message: 'Payment processing failed. Please check your payment information and try again.'
                },
                {
                    pattern: /insufficient.*funds|card.*expired|invalid.*card/i,
                    message: 'There\'s an issue with your payment method. Please check your card details or try a different card.'
                },
                {
                    pattern: /gateway.*error|processor.*error|tokenization.*failed/i,
                    message: 'Payment processing is temporarily unavailable. Please try again in a moment.'
                },

                // Address/Location errors
                {
                    pattern: /postal.*code|zip.*code|postcode/i,
                    message: 'Please enter a valid postal/zip code for your address.'
                },
                {
                    pattern: /country.*invalid|country.*required|invalid.*country/i,
                    message: 'Please select a valid country from the dropdown.'
                },
                {
                    pattern: /address.*invalid|invalid.*address|address.*required/i,
                    message: 'Please enter a valid address.'
                },
                {
                    pattern: /state.*invalid|province.*invalid|region.*invalid/i,
                    message: 'Please select a valid state/province.'
                },

                // User information errors
                {
                    pattern: /email.*invalid|email.*required|invalid.*email/i,
                    message: 'Please enter a valid email address.'
                },
                {
                    pattern: /phone.*invalid|phone.*required|invalid.*phone/i,
                    message: 'Please enter a valid phone number.'
                },
                {
                    pattern: /name.*required|name.*invalid/i,
                    message: 'Please enter your full name.'
                },

                // Rate limiting
                {
                    pattern: /too many.*requests|rate.*limit|throttle/i,
                    message: 'Too many requests. Please wait a moment and try again.'
                },

                // Security/Auth errors
                {
                    pattern: /unauthorized|access.*denied|forbidden|csrf|authentication required/i,
                    message: 'Your session has expired. Please refresh the page and try again.'
                },
                {
                    pattern: /method not supported|request method not supported/i,
                    message: 'There was a technical issue with your request. Please refresh the page and try again.'
                }
            ];

            // Check against all patterns
            for (const { pattern, handler, message: patternMessage } of errorPatterns) {
                if (pattern.test(technicalMessage)) {
                    return handler ? handler(technicalMessage) : patternMessage;
                }
            }

            // If a message is user-friendly (short, no technical jargon), show it
            if (technicalMessage.length < 150 &&
                !message.includes('exception') &&
                !message.includes('error in') &&
                !message.includes('line ') &&
                !message.includes('stack trace') &&
                !message.includes('app\\') &&
                !message.includes('vendor\\') &&
                !message.includes('called in') &&
                !message.includes('thrown in')) {
                return technicalMessage;
            }

            // Default fallback for any unhandled technical error
            return 'We encountered an issue processing your request. Please try again or contact support if this persists.';
        },

        validateCCFields() {
            // Skip validation for Adyen - it handles its own validation
            if (this.isAdyenGateway) {
                this.cardValidationErrors = [];
                return;
            }

            this.cardValidationErrors = []; // Clear previous errors

            // Clear previous card errors
            this.contactDetailErrors.card.number = false;
            this.contactDetailErrors.card.expirationDate = false;
            this.contactDetailErrors.card.cvv = false;

            // Loop through ccFieldStatus properties to see if any are false, if so, add that to the this.cardValidationErrors array
            for (const [key, value] of Object.entries(this.ccFieldStatus)) {
                if (!value) {
                    // Map field names to match what the HTML expects
                    this.cardValidationErrors.push(key);

                    // Set the corresponding contactDetailErrors.card error for UI display
                    switch (key) {
                        case 'number':
                            this.contactDetailErrors.card.number = { message: 'Enter a valid card number.' };
                            break;
                        case 'expirationDate':
                            this.contactDetailErrors.card.expirationDate = { message: 'Enter a valid expiration date (MM/YYYY).' };
                            break;
                        case 'cvv':
                            this.contactDetailErrors.card.cvv = { message: 'CVV should be 3-4 digits.' };
                            break;
                    }
                }
            }
        },
        getfieldErrors(field) {
            return this.contactErrors.filter(error => error.field === field);
        },

        // Map backend errors to specific form fields for better user feedback
        mapBackendErrorsToFormFields(backendErrors) {
            // Clear any previous field-specific errors
            this.clearFieldErrors();

            Object.keys(backendErrors).forEach(errorKey => {
                const errorValue = backendErrors[errorKey];
                const errorMessages = Array.isArray(errorValue) ? errorValue : [errorValue];

                errorMessages.forEach(errorMessage => {
                    // Map backend error keys to actual form fields
                    this.mapBackendErrorKeyToFormField(errorKey, errorMessage);
                });
            });
        },

        // Map a specific backend error key to the correct form field
        mapBackendErrorKeyToFormField(errorKey, errorMessage) {
            // Handle nested error keys like 'shipping_address.postal_code'
            if (errorKey.includes('.')) {
                const [section, field] = errorKey.split('.');

                // Special handling for shipping_address errors when shipping the same as billing
                if (section === 'shipping_address' && this.form.shipping_same === true) {
                    // When shipping same as billing, map shipping errors to billing fields
                    this.setFieldError(`billing_address_${field}`, errorMessage);
                } else {
                    // Normal mapping
                    this.setFieldError(`${section}_${field}`, errorMessage);
                }
            } else {
                // Direct field errors
                // Special handling for payment/gateway errors
                if (errorKey === 'gateway' || errorKey === 'payment' || errorKey === 'card' || errorKey === 'token') {
                    // These are payment-related errors, add to paymentErrors
                    if (!this.paymentErrors) {
                        this.paymentErrors = [];
                    }
                    this.paymentErrors.push(errorMessage);
                } else {
                    this.setFieldError(errorKey, errorMessage);
                }
            }
        },

        // Set a specific field error that the UI can display
        setFieldError(fieldName, errorMessage) {
            // Map field names to the correct error structure
            switch (fieldName) {
                case 'shipping_address':
                case 'shipping_address_address':
                    this.contactDetailErrors.visual_shipping.address = { message: errorMessage };
                    break;
                case 'shipping_city':
                case 'shipping_address_city':
                    this.contactDetailErrors.visual_shipping.city = { message: errorMessage };
                    break;
                case 'shipping_state':
                case 'shipping_address_state':
                    this.contactDetailErrors.visual_shipping.state = { message: errorMessage };
                    break;
                case 'shipping_postal_code':
                case 'shipping_address_postal_code':
                    // When shipping the same as billing, show error on the billing field
                    if (this.form.shipping_same === true) {
                        this.contactDetailErrors.billing_address.postal_code = { message: errorMessage };
                    } else {
                        this.contactDetailErrors.visual_shipping.postal_code = { message: errorMessage };
                    }
                    break;
                case 'shipping_country':
                case 'shipping_address_country':
                    this.contactDetailErrors.visual_shipping.country = { message: errorMessage };
                    break;
                case 'billing_address':
                case 'billing_address_address':
                    this.contactDetailErrors.billing_address.address = { message: errorMessage };
                    break;
                case 'billing_city':
                case 'billing_address_city':
                    this.contactDetailErrors.billing_address.city = { message: errorMessage };
                    break;
                case 'billing_state':
                case 'billing_address_state':
                    this.contactDetailErrors.billing_address.state = { message: errorMessage };
                    break;
                case 'billing_postal_code':
                case 'billing_address_postal_code':
                    this.contactDetailErrors.billing_address.postal_code = { message: errorMessage };
                    break;
                case 'billing_country':
                case 'billing_address_country':
                    this.contactDetailErrors.billing_address.country = { message: errorMessage };
                    break;
                case 'user_name':
                case 'user.name':
                    this.contactDetailErrors.name = { message: errorMessage };
                    break;
                case 'user_email':
                case 'user.email':
                    this.contactDetailErrors.email = { message: errorMessage };
                    break;
                default:
                    // Unknown field name for error mapping
                    break;
            }
        },

        // Clear all field-specific errors
        clearFieldErrors() {
            // Clear billing address errors
            if (this.contactDetailErrors.billing_address) {
                Object.keys(this.contactDetailErrors.billing_address).forEach(field => {
                    this.contactDetailErrors.billing_address[field] = false;
                });
            }

            // Clear shipping address errors
            if (this.contactDetailErrors.visual_shipping) {
                Object.keys(this.contactDetailErrors.visual_shipping).forEach(field => {
                    this.contactDetailErrors.visual_shipping[field] = false;
                });
            }

            // Clear user errors
            if (this.contactDetailErrors.name) this.contactDetailErrors.name = false;
            if (this.contactDetailErrors.email) this.contactDetailErrors.email = false;
        },
        async primeFunnelOverride(force = false) {
            const funnelOverride = this.customCheckoutOfferData?.funnel_override;
            if (!funnelOverride) {
                return null;
            }

            if (!force && this.primedFunnelOverrideValue === funnelOverride && this.funnelOverridePrimePromise) {
                return this.funnelOverridePrimePromise;
            }

            this.primedFunnelOverrideValue = funnelOverride;
            this.funnelOverridePrimePromise = window.CheckoutAPI.get(this, `/api/cart?funnel=${encodeURIComponent(funnelOverride)}`, {
                context: { operation: 'prime_funnel_override' },
                headers: getHeaders()
            });

            return this.funnelOverridePrimePromise;
        },

        /**
         * Emit a non-PII snapshot of the current phone-field state for
         * DataDog RUM. Logged values are bounded primitives (booleans,
         * lengths) so we can correlate WHERE in the express checkout
         * flow `form.user.phone` goes from "present" to "empty", without
         * leaking the actual phone number into telemetry.
         *
         * Usage: this.phoneStateSnapshot('applepay-onsubmit')
         */
        phoneStateSnapshot: function (label) {
            try {
                const phone = (this.form && this.form.user && this.form.user.phone) || '';
                const digits = phone.replace(/\D/g, '');
                addDDAction(`phone-state-${label}`, {
                    is_empty: !phone || !phone.trim(),
                    length: phone.length,
                    digits_length: digits.length,
                    has_formatting: /[()\-\s]/.test(phone),
                    is_subscribed: !!(this.form && this.form.user && this.form.user.isSubscribed),
                    phone_interacted: !!this.phoneInteracted,
                    payment_processor: this.payment_processor,
                    scene: this.currentScene,
                    locked_phone_present: !!this._lockedUserPhone,
                    locked_phone_length: (this._lockedUserPhone || '').length,
                });
            } catch (e) {
                // Telemetry must never break the checkout flow.
                console.log('[phoneStateSnapshot] failed', e);
            }
        },

        /**
         * Honor the "always respect the user-typed phone" requirement:
         * if a phone was captured at Scene 1 validation time but has
         * since become empty/whitespace on `this.form.user.phone`,
         * restore it. Defense-in-depth against any path (current or
         * future) that might blank out the user's typed phone during
         * the express checkout flows (autofill drift, Vue reactivity
         * glitches, future code that overwrites form.user.phone with
         * wallet data, etc.).
         *
         * Static code analysis as of 2026-05-13 shows nothing in the
         * Apple Pay or PayPal flows mutates form.user.phone — yet QA
         * is hitting the null-phone Klaviyo crash on Apple Pay only.
         * This lock makes the user requirement structural, not
         * behavioral, so future regressions can't break it either.
         */
        enforcePhoneLock: function () {
            if (!this._lockedUserPhone) {
                return;
            }
            const current = ((this.form && this.form.user && this.form.user.phone) || '').trim();
            if (!current) {
                this.form.user.phone = this._lockedUserPhone;
                addDDAction('phone-lock-restored', {
                    payment_processor: this.payment_processor,
                    scene: this.currentScene,
                    restored_length: this._lockedUserPhone.length,
                });
            }
        },

        async syncAddresses() {
            return window.CheckoutRequestController.runExclusive('address-sync', () => this._performAddressSync());
        },

        async _performAddressSync() {
            this.clearAllErrors();
            this.addressSyncing = true;

            try {
                if (this.payment_processor === 'paypal' || this.payment_processor === 'apple_pay') {
                    // Wallet express flows populate shipping_address from the
                    // payment sheet; do not rebuild it from billing_address
                    // or visual_shipping.
                    this.form.shipping_address.name = this.form.shipping_address.name || this.form.user.name;
                    this.form.shipping_address.email = this.form.shipping_address.email || this.form.user.email;
                    if (!this.form.shipping_address.phone) {
                        this.form.shipping_address.phone = this.form.user.phone;
                    }
                } else if (this.form.shipping_same === true) {
                    this.form.shipping_address = { ...this.form.billing_address };
                    this.form.shipping_address.name = this.form.user.name;
                    this.form.shipping_address.email = this.form.user.email;
                    this.form.shipping_address.phone = this.form.user.phone;
                } else {
                    this.form.shipping_address = { ...this.form.visual_shipping };
                    this.form.shipping_address.name = this.form.visual_shipping.name;
                    this.form.shipping_address.email = this.form.visual_shipping.email;
                    this.form.shipping_address.phone = this.form.visual_shipping.phone;
                }

                const shippingAddressForAPI = {
                    ...this.form.shipping_address,
                    country: getAlpha3CountryCode(this.form.shipping_address.country)
                };

                const result = await window.CheckoutAPI.patch(this, '/api/cart', { shipping_address: shippingAddressForAPI }, {
                    context: { operation: 'address_sync_update' },
                    headers: getHeaders(),
                    timeout: 15000
                });

                if (result.success) {
                    const processedResult = this.convertCurrency(result.data.data, this.countriesList, this.form.billing_address.country);
                    this.checkoutOffer = processedResult;
                    this.cartState = processedResult;
                    this.addressSynced = true;

                    // Auto-select least expensive shipping option on first load only.
                    // Skip for wallet express flows — shipping is chosen inside the payment sheet.
                    if (this.payment_processor !== 'paypal'
                        && this.payment_processor !== 'apple_pay'
                        && processedResult.available_shipping_categories?.length > 0) {
                        const sorted = [...processedResult.available_shipping_categories].sort(
                            (a, b) => a.price_base.amount.amount - b.price_base.amount.amount
                        );
                        if (!this._shippingAutoSelected) {
                            this._shippingAutoSelected = true;
                            if (sorted[0]) {
                                setTimeout(() => {
                                    this.selectShippingOption(sorted[0]);
                                }, 200);
                            }
                        } else if (this.selectedShippingOption && !processedResult.available_shipping_categories.find(s => s.id === this.selectedShippingOption)) {
                            if (sorted[0]) {
                                setTimeout(() => {
                                    this.selectShippingOption(sorted[0]);
                                }, 200);
                            }
                        }
                    }

                    if (this.bumperActive) {
                        this.reloadComponents();
                    }

                    return true;
                } else if (!result.handled) {
                    throw new Error('Failed to update shipping address');
                }

                return false;
            } catch (error) {
                window.CheckoutErrorMonitor.log(error, {
                    operation: 'address_sync_failed_root',
                    currentScene: this.currentScene,
                    paymentProcessor: this.payment_processor,
                    errorStatus: error.status
                });

                console.error('Error syncing addresses:', error);
                this.clearAllErrors();

                if (error.status === 422 && error.errors) {
                    const specificErrors = [];
                    Object.keys(error.errors).forEach(key => {
                        if (Array.isArray(error.errors[key])) {
                            specificErrors.push(...error.errors[key]);
                        } else {
                            specificErrors.push(error.errors[key]);
                        }
                    });
                    this.checkoutErrors = specificErrors.length > 0 ? specificErrors : ['Please check your address information and try again.'];
                } else if (error.message && error.message.includes('timeout')) {
                    this.checkoutErrors = ['Connection timeout. Please check your internet connection and try again.'];
                } else if (error.status >= 500) {
                    this.checkoutErrors = ['Server error. Please try again in a moment or contact support if this persists.'];
                } else {
                    this.checkoutErrors = [error.message || 'Failed to update shipping address. Please try again.'];
                }

                return false;
            } finally {
                this.addressSyncing = false;
            }
        },

        /**
         * Relocate the Adyen Card mount into a visible overlay so 3DS2 fingerprint /
         * ACS challenge UI is not trapped inside hidden scene 1 on step 2.
         */
        ensureAdyen3dsVisible() {
            this.full_processing = false;
            this.adyen3dsActive = true;

            const container = document.querySelector('#adyen-checkout-container');
            const holder = document.querySelector('#adyen-3ds-challenge-holder');
            if (!container || !holder) {
                console.warn('[Adyen|3DS] Challenge holder or card container not found');
                return;
            }

            if (!this.adyen3dsOriginalParent) {
                this.adyen3dsOriginalParent = container.parentElement;
            }

            if (!holder.contains(container)) {
                holder.appendChild(container);
            }

            addDDAction('checkout-adyen-3ds-overlay-shown', {
                payment_processor: this.payment_processor,
                scene: this.currentScene,
            });

            if (window.Adyen3dsErrorGuard) {
                window.Adyen3dsErrorGuard.install(this);
            }
        },

        clearAdyen3dsOverlay() {
            this.adyen3dsActive = false;

            if (window.Adyen3dsErrorGuard) {
                window.Adyen3dsErrorGuard.uninstall();
            }

            const container = document.querySelector('#adyen-checkout-container');
            if (
                container
                && this.adyen3dsOriginalParent
                && !this.adyen3dsOriginalParent.contains(container)
            ) {
                this.adyen3dsOriginalParent.appendChild(container);
            }

            addDDAction('checkout-adyen-3ds-overlay-cleared', {
                payment_processor: this.payment_processor,
                scene: this.currentScene,
            });
        },

        /**
         * Remove Adyen-injected 3DS2 DOM (fingerprint iframe / challenge modal) that can
         * remain mounted after internal shopper API failures.
         */
        teardownAdyen3dsUi() {
            document.querySelectorAll(
                '.adyen-checkout__threeds, .adyen-checkout__threeds2, .adyen-checkout__iframe--threeDS'
            ).forEach((node) => node.remove());
        },

        /**
         * Centralized recovery when 3DS2 fingerprint/challenge fails (SDK onError miss,
         * submitThreeDS2Fingerprint 500, network error, or safety timeout).
         */
        async handleAdyen3dsFailure(detail = {}) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (this._adyen3dsFailureInProgress) {
                return;
            }
            this._adyen3dsFailureInProgress = true;

            try {
                this.processing = false;
                this.full_processing = false;
                this.checkingOut = false;

                this.teardownAdyen3dsUi();
                this.clearAdyen3dsOverlay();

                if (this.adyenCardComponent && typeof this.adyenCardComponent.unmount === 'function') {
                    try {
                        this.adyenCardComponent.unmount();
                    } catch (error) {
                        console.warn('[Adyen|3DS] unmount during failure recovery:', error);
                    }
                }

                await this.restoreAdyenCardComponent();

                const message = detail.message
                    || window.Adyen3dsErrorGuard?.userMessage
                    || 'Payment authentication failed. Please try a different card or payment method.';
                this.paymentErrors = [message];

                addDDAction('checkout-adyen-3ds-failure', {
                    payment_processor: this.payment_processor,
                    phase: detail.phase || 'unknown',
                    error_code: detail.errorCode || '',
                    http_status: detail.httpStatus || '',
                });
                addDDAction(`${this.payment_processor}-checkout-fail`, {
                    phase: 'adyen_three_ds_guard',
                    detail: message,
                    error_code: detail.errorCode || '',
                });
            } finally {
                this._adyen3dsFailureInProgress = false;
            }
        },

        async submitAdyenThreeDsDetails(details) {
            // `full_processing` drives `.full_loading` (viewport overlay, z-index 9999). If it stays active
            // during Adyen handleAction / ACS challenge, the challenge iframe renders underneath and clicks
            // never reach it — shopper sees a stall until ACS times out (~minutes) then transStatus "U".
            this.full_processing = false;

            try {
                const headers = getHeaders();
                const endPointPayMethod = `?method=${this.payment_processor}`;

                addDDAction('checkout-adyen-3ds-submit', { payment_processor: this.payment_processor });

                const result = await window.CheckoutAPI.post(
                    this,
                    `/api/cart/pay/adyen-details${endPointPayMethod}`,
                    { details: details || {} },
                    {
                        context: { operation: 'adyen_three_ds_payment_details' },
                        headers: headers,
                    }
                );

                if (!result.success) {
                    if (result.handled) {
                        this.processing = false;
                        this.full_processing = false;
                        this.checkingOut = false;
                        this.clearAdyen3dsOverlay();
                        await this.restoreAdyenCardComponent();
                        addDDAction('checkout-adyen-3ds-api-handled-error', {});
                    }
                    return;
                }

                const json = result.data || {};
                const meta = json.meta;

                if (meta?.payment?.requires_action === true && meta.payment?.action) {
                    addDDAction('checkout-adyen-3ds-followup', {
                        payment_processor: this.payment_processor,
                        result_code: meta.payment.result_code || '',
                    });

                    if (!this.adyenCardComponent || typeof this.adyenCardComponent.handleAction !== 'function') {
                        throw new Error('Card form is no longer available. Please refresh the page and try again.');
                    }

                    this.ensureAdyen3dsVisible();
                    await this.adyenCardComponent.handleAction(meta.payment.action);
                    return;
                }

                if (meta?.next_url) {
                    this.clearAdyen3dsOverlay();
                    addDDAction(`checkout-success`, { offers: this.cartOffers, phase: 'after_3ds' });
                    addDDAction(`${this.payment_processor}-checkout-success`, { offers: this.cartOffers, phase: 'after_3ds' });
                    window.location = meta.next_url;
                    return;
                }

                this.processing = false;
                this.full_processing = false;
                this.checkingOut = false;
                this.clearAdyen3dsOverlay();
                await this.restoreAdyenCardComponent();
                this.checkoutErrors = ['There was an issue completing your payment.'];
                addDDAction('checkout-fail', { phase: 'adyen_three_ds_unknown', meta: meta || {} });
                addDDAction(`${this.payment_processor}-checkout-fail`, { phase: 'adyen_three_ds_unknown' });
            } catch (error) {
                this.processing = false;
                this.full_processing = false;
                this.checkingOut = false;
                this.clearAdyen3dsOverlay();
                await this.restoreAdyenCardComponent();
                console.error('[Adyen|3DS] submitAdyenThreeDsDetails:', error);
                this.paymentErrors = [error.message || 'We could not verify your card. Please try again.'];
                addDDAction(`${this.payment_processor}-checkout-fail`, {
                    phase: 'adyen_three_ds_details',
                    detail: error.message,
                });
            }
        },

        processOrder: async function (isPaypal = false, options = {}) {
            // Sessions-based PayPal: the SDK already authorized in the lightbox.
            // Only adyenPayPalCompleteSessionOrder may call processOrder; block any
            // stray submitOrder / getToken path that would hit /api/cart/pay again.
            if (this.isAdyenGateway && this.payment_processor === 'paypal') {
                if (this._adyenPayPalOrderFinalized) {
                    return;
                }
                if (this._adyenPayPalCheckoutInFlight && !options.fromPaypalSessionComplete) {
                    console.warn('[Adyen|PayPal] Blocked duplicate processOrder while session checkout is in flight');
                    return;
                }
                if (!options.fromPaypalSessionComplete && !this._adyenPayPalSessionAuthorized) {
                    console.warn('[Adyen|PayPal] Blocked processOrder — complete payment via the PayPal button first');
                    return;
                }
            }

            if (!options.preserveErrors) {
                this.clearAllErrors();
            }
            this.processing = true;
            this.full_processing = true;
            this.checkingOut = true;

            addDDAction(`checkout-init`, {});
            addDDAction(`${this.payment_processor}-checkout-init`, {});

            try {
                const headers = getHeaders();
                const primeResult = await this.primeFunnelOverride();
                if (primeResult && !primeResult.success) {
                    if (!primeResult.handled) {
                        throw new Error(primeResult.message || 'Failed to prime funnel override');
                    }
                    return;
                }

                if (!this.addressSynced) {
                    const addressSyncResult = await this.syncAddresses();
                    if (addressSyncResult === false) {
                        this.processing = false;
                        this.full_processing = false;
                        this.checkingOut = false;
                        if (options.fromPaypalSessionComplete) {
                            releaseAdyenPayPalCheckoutLock(this);
                        }
                        if (options.suppressPageErrors) {
                            throw new Error('We could not save your shipping address. Please try again or use a different payment method.');
                        }
                        return;
                    }
                }
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);
                const nonceOverride = urlParams.get('test_nonce_override');
                const endPointPayMethod = `?method=${this.payment_processor}`;

                if (this.isAdyenGateway && this.payment_processor === 'credit_card') {
                    const cardholderName = this.cardState?.data?.holderName ?? '';
                    this.setPreAuthIfComplete(cardholderName);
                    this.warnIfPreAuthIncomplete();
                }

                const result = await window.CheckoutRequestController.runCheckoutStep('final-payment', () =>
                    window.CheckoutAPI.post(this, '/api/cart/pay' + endPointPayMethod, { ...remapForm(this.form), nonceOverride }, {
                        context: { operation: 'final_payment_processing' },
                        headers: headers,
                        suppressPageErrors: options.suppressPageErrors === true
                    })
                );

                let response, json, meta, data, errors;
                if (result.success) {
                    response = result.response;
                    json = result.data;
                    meta = json.meta;
                    data = json.data;
                    errors = json.errors;
                } else if (!result.handled) {
                    this.processing = false;
                    this.full_processing = false;
                    this.checkingOut = false;
                    if (options.fromPaypalSessionComplete) {
                        releaseAdyenPayPalCheckoutLock(this);
                    }
                    // Critical payment error - ensure it's handled
                    throw new Error(result.message || 'Payment processing failed');
                } else {
                    this.processing = false;
                    this.full_processing = false;
                    this.checkingOut = false;
                    if (options.fromPaypalSessionComplete) {
                        releaseAdyenPayPalCheckoutLock(this);
                    }
                    if (options.suppressPageErrors) {
                        throw buildWalletPaymentFailureError(result.data, result.response, result.errors, result.message || 'Payment processing failed');
                    }
                    // Error was handled by our system, but we need to stop processing
                    return;
                }

                if (
                    response.ok
                    && this.isAdyenGateway
                    && this.payment_processor === 'credit_card'
                    && meta?.payment?.requires_action === true
                    && meta.payment?.action
                ) {
                    addDDAction('checkout-adyen-3ds-initial-action', {
                        payment_processor: this.payment_processor,
                        result_code: meta.payment.result_code || '',
                    });

                    try {
                        if (!this.adyenCardComponent || typeof this.adyenCardComponent.handleAction !== 'function') {
                            throw new Error('Card form is no longer available. Please refresh the page and try again.');
                        }
                        // Hide fullscreen loader and show 3DS overlay before fingerprint / challenge.
                        this.ensureAdyen3dsVisible();
                        await this.adyenCardComponent.handleAction(meta.payment.action);
                    } catch (threeDsErr) {
                        console.error('[Adyen|3DS] Initial handleAction failed:', threeDsErr);
                        this.processing = false;
                        this.full_processing = false;
                        this.checkingOut = false;
                        this.clearAdyen3dsOverlay();
                        await this.restoreAdyenCardComponent();
                        this.paymentErrors = [threeDsErr.message || 'We could not start card verification. Please try again.'];
                        addDDAction(`${this.payment_processor}-checkout-fail`, {
                            detail: threeDsErr.message,
                            phase: '3ds_initial_handle_action',
                        });
                    }
                    return;
                }

                if (response.ok && meta?.next_url) {
                    addDDAction(`checkout-success`, { offers: this.cartOffers });
                    addDDAction(`${this.payment_processor}-checkout-success`, { offers: this.cartOffers });
                    this.cartOffers.forEach(order_offer => {
                        addDDAction(`purchase-sku-${order_offer?.offer?.sku}`, { offer: order_offer.offer });
                    });

                    if (this.isAdyenGateway && this.payment_processor === 'paypal') {
                        this._adyenPayPalOrderFinalized = true;
                    }

                    window.location = meta.next_url;
                } else {
                    this.processing = false;
                    this.full_processing = false;
                    this.checkingOut = false;
                    if (options.fromPaypalSessionComplete) {
                        releaseAdyenPayPalCheckoutLock(this);
                    }

                    if (json.errors && typeof json.errors === 'object') {
                        this.clearAllErrors();
                        if (json.errors.gateway) {
                            this.paymentErrors = Array.isArray(json.errors.gateway)
                                ? json.errors.gateway
                                : [json.errors.gateway];
                        } else if (json.errors.address) {
                            this.checkoutErrors = Array.isArray(json.errors.address)
                                ? json.errors.address
                                : [json.errors.address];
                        } else if (json.errors.shipping_address) {
                            this.checkoutErrors = Array.isArray(json.errors.shipping_address)
                                ? json.errors.shipping_address
                                : [json.errors.shipping_address];
                        } else if (json.errors.payment) {
                            // Handle payment-specific errors
                            this.paymentErrors = Array.isArray(json.errors.payment)
                                ? json.errors.payment
                                : [json.errors.payment];
                        } else if (json.errors.user) {
                            // Handle user validation errors
                            this.userErrors = Array.isArray(json.errors.user)
                                ? json.errors.user
                                : [json.errors.user];
                        } else {
                            // Fallback: Generic handling for any other errors.*
                            const errorMessages = [];
                            Object.keys(json.errors).forEach(key => {
                                if (Array.isArray(json.errors[key])) {
                                    errorMessages.push(...json.errors[key]);
                                } else {
                                    errorMessages.push(json.errors[key]);
                                }
                            });
                            this.checkoutErrors = errorMessages;
                        }
                    } else if (typeof errors?.message !== 'undefined') {
                        // Fallback to the old format
                        this.checkoutErrors = [errors?.message];
                    } else if (typeof data?.errors !== 'undefined') {
                        // Another fallback format
                        this.checkoutErrors = data.errors;
                    } else {
                        // Generic fallback
                        this.checkoutErrors = ['There was an issue submitting your payment'];
                    }

                    addDDAction(`checkout-fail`, {
                        detail: this.checkoutErrors
                    });
                    addDDAction(`${this.payment_processor}-checkout-fail`, {
                        detail: this.checkoutErrors
                    });

                    if (options.suppressPageErrors) {
                        throw buildWalletPaymentFailureError(json, response, errors);
                    }
                }
            } catch (error) {
                this.processing = false;
                this.checkingOut = false;
                this.full_processing = false;
                if (options.fromPaypalSessionComplete) {
                    releaseAdyenPayPalCheckoutLock(this);
                }
                addDDAction(`checkout-fail`, {
                    detail: error
                });
                addDDAction(`${this.payment_processor}-checkout-fail`, {
                    detail: this.checkoutErrors
                });
                if (error.response) {
                    try {
                        const errorResponse = await error.response.json();
                        if (errorResponse.errors && typeof errorResponse.errors === 'object') {
                            this.clearAllErrors();

                            // Handle specific error types with a priority order
                            if (errorResponse.errors.gateway) {
                                this.paymentErrors = Array.isArray(errorResponse.errors.gateway)
                                    ? errorResponse.errors.gateway
                                    : [errorResponse.errors.gateway];
                            } else if (errorResponse.errors.address) {
                                this.checkoutErrors = Array.isArray(errorResponse.errors.address)
                                    ? errorResponse.errors.address
                                    : [errorResponse.errors.address];
                            } else if (errorResponse.errors.shipping_address) {
                                this.checkoutErrors = Array.isArray(errorResponse.errors.shipping_address)
                                    ? errorResponse.errors.shipping_address
                                    : [errorResponse.errors.shipping_address];
                            } else if (errorResponse.errors.payment) {
                                // Handle payment-specific errors
                                this.paymentErrors = Array.isArray(errorResponse.errors.payment)
                                    ? errorResponse.errors.payment
                                    : [errorResponse.errors.payment];
                            } else if (errorResponse.errors.user) {
                                // Handle user validation errors
                                this.userErrors = Array.isArray(errorResponse.errors.user)
                                    ? errorResponse.errors.user
                                    : [errorResponse.errors.user];
                            } else {
                                // Fallback: Generic handling for any other errors.*
                                const errorMessages = [];
                                Object.keys(errorResponse.errors).forEach(key => {
                                    if (Array.isArray(errorResponse.errors[key])) {
                                        errorMessages.push(...errorResponse.errors[key]);
                                    } else {
                                        errorMessages.push(errorResponse.errors[key]);
                                    }
                                });
                                this.checkoutErrors = errorMessages;
                            }
                        } else if (errorResponse.data?.errors) {
                            // Fallback to the old format
                            this.checkoutErrors = errorResponse.data.errors;
                        } else {
                            // Generic fallback
                            this.checkoutErrors = ['There was an issue submitting your payment'];
                        }
                    } catch (parseError) {
                        // If we can't parse the error response, use a generic message
                        console.error('Failed to parse error response:', parseError);
                        this.checkoutErrors = [['There was an issue submitting your payment']];
                    }
                }
                console.error('Failed to pay cart.', error);
                if (options.suppressPageErrors) {
                    throw error;
                }
            }
        },
        paypalCompleteOrder: function (payload) {
            // Honor the user-typed phone (FET-2669 / Klaviyo SMS null-phone
            // crash): if anything in the wallet flow blanked out
            // form.user.phone, restore from the Scene 1 snapshot before
            // we hit /api/cart/pay. Emit a state snapshot for DataDog
            // either way so we can see what we actually shipped.
            this.enforcePhoneLock();
            this.phoneStateSnapshot('paypal-complete-entry');

            addDDAction(`paypal-payment-init`, { payload: payload });
            try {
                this.form.payment.token = payload.nonce;
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);
                const isQa = urlParams.get('qa_test');
                this.form.payment.gateway = (isQa && isQa === 'VSQAT')
                    ? 'Braintree_Testing'
                    : 'Braintree';

                this.form.payment.paypal = {
                    email: payload.details.email
                };

                // Use billingAddress if available, otherwise fall back to shippingAddress
                const billingData = payload.details.billingAddress || payload.details.shippingAddress;
                const recipientName = payload.details.shippingAddress.recipientName.split(' ');

                this.form.billing_address = {
                    name: (payload.details.firstName && payload.details.lastName)
                        ? `${payload.details.firstName} ${payload.details.lastName}`
                        : payload.details.shippingAddress.recipientName,
                    first_name: payload.details.firstName || recipientName[0],
                    last_name: payload.details.lastName || (recipientName[1] || ''),
                    company_name: '',
                    country: billingData.countryCode,
                    address: billingData.line1,
                    address2: billingData.line2 || '',
                    city: billingData.city,
                    state: billingData.state,
                    postal_code: billingData.postalCode,
                    phone: '',
                };

                this.form.shipping_address = {
                    name: recipientName.length > 1
                        ? `${recipientName[0]} ${recipientName[1]}`
                        : recipientName[0],
                    first_name: recipientName[0],
                    last_name: recipientName.length > 1
                        ? recipientName[1]
                        : '',
                    company_name: '',
                    country: payload.details.shippingAddress.countryCode,
                    address: payload.details.shippingAddress.line1,
                    address2: payload.details.shippingAddress.line2 || '',
                    city: payload.details.shippingAddress.city,
                    state: payload.details.shippingAddress.state,
                    postal_code: payload.details.shippingAddress.postalCode,
                    phone: '',
                };

                if (!this.form.user.name) {
                    this.form.user.name = this.form.shipping_address.name;
                }
                if (!this.form.user.email || this.form.user.email !== payload.details.email) {
                    this.form.user.email = payload.details.email;
                }
                this.form.shipping_same = (JSON.stringify(this.form.billing_address) === JSON.stringify(this.form.shipping_address));

                this.processOrder(true);
            } catch (e) {
                addDDAction(`paypal-payment-fail`, {
                    detail: e,
                    payload: payload,
                    form: this.form
                });
                console.error("PayPal Payment Failure", e);
            }
        },
        getAmountBase: function () {
            return this.cartState?.amount_base ? this.cartState?.amount_base : this.cartState?.amount;
        },
        createPaypalInstance: async function (customCart = false) {
            if ((this.gateway !== 'Braintree' && this.gateway !== 'Braintree_Testing') || this.payment_processor !== 'paypal') {
                return;
            }

            // Wait for the DOM to update so that .paypal-button-holder / .braintree-paypal-container are rendered
            await new Promise(resolve => setTimeout(resolve, 250));

            const thisCart = this.cartState;
            this.initializingPaypal = true;

            // Guard against client initialization failures so PayPal setup
            // does not throw noisy runtime errors for users.
            if (
                !this.clientInstanceField
                || typeof this.clientInstanceField.request !== 'function'
                || typeof this.clientInstanceField.teardown !== 'function'
            ) {
                this.initializingPaypal = false;
                return null;
            }

            // Find the BrainTree PayPal container rendered by the template
            let container = document.querySelector('.braintree-paypal-container');
            if (!container) {
                // Fallback: try to find .paypal-button-holder and create a container inside it
                const holder = document.querySelector('.paypal-button-holder');
                if (holder) {
                    container = document.createElement('div');
                    container.classList.add('braintree-paypal-container');
                    holder.appendChild(container);
                } else {
                    console.error('[BrainTree|PayPal] PayPal button holder not found in DOM');
                    this.initializingPaypal = false;
                    return;
                }
            }

            // Clear any previous PayPal button content
            container.innerHTML = '';

            // Create a render target for PayPal Buttons
            const paypalButtonDiv = document.createElement('div');
            paypalButtonDiv.classList.add('paypal-button');
            container.appendChild(paypalButtonDiv);

            const availableShippingCategories = (thisCart.available_shipping_categories || []).map(category => ({
                id: category.id,
                type: 'SHIPPING',
                label: category.name,
                selected: false,
                amount: {
                    value: category.price_base.amount.decimal,
                    currency: 'USD'
                }
            }));
            if (availableShippingCategories.length > 0) {
                const lowestPriceCategory = availableShippingCategories.reduce((prev, curr) =>
                    parseFloat(curr.amount.value) < parseFloat(prev.amount.value) ? curr : prev
                );
                lowestPriceCategory.selected = true;
            }
            const thisAmount = thisCart.amount_base ? thisCart.amount_base.amount : thisCart.amount.amount;
            const thisCurrency = thisCart.amount_base ? thisCart.amount_base.currency : thisCart.amount.currency;
            this.paypalOrderOptions = {
                requestBillingAgreement: true,
                flow: 'checkout',
                amount: (thisAmount / 100).toFixed(2),
                currency: thisCurrency,
                enableShippingAddress: true,
                shippingAddressEditable: true,
                shippingAddressOverride: true,
                shippingOptions: availableShippingCategories
            }

            try {
                if (!this.clientInstanceField) {
                    console.error('[BrainTree|PayPal] Client instance not available. BrainTree may not be initialized.');
                    this.initializingPaypal = false;
                    return;
                }

                const paypalCheckoutInstance = await braintreeWeb.paypalCheckout.create({
                    client: this.clientInstanceField,
                });

                await paypalCheckoutInstance.loadPayPalSDK({
                    vault: true,
                    currency: thisCurrency
                });

                // Re-check the container is still in the DOM (user may have switched away)
                const renderTarget = document.querySelector('.braintree-paypal-container .paypal-button');
                if (!renderTarget) {
                    console.warn('[BrainTree|PayPal] Render target no longer in DOM, aborting');
                    this.initializingPaypal = false;
                    return;
                }

                await window.paypal
                    .Buttons({
                        fundingSource: window.paypal.FUNDING.PAYPAL,
                        style: {
                            label: 'pay',
                        },
                        createBillingAgreement: () =>
                            paypalCheckoutInstance.createPayment(
                                this.paypalOrderOptions
                            ),
                        onClick: (data, actions) => {
                            // Validate contact fields before opening PayPal modal
                            try {
                                this.validateContact();
                            } catch (e) {
                                console.error('Error in validateContact:', e);
                            }

                            if (this.contactErrors.length > 0) {
                                requestAnimationFrame(() => {
                                    const firstInvalidElement = document.querySelector('[aria-invalid="true"]');
                                    if (firstInvalidElement) {
                                        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                });
                                return actions.reject();
                            }

                            return actions.resolve();
                        },
                        onCancel: () => {
                            this.processing = false;
                        },
                        onShippingChange: (shippingData) => {
                            window.CheckoutAPI.get(defaultCheckoutAppNew, `/api/location-check/lookup-postal-code?postal_code=${shippingData.shipping_address.postal_code}&country=${shippingData.shipping_address.country}`, {
                                context: { operation: 'postal_code_lookup' },
                                headers: getHeaders()
                            })
                                .then(result => {
                                    if (result.success) {
                                        const postalCodeData = result.data;
                                        // Convert country code from backend (alpha-3) to form format (alpha-2)
                                        defaultCheckoutAppNew.form.shipping_address.country = getAlpha2CountryCode(postalCodeData.data.country);
                                        defaultCheckoutAppNew.form.shipping_address.state = shippingData.shipping_address.state;
                                        defaultCheckoutAppNew.form.shipping_address.city = shippingData.shipping_address.city;
                                        defaultCheckoutAppNew.form.shipping_address.postal_code = shippingData.shipping_address.postal_code;
                                        window.CheckoutAPI.patch(defaultCheckoutAppNew, '/api/cart', { shipping_address: defaultCheckoutAppNew.form.shipping_address }, {
                                            context: { operation: 'paypal_shipping_change' },
                                            headers: getHeaders()
                                        })
                                            .then(result => {
                                                if (result.success) {
                                                    const availableShippingCategories = (result.data.data.available_shipping_categories || []).map(category => ({
                                                        id: category.id,
                                                        type: 'SHIPPING',
                                                        label: category.name,
                                                        selected: false,
                                                        amount: {
                                                            value: category.price_base.amount.decimal,
                                                            currency: 'USD'
                                                        }
                                                    }));
                                                    if (availableShippingCategories.length > 0) {
                                                        const lowestPriceCategory = availableShippingCategories.reduce((prev, curr) =>
                                                            parseFloat(curr.amount.value) < parseFloat(prev.amount.value) ? curr : prev
                                                        );
                                                        lowestPriceCategory.selected = true;
                                                    }
                                                    return paypalCheckoutInstance.updatePayment({
                                                        paymentId: shippingData.paymentId,
                                                        amount: (result.data.data.amount_base.amount / 100).toFixed(2),
                                                        currency: 'USD',
                                                        shippingOptions: availableShippingCategories
                                                    });
                                                }
                                            })
                                            .catch(error => {
                                                console.error('[BrainTree|PayPal] Error updating shipping:', error);
                                            });
                                    }
                                })
                                .catch(error => {
                                    console.error('[BrainTree|PayPal] Error looking up postal code:', error);
                                });
                        },
                        onApprove: (data) =>
                            paypalCheckoutInstance
                                .tokenizePayment(data)
                                .then((payload) => {
                                    this.paypalCompleteOrder(payload, this.form);
                                }),
                    })
                    .render(renderTarget);

                this.initializingPaypal = false;
                console.log('[BrainTree|PayPal] PayPal button rendered successfully');
            } catch (error) {
                console.error('[BrainTree|PayPal] Error creating PayPal instance:', error);
                this.initializingPaypal = false;
                this.checkoutErrors = [error.message || 'Failed to initialize PayPal. Please try again.'];
            }
        },

        destroyAdyenCardComponent() {
            this.clearAdyen3dsOverlay();
            this.adyen3dsOriginalParent = null;

            if (this.adyenCardComponent) {
                try {
                    this.adyenCardComponent.unmount();
                } catch (e) {
                    console.error('[Adyen] Error unmounting card component:', e);
                }
                this.adyenCardComponent = null;
            }

            const container = document.querySelector('#adyen-checkout-container');
            if (container) {
                container.innerHTML = '';
            }
        },

        resetAdyenCardFields() {
            this.cardState = null;
            this.ccReady = false;
            Object.keys(this.preAuthFields).forEach((key) => {
                this.preAuthFields[key] = null;
            });
            if (this.form?.payment) {
                delete this.form.payment.preAuth;
            }
        },

        async restoreAdyenCardComponent() {
            if (this.restoringCardComponent || !this.isAdyenGateway || this.payment_processor !== 'credit_card') {
                return;
            }

            this.restoringCardComponent = true;

            try {
                this.destroyAdyenCardComponent();
                this.resetAdyenCardFields();
                await this.createAdyenCardComponent();
                addDDAction('checkout-adyen-card-restored', {
                    payment_processor: this.payment_processor,
                    scene: this.currentScene,
                });
            } catch (error) {
                console.error('[Adyen|3DS] Failed to restore card component:', error);
                addDDAction('checkout-adyen-card-restore-failed', {
                    detail: error.message,
                    scene: this.currentScene,
                });
            } finally {
                this.restoringCardComponent = false;
            }
        },
        // Destroy BrainTree PayPal component
        destroyBraintreePayPalComponent() {
            const container = document.querySelector('.braintree-paypal-container');
            if (container) {
                container.innerHTML = '';
            }
            this.initializingPaypal = false;
        },
        // Destroy Adyen components
        destroyAdyenComponents() {
            this.clearAdyen3dsOverlay();
            this.adyen3dsOriginalParent = null;
            this.destroyAdyenPayPalComponent();
            this.destroyAdyenApplePayComponent();
            if (this.adyenCardComponent) {
                try {
                    this.adyenCardComponent.unmount();
                } catch (e) {
                    console.error('[Adyen] Error unmounting card component:', e);
                }
                this.adyenCardComponent = null;
            }
        },
        destroyAdyenPayPalComponent() {
            if (this.adyenPayPalComponent) {
                try {
                    this.adyenPayPalComponent.unmount();
                } catch (e) {
                    console.error('[Adyen] Error unmounting PayPal component:', e);
                }
                this.adyenPayPalComponent = null;
            }
            // Clear all PayPal containers (desktop and mobile instances)
            document.querySelectorAll('.adyen-paypal-container').forEach(container => {
                container.innerHTML = '';
            });
        },
        destroyAdyenApplePayComponent() {
            if (this.adyenApplePayComponent) {
                try {
                    this.adyenApplePayComponent.unmount();
                } catch (e) {
                    console.error('[Adyen] Error unmounting Apple Pay component:', e);
                }
                this.adyenApplePayComponent = null;
            }
            // Clear all Apple Pay containers (desktop and mobile instances)
            document.querySelectorAll('.adyen-applepay-container').forEach(container => {
                container.innerHTML = '';
            });
        },
        // Create an Adyen Card component
        async createAdyenCardComponent() {
            const root = window.checkoutAppScope || this;
            if (!root.adyenCheckout) {
                console.error('[Adyen] Checkout instance not initialized');
                return;
            }
            const CardComponent = window.AdyenWeb?.Card || window.AdyenCard;
            if (!CardComponent) {
                console.error('[Adyen] Card component class is not available (check Adyen Web SDK load / include_adyen_js)');
                root.checkoutErrors = ['Payment form failed to load. Please refresh and try again.'];
                return;
            }
            try {
                if (root.adyenCardComponent) { return; }

                const cardConfiguration = {
                    enableStoreDetails: false,
                    hasHolderName: false,
                    holderNameRequired: false,
                    billingAddressRequired: false,
                    name: 'Credit Card',
                    showPayButton: false,
                    showStoreDetails: false,
                    storeDetailsInitialState: false,
                    exposeExpiryDate: true,
                    onConfigSuccess: () => {
                        syncPaymentFieldTabOrder(root.currentScene);
                    },
                    onBinLookup: (response) => {
                        if (response.detectedBrands && response.detectedBrands.length > 0) {
                            root.preAuthFields.cardBrand = response.detectedBrands[0];
                        }
                    },
                    onBinValue: (binValue) => {
                        const issuerBin = extractAdyenBinValue(binValue);
                        if (issuerBin) {
                            root.preAuthFields.issuerBin = issuerBin;
                            root.setPreAuthIfComplete(root.cardState?.data?.holderName ?? '');
                        }
                    },
                    onFieldValid: (response) => {
                        if (response.valid) {
                            const preAuthKeys = Object.keys(root.preAuthFields);
                            preAuthKeys.forEach(key => {
                                if (!Object.prototype.hasOwnProperty.call(response, key)) {
                                    return;
                                }

                                if (key === 'issuerBin') {
                                    const issuerBin = extractAdyenBinValue(response[key]);
                                    if (issuerBin) {
                                        root.preAuthFields.issuerBin = issuerBin;
                                    }
                                    return;
                                }

                                root.preAuthFields[key] = response[key];
                            });
                            root.setPreAuthIfComplete(root.cardState?.data?.holderName ?? '');
                        }
                    },
                    onChange: (state) => {
                        if (state.isValid) {
                            const cardholderName = state.data?.holderName ?? '';
                            root.setPreAuthIfComplete(cardholderName);
                        } else if (root.form?.payment) {
                            delete root.form.payment.preAuth;
                        }
                        root.cardState = state;
                        root.ccReady = !!state.isValid;
                    },
                    onAllValid: () => {
                        root.ccReady = true;
                    },
                    onError: (error) => {
                        console.error('[Adyen] Card error:', error);
                        if (root.adyen3dsActive) {
                            root.handleAdyen3dsFailure({
                                message: error?.message || window.Adyen3dsErrorGuard?.userMessage,
                                phase: 'card_on_error',
                                errorCode: error?.errorCode,
                            });
                        }
                    },
                    /**
                     * After /payments/details (server: POST /api/cart/pay/adyen-details), relay `details`
                     * from the Web Component fingerprint/challenge completion.
                     * @see https://docs.adyen.com/online-payments/build-your-integration/advanced-flow/?platform=Web&integration=Components
                     */
                    onAdditionalDetails: async (state /* , component */) => {
                        // Adyen sends either `state.data.details` or places the fingerprint/challenge blob on `state.data`.
                        // Do not shadow checkout `root` (checkoutAppScope) — that object owns submitAdyenThreeDsDetails.
                        const dataRoot = state && state.data !== undefined ? state.data : state;
                        const detailsFromData = dataRoot && typeof dataRoot === 'object' && 'details' in dataRoot
                            ? dataRoot.details
                            : dataRoot;
                        const details = detailsFromData !== undefined
                            ? detailsFromData
                            : (state && state.details !== undefined ? state.details : {});
                        await root.submitAdyenThreeDsDetails(details);
                    },
                };

                const container = document.querySelector('#adyen-checkout-container');
                if (container) {
                    root.adyenCardComponent = new CardComponent(root.adyenCheckout, cardConfiguration).mount('#adyen-checkout-container');
                    syncPaymentFieldTabOrder(root.currentScene);
                }
            } catch (error) {
                console.error('[Adyen] Error creating card component:', error);
                root.checkoutErrors = [error.message || 'Failed to initialize card form'];
            }
        },
        /**
         * Pre-condition the cart so the PayPal lightbox renders a shipping
         * picker on the very first open.
         *
         * Problem we're solving:
         *   - When the user first clicks "Pay with PayPal" we have no real
         *     shipping address yet (the BillingShipping form is hidden in
         *     the 2-step flow; PayPal collects the address inside its own
         *     lightbox via onShippingAddressChange).
         *   - Without ANY shipping_address on the cart, the backend's
         *     DomesticOrInternational pipe can't decide which shipping
         *     categories are valid, so `available_shipping_categories`
         *     comes back empty.
         *   - Without `availableShippingCategories` we can't pass
         *     `shippingOptions` to the Adyen PayPal component, and PayPal
         *     therefore hides its shipping method picker.
         *
         * What we do:
         *   1. Use the geoip-inferred country (or any country already on
         *      the form) and an UN/CEFACT-safe stub postal code so
         *      the BE never assumes USA-only when the visitor is abroad.
         *   2. PATCH /api/cart with just that minimal shipping_address.
         *      The `EnsureShippingIsAppliedIfRequired` pipe then attaches
         *      a default (most expensive) shipping category to the cart.
         *   3. Refresh local cartState so the rest of
         *      createAdyenPayPalComponent sees the populated
         *      `available_shipping_categories`.
         *
         * Notes:
         *   - We deliberately do NOT switch to "cheapest" here. Letting
         *     the BE pick its default and reporting that as the selected
         *     deliveryMethod avoids the race where the PayPal lightbox
         *     opens with a different selection than what the cart was
         *     re-priced for (root cause of the
         *     `selected_delivery_amount_does_not_match_cart_shipping` 422).
         *   - Best-effort: any failure here is logged and swallowed so the
         *     lightbox still opens (it just won't show shipping options
         *     until onShippingAddressChange fires).
         */
        async ensureCartShippingForPaypalLightbox() {
            try {
                const currentCart = this.cartState || this.checkoutOffer || {};
                const existingShipping = currentCart.shipping_address || {};

                // If the cart already has a usable shipping_address (state
                // + country + postal) we don't need to stub anything.
                if (existingShipping && existingShipping.country &&
                    existingShipping.state && existingShipping.postal_code) {
                    paypalDebug('ensure_cart_shipping.skip', {
                        reason: 'cart_already_has_shipping_address',
                        country: existingShipping.country,
                        state:   existingShipping.state,
                    });
                    return;
                }

                // Prefer (a) anything the form already has, then (b) the
                // geoip country (provided by the page via window.userCountry
                // / similar; fall back to USA which is by far the dominant
                // traffic source).
                const formShipping = (this.form && this.form.shipping_address) || {};
                const countryAlpha2 = formShipping.country || window.userCountry || 'US';
                const inferredCountry = getAlpha3CountryCode(countryAlpha2) || 'USA';

                // Tax-neutral stub. We intentionally avoid a real ZIP so
                // AvaTax doesn't recalc real taxes against a placeholder
                // address — the BE pipeline only needs SOME address to
                // populate available_shipping_categories.
                const stub = {
                    address:     formShipping.address     || ' ',
                    address_2:   formShipping.address_2   || '',
                    city:        formShipping.city        || ' ',
                    state:       formShipping.state       || ' ',
                    postal_code: formShipping.postal_code || '00000',
                    country:     inferredCountry,
                };

                paypalDebug('ensure_cart_shipping.before_patch', { stub_shipping_address: stub });

                const cartResult = await window.CheckoutAPI.patch(this, '/api/cart', {
                    shipping_address: stub,
                }, {
                    context: { operation: 'paypal_ensure_cart_shipping' },
                    headers: getHeaders(),
                });

                if (!cartResult.success) {
                    paypalDebug('ensure_cart_shipping.patch_failed', { response: cartResult });
                    return;
                }

                const processed = this.convertCurrency(cartResult.data.data, this.countriesList,
                    this.form.billing_address.country);
                this.checkoutOffer = processed;
                this.cartState = processed;

                paypalDebug('ensure_cart_shipping.patch_success',
                    snapshotCartForDebug(processed));
            } catch (err) {
                paypalDebug('ensure_cart_shipping.exception', {
                    message: (err && err.message) || String(err),
                });
                // Swallow — fall through and let the lightbox open without
                // shipping options if we couldn't pre-condition the cart.
            }
        },

        /**
         * Helper function to fetch PayPal session for Adyen Drop-in integration.
         * Returns a promise that resolves with the PayPal session response.
         */
        async sessionsDropin() {
            // Access payment methods response from the current context or global app instance
            const paymentMethodsResponse = this.adyenPaymentMethodsResponse || window.checkoutAppInstance?.adyenPaymentMethodsResponse;
            if (!paymentMethodsResponse) {
                throw new Error('[Adyen] payment methods not initialized');
            }

            // Check if PayPal is available
            const paypalMethod = paymentMethodsResponse.paymentMethods?.find(m => m.type === 'paypal');
            if (!paypalMethod) {
                throw new Error('[Adyen] PayPal payment method not available');
            }

            const thisCart = this.cartState || this.checkoutOffer;
            const thisAmount = thisCart.amount_base ? thisCart.amount_base.decimal : thisCart.amount.decimal;
            const thisCurrency = this.fetchCurrentCurrency() ?? 'USD';

            // Fetch a PayPal-specific session (without vaulting) for Express Checkout
            // PayPal Express Checkout with the intent: 'authorize' cannot have storePaymentMethod: true
            const payload = {
                amount: thisAmount,
                currency: thisCurrency,
                orderId: thisCart.id,
            };
            const query = new URLSearchParams(payload).toString();

            paypalDebug('session_dropin.before_request', Object.assign({
                request_payload: payload,
            }, snapshotCartForDebug(thisCart)));

            const paypalSessionResponse = await window.CheckoutAPI.get(this, `/api/adyen/paypal/session?${query}`, {
                context: { operation: 'adyen_paypal_session_init' },
                headers: getHeaders()
            });

            if (!paypalSessionResponse.success || !paypalSessionResponse.data?.session) {
                paypalDebug('session_dropin.failed', { response: paypalSessionResponse });
                console.error('[Adyen|PayPal] Failed to create PayPal session:', paypalSessionResponse);
                throw new Error('Failed to initialize PayPal. Please try again.');
            }

            // Store the PayPal-specific session
            this.adyenPayPalSession = paypalSessionResponse.data.session;

            paypalDebug('session_dropin.success', {
                session_id: paypalSessionResponse.data.session?.id,
                adyen_amount: paypalSessionResponse.data.session?.amount,
            });

            return paypalSessionResponse;
        },
        // Create an Adyen PayPal component
        async createAdyenPayPalComponent() {
            // TEMP PAYPAL GATE - BET-1274: skip entirely while PayPal is disabled
            // so we don't fire Adyen session calls or mount an unused button.
            if (typeof PAYPAL_DISABLED !== 'undefined' && PAYPAL_DISABLED) {
                return;
            }

            // Re-init staleness guard. Because we now rebuild the PayPal
            // button on every cart change (toggle a CDP/bump, change shipping,
            // etc. — see reloadComponents), two inits can overlap: this method
            // awaits a 250ms container settle + an async session fetch, so a
            // newer cart change can fire a second init while the first is still
            // in flight. Mounting a button built from a stale session would
            // show outdated line items / totals — the exact problem we are
            // fixing. Each call claims a monotonically increasing token; only
            // the most recent call is allowed to mount.
            const initToken = (this._paypalInitToken || 0) + 1;
            this._paypalInitToken = initToken;
            // Adyen callbacks can run with a child-scope `this` that lacks
            // root-only methods (syncAddresses, addressSynced). Route cart
            // mutations through the root scope object — NOT
            // window.checkoutAppInstance, which is the Petite-Vue app wrapper
            // and does not expose .form.
            const checkoutApp = defaultCheckoutAppNew;

            // Open a fresh debug flow so every request fired during this
            // component lifecycle is correlated under one X-Paypal-Flow-Id.
            startPaypalDebugFlow();
            paypalDebug('component.create.entry', { init_token: initToken });

            try {
                // Destroy existing component if any
                this.destroyAdyenPayPalComponent();

                // Wait for the container to be available
                await new Promise(resolve => setTimeout(resolve, 250));
                // Find the visible container (there may be multiple due to desktop/mobile order summary instances)
                const paypalContainers = document.querySelectorAll('.adyen-paypal-container');
                let container = null;
                for (const el of paypalContainers) {
                    if (el.offsetParent !== null) {
                        container = el;
                        break;
                    }
                }
                if (!container) container = paypalContainers[0] || null;
                if (!container) {
                    console.error('[Adyen] PayPal container not found');
                    paypalDebug('component.create.no_container', {});
                    endPaypalDebugFlow('no_container');
                    return;
                }

                // Pre-condition the cart so the lightbox opens with a
                // shipping picker on the very first show. See helper for
                // rationale. Best-effort — never blocks the lightbox.
                paypalDebug('component.before_ensure_cart_shipping',
                    snapshotCartForDebug(this.cartState || this.checkoutOffer));
                await this.ensureCartShippingForPaypalLightbox();
                paypalDebug('component.after_ensure_cart_shipping',
                    snapshotCartForDebug(this.cartState || this.checkoutOffer));

                const thisCart = this.cartState || this.checkoutOffer;

                // Use sessionsDropin() helper to fetch PayPal session
                paypalDebug('component.before_session_dropin', {});
                await this.sessionsDropin().then(async (paypalSessionResponse) => {
                    // Initialize AdyenCheckout instance
                    const configuration = {
                        locale: 'en_US',
                        countryCode: 'US',
                        environment: paypalSessionResponse.data.environment || 'test',
                        clientKey: paypalSessionResponse.data.clientKey,
                        session: paypalSessionResponse.data.session,
                        // paymentMethodsResponse: this.adyenPaymentMethodsResponse.data,
                        onError: (error) => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            console.error('[Adyen] Checkout error:', error);
                            paypalDebug('checkout.onError', {
                                message: error && error.message,
                                name:    error && error.name,
                                code:    error && error.code,
                            });
                            this.checkoutErrors = [error.message || 'Payment initialization failed'];
                        },
                    };

                    this.adyenPayPalCheckout = await window.AdyenWeb.AdyenCheckout(configuration);
                    this.adyenSession = paypalSessionResponse.data.session;

                    // IMPORTANT — match the BE's selected_delivery
                    // reconciliation rule, NOT the price of the category.
                    //
                    // The BE compares `selected.amount.value` to
                    // `cart->shipping_amount`, which is the sum of all
                    // `OrderOffer` rows of type=shipping on the order.
                    // `available_shipping_categories[i].price_base` is the
                    // raw category price (single SKU), which equals
                    // shipping_amount only when there is exactly one
                    // shipping line on the cart. In every other case
                    // (multiple shipping SKUs, free-shipping promo,
                    // quantity > 1, etc.) the category price will diverge
                    // from shipping_amount and trigger
                    // `selected_delivery_amount_does_not_match_cart_shipping`.
                    //
                    // So we derive the "selected" amount from the cart's
                    // shipping_amount (computed FE-side from order_offers
                    // where type === 'shipping') and mark the category
                    // that backs that selection (the one matching
                    // thisCart.shipping_category.id) as the selected one.
                    const availableShippingCategories = (thisCart.available_shipping_categories || []).map(category => ({
                        id:       category.id.toString(),
                        type:     'SHIPPING',
                        label:    category.name,
                        selected: false,
                        amount: {
                            value:    parseInt(category.price_base.amount.amount, 10),
                            currency: this.fetchCurrentCurrency(),
                        },
                    }));

                    // Compute cart_shipping_minor = sum(shipping order_offers).
                    const cartShippingMinor = (thisCart.order_offers || [])
                        .filter(o => o && o.type === 'shipping')
                        .reduce((sum, o) => {
                            const raw = (o.amount_base && o.amount_base.amount)
                                || (o.amount_local && o.amount_local.amount) || 0;
                            return sum + (parseInt(raw, 10) || 0);
                        }, 0);

                    const appliedShippingCategoryId = thisCart.shipping_category
                        ? thisCart.shipping_category.id
                        : null;

                    let selectedShippingMethod = null;
                    if (appliedShippingCategoryId !== null) {
                        selectedShippingMethod = availableShippingCategories
                            .find(c => parseInt(c.id, 10) === appliedShippingCategoryId) || null;
                        if (selectedShippingMethod) {
                            // Force the "selected" amount to match the BE
                            // reconciler, regardless of the category's raw
                            // price.
                            selectedShippingMethod.amount.value = cartShippingMinor;
                            selectedShippingMethod.selected = true;
                        }
                    }

                    // Edge case: cart has a shipping category but no
                    // shipping offers (cartShippingMinor === 0). Inject a
                    // phantom $0 shipping entry so PayPal still has
                    // something to "select" and the reconciler still
                    // passes (0 === 0).
                    if (!selectedShippingMethod && availableShippingCategories.length > 0) {
                        availableShippingCategories[0].selected = true;
                        availableShippingCategories[0].amount.value = cartShippingMinor;
                        selectedShippingMethod = availableShippingCategories[0];
                    }

                    if (selectedShippingMethod) {
                        this.selectedShippingMethodId = selectedShippingMethod.id;
                    }

                    paypalDebug('component.shipping_options_built', {
                        applied_shipping_category_id: appliedShippingCategoryId,
                        cart_shipping_minor:          cartShippingMinor,
                        selected_method:              selectedShippingMethod,
                        available_count:              availableShippingCategories.length,
                        available_summary:            availableShippingCategories.map(c => ({
                            id: c.id, value: c.amount.value, selected: c.selected,
                        })),
                    });

                    // PayPal address normalizer. Mirrors transformApplePayContactToFormAddress
                    // (defined further down for Apple Pay) - accepts the various field-name
                    // aliases PayPal returns across SDK versions and event payloads, and
                    // produces a form-shaped address with safe fallbacks. Without these
                    // aliases, a deliveryAddress with `addressLine1` instead of `street`
                    // silently got saved as an empty Address record on the BE.
                    const transformPaypalAddressToFormAddress = (source, fallback = null) => {
                        if (!source) return fallback;
                        const street       = source.street || source.line1 || source.addressLine1 || source.address_line_1 || '';
                        const houseNumber  = source.houseNumberOrName || source.house_number_or_name || '';
                        const cleanHN      = (houseNumber && houseNumber !== 'ZZ') ? houseNumber : '';
                        const addressLine1 = cleanHN ? `${cleanHN} ${street}`.trim() : street;
                        const addressLine2 = source.line2 || source.addressLine2 || source.address_line_2 || '';

                        let firstName = source.firstName || source.givenName || source.given_name || '';
                        let lastName  = source.lastName  || source.familyName || source.family_name || '';
                        if (!lastName && firstName && /\s/.test(firstName)) {
                            const parts = firstName.trim().split(/\s+/);
                            firstName = parts[0];
                            lastName  = parts.slice(1).join(' ');
                        }

                        const rawCountry = source.country || source.countryCode || source.country_code || '';
                        const country    = (rawCountry === 'US' ? 'USA' : rawCountry) || (fallback && fallback.country) || 'USA';

                        const phone = source.phoneNumber || source.phone_number || source.phone || '';

                        return {
                            first_name:  firstName    || (fallback && fallback.first_name)  || '',
                            last_name:   lastName     || (fallback && fallback.last_name)   || '',
                            address:     addressLine1 || (fallback && fallback.address)     || '',
                            address2:    addressLine2 || (fallback && fallback.address2)    || '',
                            city:        source.city || source.locality || source.admin_area_2 || (fallback && fallback.city) || '',
                            state:       source.stateOrProvince || source.state || source.administrative_area || source.admin_area_1 || (fallback && fallback.state) || '',
                            postal_code: source.postalCode || source.postal_code || source.zip || (fallback && fallback.postal_code) || '',
                            country:     country,
                            phone:       phone        || (fallback && fallback.phone)       || '',
                        };
                    };

                    // Pull a complete billing + shipping pair from any PayPal data location
                    // we know about (Adyen-normalized top-level, raw purchase_units, payer)
                    // and cross-fill blank fields between the two. Without this, a missing
                    // or partial `data.deliveryAddress` silently produced empty Address
                    // records on the BE - which 422'd physical upsells with "invalid
                    // shipping address" (the bug this fix targets).
                    const extractPaypalAddresses = (data, currentForm) => {
                        const auth         = (data && data.authorizedEvent) || {};
                        const purchaseUnit = (auth.purchase_units && auth.purchase_units[0]) || null;
                        const rawShipNode  = (purchaseUnit && purchaseUnit.shipping) || null;
                        const rawShipAddr  = (rawShipNode && rawShipNode.address) || null;
                        const rawShipName  = rawShipNode && rawShipNode.name && rawShipNode.name.full_name
                            ? { firstName: rawShipNode.name.full_name }
                            : null;
                        const payer        = auth.payer || null;
                        const payerAddr    = (payer && payer.address) || null;
                        const payerName    = payer && payer.name
                            ? { given_name: payer.name.given_name, surname: payer.name.surname }
                            : null;
                        const payerPhone   = (payer && payer.phone && payer.phone.phone_number && payer.phone.phone_number.national_number) || '';

                        const mergeSource = (...parts) => Object.assign({}, ...parts.filter(p => p && Object.keys(p).length > 0));

                        // Shipping: Adyen-normalized -> raw PayPal purchase_units -> payer
                        let shipping = transformPaypalAddressToFormAddress((data && data.deliveryAddress) || null, null);
                        if (!shipping || !shipping.address) {
                            const merged = mergeSource(rawShipAddr, rawShipName, payerName, payerPhone ? { phone: payerPhone } : null);
                            const fromRaw = transformPaypalAddressToFormAddress(Object.keys(merged).length ? merged : null, shipping);
                            if (fromRaw && fromRaw.address) shipping = fromRaw;
                        }
                        if (!shipping || !shipping.address) {
                            const merged = mergeSource(payerAddr, payerName, payerPhone ? { phone: payerPhone } : null);
                            const fromPayer = transformPaypalAddressToFormAddress(Object.keys(merged).length ? merged : null, shipping);
                            if (fromPayer && fromPayer.address) shipping = fromPayer;
                        }

                        // Billing: Adyen-normalized -> payer
                        let billing = transformPaypalAddressToFormAddress((data && data.billingAddress) || null, null);
                        if (!billing || !billing.address) {
                            const merged = mergeSource(payerAddr, payerName, payerPhone ? { phone: payerPhone } : null);
                            const fromPayer = transformPaypalAddressToFormAddress(Object.keys(merged).length ? merged : null, billing);
                            if (fromPayer && fromPayer.address) billing = fromPayer;
                        }

                        // Cross-fill shipping <-> billing when one side is empty or partial.
                        if (!shipping && billing) shipping = Object.assign({}, billing);
                        if (!billing && shipping) billing  = Object.assign({}, shipping);
                        const fillBlanks = (target, src) => {
                            if (!target || !src) return;
                            for (const key of ['first_name','last_name','address','address2','city','state','postal_code','country','phone']) {
                                if (!target[key] && src[key]) target[key] = src[key];
                            }
                        };

                        // Final fallback: name / country / phone from the existing form state
                        // (which the user may have populated in step 1).
                        const formShip = currentForm && currentForm.shipping_address;
                        const formBill = currentForm && currentForm.billing_address;
                        if (shipping) {
                            if (!shipping.first_name) shipping.first_name = (formShip && formShip.first_name) || '';
                            if (!shipping.last_name)  shipping.last_name  = (formShip && formShip.last_name)  || '';
                            if (!shipping.country)    shipping.country    = (formShip && formShip.country)    || 'USA';
                            if (!shipping.phone)      shipping.phone      = (formShip && formShip.phone)      || '';
                        }
                        if (billing) {
                            if (!billing.first_name) billing.first_name = (formBill && formBill.first_name) || '';
                            if (!billing.last_name)  billing.last_name  = (formBill && formBill.last_name)  || '';
                            if (!billing.country)    billing.country    = (formBill && formBill.country)    || 'USA';
                            if (!billing.phone)      billing.phone      = (formBill && formBill.phone)      || '';
                        }

                        const problems = [];
                        if (!shipping) {
                            problems.push('shipping_source_missing_entirely');
                        } else {
                            for (const key of ['address','city','state','postal_code']) {
                                if (!shipping[key]) problems.push('shipping.' + key + '_empty_after_fallbacks');
                            }
                        }

                        return { billing, shipping, problems };
                    };

                    const paypalConfiguration = {
                        isExpress: true,
                        intent: 'authorize',
                        // `userAction: 'commit'` (vs the previous 'continue')
                        // makes PayPal render a final-action button copy
                        // ("Pay $X" / "Pay Now") instead of "Continue" /
                        // "Link and Review". 'continue' is for flows that
                        // show a merchant-side review step before the
                        // charge actually happens; our flow goes straight
                        // from `onPaymentCompleted` to /api/cart/pay with
                        // no intervening confirmation screen, so 'commit'
                        // is the honest signal. Vaulting still happens —
                        // PayPal will keep the small-print
                        // "Link PayPal to VShredECOM" disclosure beneath
                        // the button because of
                        // storePaymentMethodMode: ENABLED, which is the
                        // part we want for frictionless upsells.
                        userAction: 'commit',
                        blockPayPalCreditButton: false,
                        blockPayPalVenmoButton: true,
                        blockPayPalPayLaterButton: true,

                        // Shipping options (if physical products)
                        shippingOptions: availableShippingCategories.length > 0 ? availableShippingCategories : undefined,

                        /**
                         * Validate contact fields before opening the PayPal modal.
                         *
                         * Adyen Web v6 PayPal component signature: (data, actions) => actions.resolve() | actions.reject()
                         * (Different from v5 which exposed `resolve`/`reject` directly. See
                         * https://docs.adyen.com/online-payments/upgrade-your-integration/upgrade-to-web-v6)
                         */
                        onClick: (data, actions) => {
                            try {
                                this.validateContact();
                            } catch (e) {
                                console.error('Error in validateContact:', e);
                            }

                            if (this.contactErrors.length > 0) {
                                requestAnimationFrame(() => {
                                    const firstInvalidElement = document.querySelector('[aria-invalid="true"]');
                                    if (firstInvalidElement) {
                                        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                });
                                return actions.reject();
                            }

                            return actions.resolve();
                        },

                        /**
                         * Called when shipping address changes in PayPal.
                         * Use this to recalculate taxes and update the order total.
                         * According to Adyen v6 docs, we must call updateOrder API and use component.updatePaymentData()
                         */
                        onShippingAddressChange: async (data, actions, component) => {
                            try {
                                paypalDebug('on_shipping_address_change.entry', {
                                    shipping_address: data && data.shippingAddress,
                                });

                                const { shippingAddress } = data;

                                // Update form with new shipping address for tax calculation
                                if (shippingAddress) {
                                    this.form.shipping_address = {
                                        ...this.form.shipping_address,
                                        city: shippingAddress.city || '',
                                        state: shippingAddress.state || '',
                                        postal_code: shippingAddress.postalCode || '',
                                        country: shippingAddress.countryCode || 'US',
                                    };
                                }

                                // Per Adyen v6 docs, the current paymentData for /paypal/updateOrder lives on `component.paymentData`.
                                // See: https://docs.adyen.com/payment-methods/paypal/web-component/express-checkout/#handle-delivery-address-changes-sessions
                                const currentPaymentData = component.paymentData;
                                if (!currentPaymentData) {
                                    console.warn('[Adyen|PayPal] No paymentData available on component at shipping-address-change');
                                    paypalDebug('on_shipping_address_change.no_payment_data', {});
                                    actions.reject();
                                    return;
                                }

                                // Recalculate the total and get updated shipping options
                                const updatedData = await this.recalculateTotal(data);

                                // Prepare updateOrder request according to Adyen docs.
                                // Adyen minor-units must be integers — coerce client-side as a safety net.
                                const updateOrderData = {
                                    paymentData: currentPaymentData,
                                    sessionId: paypalSessionResponse.data.session?.id,
                                    amount: {
                                        currency: updatedData.amount.currency,
                                        value: parseInt(updatedData.amount.value, 10)
                                    },
                                    deliveryMethods: updatedData.shippingOptions?.map(option => ({
                                        reference: option.id,
                                        description: option.label,
                                        type: 'Shipping',
                                        amount: {
                                            currency: option.amount.currency,
                                            value: parseInt(option.amount.value, 10)
                                        },
                                        selected: option.selected || false
                                    })) || []
                                };

                                paypalDebug('on_shipping_address_change.before_update_order_post', {
                                    session_id:        paypalSessionResponse.data.session?.id,
                                    amount:            updateOrderData.amount,
                                    delivery_methods:  updateOrderData.deliveryMethods,
                                });

                                // Call updateOrder API endpoint
                                const updateResponse = await window.CheckoutAPI.post(this, '/api/adyen/paypal/update', updateOrderData, {
                                    context: { operation: 'paypal_update_order' },
                                    headers: getHeaders()
                                });

                                if (window.PayPalUpdateOrderErrors.handle(this, updateResponse, 'shipping_address_change')) {
                                    actions.reject();
                                    return;
                                }

                                paypalDebug('on_shipping_address_change.update_order_response', {
                                    success: updateResponse.success,
                                    reason:  updateResponse.data?.reason,
                                    message: updateResponse.data?.message,
                                });

                                if (!updateResponse.success || !updateResponse.data?.paymentData) {
                                    throw new Error('Failed to update PayPal order');
                                }

                                // Update component with new paymentData from Adyen
                                component.updatePaymentData(updateResponse.data.paymentData);
                                paypalDebug('on_shipping_address_change.ok', {});
                            } catch (error) {
                                if (window.PayPalUpdateOrderErrors.handle(this, error, 'shipping_address_change')) {
                                    actions.reject();
                                    return;
                                }
                                console.error('[Adyen|PayPal] Error handling shipping address change:', error);
                                paypalDebug('on_shipping_address_change.exception', {
                                    message: (error && error.message) || String(error),
                                    stack:   error && error.stack,
                                });
                                this.checkoutErrors = [window.PayPalUpdateOrderErrors.getUserMessage(error, 'We could not update your shipping address. Please try again.')];
                                actions.reject();
                            }
                        },

                        /**
                         * Called when a shipping option changes in PayPal.
                         * Use this to update shipping costs.
                         * According to Adyen v6 docs, we must call updateOrder API and use component.updatePaymentData()
                         */
                        onShippingOptionsChange: async (data, actions, component) => {
                            try {
                                paypalDebug('on_shipping_options_change.entry', {
                                    selected_option: data && data.selectedShippingOption,
                                });

                                const { selectedShippingOption } = data;

                                if (!selectedShippingOption) {
                                    paypalDebug('on_shipping_options_change.no_selection', {});
                                    actions.reject();
                                    return;
                                }

                                // Update the selected shipping method
                                this.selectedShippingMethodId = selectedShippingOption.id;

                                // Per Adyen v6 docs, component.paymentData holds the latest token for /paypal/updateOrder.
                                const currentPaymentData = component.paymentData;
                                if (!currentPaymentData) {
                                    console.warn('[Adyen|PayPal] No paymentData available on component at shipping-options-change');
                                    paypalDebug('on_shipping_options_change.no_payment_data', {});
                                    actions.reject();
                                    return;
                                }

                                // Respect the shopper's explicit pick from the PayPal lightbox
                                // rather than forcing the cheapest.
                                const updatedData = await this.recalculateTotal(data, {
                                    shippingCategoryId: selectedShippingOption.id
                                });

                                // Prepare updateOrder request according to Adyen docs.
                                // Adyen minor-units must be integers — coerce client-side as a safety net.
                                const updateOrderData = {
                                    paymentData: currentPaymentData,
                                    sessionId: paypalSessionResponse.data.session?.id,
                                    amount: {
                                        currency: updatedData.amount.currency,
                                        value: parseInt(updatedData.amount.value, 10)
                                    },
                                    deliveryMethods: updatedData.shippingOptions?.map(option => ({
                                        reference: option.id,
                                        description: option.label,
                                        type: 'Shipping',
                                        amount: {
                                            currency: option.amount.currency,
                                            value: parseInt(option.amount.value, 10)
                                        },
                                        selected: option.selected || false
                                    })) || []
                                };

                                paypalDebug('on_shipping_options_change.before_update_order_post', {
                                    session_id:       paypalSessionResponse.data.session?.id,
                                    amount:           updateOrderData.amount,
                                    delivery_methods: updateOrderData.deliveryMethods,
                                });

                                // Call updateOrder API endpoint
                                const updateResponse = await window.CheckoutAPI.post(this, '/api/adyen/paypal/update', updateOrderData, {
                                    context: { operation: 'paypal_update_order' },
                                    headers: getHeaders()
                                });

                                if (window.PayPalUpdateOrderErrors.handle(this, updateResponse, 'shipping_options_change')) {
                                    actions.reject();
                                    return;
                                }

                                paypalDebug('on_shipping_options_change.update_order_response', {
                                    success: updateResponse.success,
                                    reason:  updateResponse.data?.reason,
                                    message: updateResponse.data?.message,
                                });

                                if (!updateResponse.success || !updateResponse.data?.paymentData) {
                                    throw new Error('Failed to update PayPal order');
                                }

                                // Update component with new paymentData from Adyen
                                component.updatePaymentData(updateResponse.data.paymentData);
                                paypalDebug('on_shipping_options_change.ok', {});
                            } catch (error) {
                                if (window.PayPalUpdateOrderErrors.handle(this, error, 'shipping_options_change')) {
                                    actions.reject();
                                    return;
                                }
                                console.error('[Adyen|PayPal] Error handling shipping option change:', error);
                                paypalDebug('on_shipping_options_change.exception', {
                                    message: (error && error.message) || String(error),
                                    stack:   error && error.stack,
                                });
                                this.checkoutErrors = [window.PayPalUpdateOrderErrors.getUserMessage(error, 'We could not update your shipping method. Please try again.')];
                                actions.reject();
                            }
                        },

                        /**
                         * After the shopper completes the payment in the PayPal lightbox and the lightbox closes,
                         * you can get the shopper's payment information.
                         * When the lightbox closes, the Component triggers the onAuthorized callback.
                         */
                        onAuthorized: async (data, actions) => {
                            try {
                                paypalDebug('on_authorized.entry', {
                                    status:                 data && data.authorizedEvent && data.authorizedEvent.status,
                                    has_delivery_address:   !!(data && data.deliveryAddress),
                                    has_billing_address:    !!(data && data.billingAddress),
                                    has_purchase_units:     !!(data && data.authorizedEvent && data.authorizedEvent.purchase_units),
                                    has_payer:              !!(data && data.authorizedEvent && data.authorizedEvent.payer),
                                });

                                if (data.authorizedEvent.status !== 'APPROVED') {
                                    console.error('[Adyen|PayPal] Authorized Event Status:', data.authorizedEvent.status);
                                    paypalDebug('on_authorized.not_approved', {
                                        status: data.authorizedEvent.status,
                                    });
                                    actions.reject();
                                    return;
                                }

                                // TEMP DIAGNOSTIC: dump the raw PayPal payload structure to the
                                // browser console so we can verify which fields are populated
                                // in this environment. Remove once the multi-source extraction
                                // below has been confirmed to produce complete addresses
                                // end-to-end (initial purchase + physical upsell).
                                try {
                                    console.debug('[Adyen|PayPal] onAuthorized raw data structure', {
                                        deliveryAddress: data.deliveryAddress,
                                        billingAddress:  data.billingAddress,
                                        purchase_units:  data && data.authorizedEvent && data.authorizedEvent.purchase_units,
                                        payer:           data && data.authorizedEvent && data.authorizedEvent.payer,
                                    });
                                } catch (_) { /* never let logging break checkout */ }

                                // Preserve raw deliveryAddress for downstream:
                                // adyenPayPalCompleteSessionOrder forwards it into the
                                // /api/cart/pay payload via form.payment.details.deliveryAddress.
                                checkoutApp.paypalDeliveryAddress = data.deliveryAddress;

                                // Store PayPal order/session identifiers for attaching to the User Payment Method
                                // authorizedEvent.id is the PayPal Order ID (canonical transaction reference)
                                this.paypalAuthorizedEvent = {
                                    id: data.authorizedEvent?.id || null,
                                    payer_id: data.authorizedEvent?.payer?.payer_id || null,
                                    email_address: data.authorizedEvent?.payer?.email_address || null,
                                };

                                // Extract billing + shipping from any PayPal source (Adyen
                                // normalized, raw purchase_units, payer), cross-fill blanks
                                // between billing and shipping, and apply to the form.
                                const { billing, shipping, problems } = extractPaypalAddresses(data, checkoutApp.form);

                                if (billing)  checkoutApp.form.billing_address  = billing;
                                if (shipping) checkoutApp.form.shipping_address = shipping;

                                // Hard-fail when shipping is still incomplete after every
                                // fallback. Without this guard the BE persisted Address
                                // records with empty street/city/state/postal_code (just
                                // name+phone copied from the user profile), which 422'd
                                // physical-product upsells with "invalid shipping address" -
                                // the exact bug this change fixes.
                                const ship = checkoutApp.form.shipping_address;
                                const isCompleteShipping = !!(ship && ship.address && ship.city && ship.state && ship.postal_code);

                                if (!isCompleteShipping) {
                                    console.error('[Adyen|PayPal] Incomplete shipping address from PayPal', {
                                        problems:     problems,
                                        shipping:    shipping,
                                        billing:     billing,
                                        rawDelivery: data.deliveryAddress,
                                        rawBilling:  data.billingAddress,
                                    });
                                    paypalDebug('on_authorized.shipping_address_incomplete', { problems: problems });
                                    checkoutApp.checkoutErrors = [
                                        'We could not get a complete shipping address from your PayPal account. ' +
                                        'Please add a shipping address to PayPal and try again, or use a different payment method.',
                                    ];
                                    actions.reject();
                                    return;
                                }

                                paypalDebug('on_authorized.addresses_resolved', {
                                    shipping_has_street: !!ship.address,
                                    shipping_has_phone:  !!ship.phone,
                                    billing_has_street:  !!(checkoutApp.form.billing_address && checkoutApp.form.billing_address.address),
                                    problems_count:      problems.length,
                                });

                                // Mirror Apple Pay: record whether billing and shipping
                                // differ so syncAddresses() does not overwrite the
                                // wallet-provided shipping_address.
                                checkoutApp.form.shipping_same = (JSON.stringify(checkoutApp.form.billing_address) === JSON.stringify(checkoutApp.form.shipping_address));

                                // Persist the PayPal address to the cart and mark
                                // addressSynced so processOrder() can proceed.
                                await checkoutApp.syncAddresses();
                                if (!checkoutApp.addressSynced) {
                                    paypalDebug('on_authorized.address_sync_failed', {});
                                    checkoutApp.checkoutErrors = [
                                        'We could not save your shipping address. Please try again or use a different payment method.',
                                    ];
                                    actions.reject();
                                    return;
                                }

                                actions.resolve();
                                paypalDebug('on_authorized.resolved', { address_synced: checkoutApp.addressSynced });
                            } catch (error) {
                                console.error('[Adyen|PayPal] Error handling onAuthorized:', error);
                                paypalDebug('on_authorized.exception', {
                                    message: (error && error.message) || String(error),
                                    stack:   error && error.stack,
                                });
                                actions.reject();
                            }
                        },

                        /**
                         * Called when payment is completed successfully.
                         * For sessions-based PayPal with authorize intent, the payment is authorized
                         * when this callback fires with resultCode: "Authorised".
                         */
                        onPaymentCompleted: async (data, component) => {
                            const checkoutApp = window.checkoutAppScope || defaultCheckoutAppNew;
                            try {
                                // Sessions flow: Adyen already authorized inside the SDK.
                                // Guard against duplicate handler invocations (remounts, double
                                // callbacks) that would call /api/cart/pay twice.
                                if (checkoutApp._adyenPayPalCheckoutInFlight || checkoutApp._adyenPayPalOrderFinalized) {
                                    paypalDebug('on_payment_completed.duplicate_blocked', {
                                        in_flight: checkoutApp._adyenPayPalCheckoutInFlight,
                                        finalized: checkoutApp._adyenPayPalOrderFinalized,
                                    });
                                    return;
                                }
                                checkoutApp._adyenPayPalCheckoutInFlight = true;

                                paypalDebug('on_payment_completed.entry', {
                                    result_code: data && data.resultCode,
                                });

                                const sessionResult = extractPaypalSessionResult(data, component);
                                if (!sessionResult) {
                                    checkoutApp._adyenPayPalCheckoutInFlight = false;
                                    console.error('[Adyen|PayPal] onPaymentCompleted missing sessionResult', { data });
                                    checkoutApp.paymentErrors = [
                                        'Payment verification data is missing. Please try again.',
                                    ];
                                    endPaypalDebugFlow('payment_completed_missing_session_result');
                                    await checkoutApp.handleAdyenPayPalFlowFailure(
                                        new Error('Payment verification data is missing. Please try again.'),
                                        { preserveErrors: true },
                                    );
                                    return;
                                }

                                checkoutApp._adyenPayPalSessionAuthorized = true;

                                // Adyen's onPaymentCompleted payload omits the sessionId — read it
                                // from the live PayPal session we stashed in createPaypalSession().
                                const sessionId = checkoutApp.adyenPayPalSession?.id;
                                await checkoutApp.adyenPayPalCompleteSessionOrder({
                                    resultCode: data && data.resultCode,
                                    sessionData: data && data.sessionData,
                                    sessionResult: sessionResult,
                                    sessionId: sessionId,
                                    gateway: checkoutApp.gateway,
                                });

                                // Prevent a second lightbox authorization while we redirect.
                                checkoutApp.destroyAdyenPayPalComponent();

                                paypalDebug('on_payment_completed.ok', {});
                                endPaypalDebugFlow('payment_completed');
                            } catch (error) {
                                releaseAdyenPayPalCheckoutLock(checkoutApp);
                                console.error('[Adyen|PayPal] Error completing order:', error);
                                paypalDebug('on_payment_completed.exception', {
                                    message: (error && error.message) || String(error),
                                    stack:   error && error.stack,
                                });
                                checkoutApp.clearAllErrors();
                                const gateway = error?.errors?.gateway;
                                if (Array.isArray(gateway) && gateway.length) {
                                    checkoutApp.paymentErrors = gateway;
                                } else if (typeof gateway === 'string' && gateway.trim()) {
                                    checkoutApp.paymentErrors = [gateway.trim()];
                                } else {
                                    checkoutApp.paymentErrors = [error.message || 'Failed to complete PayPal payment'];
                                }
                                endPaypalDebugFlow('payment_completed_exception');
                                await checkoutApp.handleAdyenPayPalFlowFailure(error, { preserveErrors: true });
                            }
                        },

                        /**
                         * Called when payment fails.
                         */
                        onPaymentFailed: async (data, component) => {
                            const checkoutApp = window.checkoutAppScope || defaultCheckoutAppNew;
                            console.error('[Adyen|PayPal] onPaymentFailed fired', { data, component });
                            paypalDebug('on_payment_failed', {
                                data_summary: data && {
                                    resultCode: data.resultCode,
                                    refusalReason: data.refusalReason,
                                },
                            });
                            checkoutApp.paymentErrors = ['PayPal payment was declined. Please try again.'];
                            endPaypalDebugFlow('payment_failed');
                            await checkoutApp.handleAdyenPayPalFlowFailure(
                                new Error('PayPal payment was declined. Please try again.'),
                                { preserveErrors: true },
                            );
                        },

                        onError: async (error) => {
                            const checkoutApp = window.checkoutAppScope || defaultCheckoutAppNew;
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            console.error('[Adyen] PayPal error:', error);
                            paypalDebug('paypal.on_error', {
                                message: error && error.message,
                                name:    error && error.name,
                                code:    error && error.code,
                            });
                            checkoutApp.paymentErrors = [error.message || 'PayPal payment failed'];
                            endPaypalDebugFlow('paypal_on_error');
                            await checkoutApp.handleAdyenPayPalFlowFailure(error, { preserveErrors: true });
                        },

                        /*onShopperDetails: async (shopperDetails, rawData, actions) => {
							// hook reserved for future use
						},*/
                    };

                    // Bail if a newer init superseded this one while we were
                    // fetching the session — the latest cart state wins.
                    if (this._paypalInitToken !== initToken) {
                        paypalDebug('component.create.superseded', {
                            this_token:   initToken,
                            latest_token: this._paypalInitToken,
                        });
                        return;
                    }

                    this.adyenPayPalComponent = new window.AdyenWeb.PayPal(this.adyenPayPalCheckout, paypalConfiguration).mount(container);
                    paypalDebug('component.mounted', { init_token: initToken });
                }).catch((error) => {
                    console.error('[Adyen] Error in sessionsDropin:', error);
                    paypalDebug('component.sessions_dropin_then_error', {
                        message: (error && error.message) || String(error),
                        stack:   error && error.stack,
                    });
                    this.checkoutErrors = [error.message || 'Failed to initialize PayPal'];
                    endPaypalDebugFlow('sessions_dropin_error');
                });
            } catch (error) {
                console.error('[Adyen] Error creating PayPal component:', error);
                paypalDebug('component.create.exception', {
                    message: (error && error.message) || String(error),
                    stack:   error && error.stack,
                });
                this.checkoutErrors = [error.message || 'Failed to initialize PayPal'];
                endPaypalDebugFlow('component_create_exception');
            }
        },
        recalculateTotal: async function(data, options = {}) {
            paypalDebug('recalculate_total.entry', Object.assign({
                options:          options,
                paypal_shipping:  data && data.shippingAddress,
                paypal_selected:  data && data.selectedShippingOption,
            }, snapshotCartForDebug(this.cartState || this.checkoutOffer)));

            // 1) Resolve the shipping country.
            //
            //    The PayPal-provided countryCode (alpha-2) is the authoritative
            //    source of truth — the user just confirmed it inside the
            //    PayPal wallet. The previous implementation relied solely on
            //    /api/location-check/lookup-postal-code, which on testing-4
            //    sometimes returns `{country: "N/A"}` (geoip lookup down or
            //    rate-limited). Forwarding "N/A" to the BE PATCH /api/cart
            //    causes the DomesticOrInternational pipe to treat the cart
            //    as international (since "N/A" !== "USA"), which restricts
            //    available_shipping_categories to International Flat Rate
            //    only — exactly the bug we are fixing.
            //
            //    So:
            //      a. Start with the form's existing country.
            //      b. If PayPal handed us a countryCode, prefer it (alpha-2
            //         → alpha-3 conversion for our BE).
            //      c. lookupPostalCode is treated as a state/city enrichment
            //         only, and is fully ignored unless it returns a real
            //         country code (i.e. not "N/A").
            let resolvedCountryAlpha3 = getAlpha3CountryCode(this.form.shipping_address.country) || 'USA';

            const paypalCountryAlpha2 = data && data.shippingAddress
                && data.shippingAddress.countryCode;
            if (paypalCountryAlpha2) {
                resolvedCountryAlpha3 = getAlpha3CountryCode(paypalCountryAlpha2) || resolvedCountryAlpha3;
            }

            let resolvedState     = (data && data.shippingAddress && data.shippingAddress.state)        || this.form.shipping_address.state || '';
            let resolvedCity      = (data && data.shippingAddress && data.shippingAddress.city)         || this.form.shipping_address.city  || '';
            const resolvedPostal  = (data && data.shippingAddress && data.shippingAddress.postalCode)   || this.form.shipping_address.postal_code || '';

            // Best-effort enrichment via postal-code lookup (skip if no postal).
            let lookupSource = 'skipped_no_postal';
            let lookupCountry = null;
            if (resolvedPostal) {
                try {
                    const lookupResult = await window.CheckoutAPI.get(this, `/api/location-check/lookup-postal-code?postal_code=${encodeURIComponent(resolvedPostal)}&country=${encodeURIComponent(resolvedCountryAlpha3)}`, {
                        context: { operation: 'paypal_postal_code_lookup' },
                        headers: getHeaders(),
                    });
                    if (lookupResult.success && lookupResult.data && lookupResult.data.data) {
                        lookupCountry = lookupResult.data.data.country || null;
                        // Only honor the lookup country if it's a real code
                        // (i.e. not "N/A" or empty).
                        if (lookupCountry && lookupCountry !== 'N/A') {
                            if (!paypalCountryAlpha2) {
                                // Caller didn't give us a PayPal country, trust the lookup.
                                resolvedCountryAlpha3 = lookupCountry;
                            }
                            lookupSource = 'used_full';
                        } else {
                            lookupSource = 'discarded_na';
                        }
                        if (!resolvedState && lookupResult.data.data.state) {
                            resolvedState = lookupResult.data.data.state;
                        }
                        if (!resolvedCity && lookupResult.data.data.city) {
                            resolvedCity = lookupResult.data.data.city;
                        }
                    } else {
                        lookupSource = 'failed';
                    }
                } catch (e) {
                    lookupSource = 'exception';
                }
            }

            paypalDebug('recalculate_total.country_resolution', {
                paypal_country_alpha2:  paypalCountryAlpha2,
                lookup_country_alpha3:  lookupCountry,
                lookup_source:          lookupSource,
                resolved_country_alpha3: resolvedCountryAlpha3,
                resolved_state:         resolvedState,
                resolved_city:          resolvedCity,
                resolved_postal:        resolvedPostal,
            });

            // Persist the resolved country back to the form in alpha-2 form so
            // other parts of the page (BillingShipping, etc) stay consistent.
            this.form.shipping_address.country = getAlpha2CountryCode(resolvedCountryAlpha3);
            this.form.shipping_address.state   = resolvedState;
            this.form.shipping_address.city    = resolvedCity;
            this.form.shipping_address.postal_code = resolvedPostal;

            // Build the API payload (BE expects alpha-3).
            const shippingAddressForAPI = {
                ...this.form.shipping_address,
                country: resolvedCountryAlpha3,
                state:   resolvedState,
                city:    resolvedCity,
                postal_code: resolvedPostal,
            };

            // 2) Sync the shipping address. Note: PATCH /api/cart ignores
            //    `shipping_category_id` in the body — only `shipping_address`
            //    is honored. The backend's `EnsureShippingIsAppliedIfRequired`
            //    pipe then auto-picks the MOST EXPENSIVE category as the
            //    default whenever the cart has no category assigned.
            paypalDebug('recalculate_total.before_patch_cart', {
                shipping_address: shippingAddressForAPI,
            });

            const cartResult = await window.CheckoutAPI.patch(this, '/api/cart', { shipping_address: shippingAddressForAPI }, {
                context: { operation: 'paypal_shipping_address_change' },
                headers: getHeaders()
            });

            if (!cartResult.success) {
                paypalDebug('recalculate_total.patch_failed', { response: cartResult });
                throw new Error('Failed to update shipping address');
            }

            let processedResult = this.convertCurrency(cartResult.data.data, this.countriesList, this.form.billing_address.country);
            this.checkoutOffer = processedResult;
            this.cartState = processedResult;
            // PayPal lightbox address/shipping changes PATCH the cart here;
            // mark synced so processOrder() does not re-run syncAddresses()
            // with stale form state when onAuthorized has not fired yet.
            this.addressSynced = true;

            paypalDebug('recalculate_total.after_patch_cart',
                snapshotCartForDebug(processedResult));

            // 3) Decide whether to switch shipping_category.
            //
            //    We DO NOT auto-switch to cheapest here. The previous
            //    implementation did so on every onShippingAddressChange,
            //    which produced a rapid PATCH /api/cart → POST
            //    /api/cart/shipping-categories/X sequence. Those two
            //    operations race for `Cache::lock('cart-recalculate-…')`
            //    inside ShoppingCart::recalculate; when the second call
            //    fails to acquire the lock, its pipeline silently skips,
            //    leaving the cart in an inconsistent state where
            //    `shipping_amount` no longer matches `available_shipping_categories[i].price`.
            //    That mismatch is what the reconciler rejects as
            //    `selected_delivery_amount_does_not_match_cart_shipping`.
            //
            //    So we only POST /api/cart/shipping-categories/{id} when the
            //    caller explicitly asked for a specific category (i.e. the
            //    user picked one inside the PayPal lightbox).
            const available = processedResult.available_shipping_categories || [];
            let appliedShippingId = processedResult.shipping_category?.id ?? null;

            let desiredShippingId = null;
            if (options.shippingCategoryId != null) {
                const requested = parseInt(options.shippingCategoryId, 10);
                if (available.some(c => c.id === requested)) {
                    desiredShippingId = requested;
                }
            }

            paypalDebug('recalculate_total.switch_decision', {
                applied_shipping_id:    appliedShippingId,
                desired_shipping_id:    desiredShippingId,
                will_switch:            desiredShippingId !== null && appliedShippingId !== desiredShippingId,
                available_ids:          available.map(c => c.id),
            });

            if (desiredShippingId !== null && appliedShippingId !== desiredShippingId) {
                paypalDebug('recalculate_total.before_switch_post', {
                    target_shipping_id: desiredShippingId,
                });

                const switchResult = await window.CheckoutAPI.post(this, `/api/cart/shipping-categories/${desiredShippingId}`, {}, {
                    context: { operation: 'paypal_shipping_category_apply' },
                    headers: getHeaders()
                });

                paypalDebug('recalculate_total.after_switch_post', {
                    success:  switchResult.success,
                    response: switchResult,
                });

                if (switchResult.success) {
                    processedResult = this.convertCurrency(switchResult.data.data, this.countriesList, this.form.billing_address.country);
                    this.checkoutOffer = processedResult;
                    this.cartState = processedResult;
                    appliedShippingId = processedResult.shipping_category?.id ?? desiredShippingId;
                } else {
                    paypalDebug('recalculate_total.switch_failed', { response: switchResult });
                }
            }

            this.selectedShippingMethodId = appliedShippingId;
            this.selectedShippingOption = appliedShippingId;

            // 4) Build deliveryMethods. The "selected" entry's amount must
            //    equal `cart->shipping_amount` (sum of all type=shipping
            //    OrderOffer rows), otherwise reconcileDeliveryMethodsWithOrder
            //    rejects /paypal/updateOrder.
            const cartShippingMinor = (processedResult.order_offers || [])
                .filter(o => o && o.type === 'shipping')
                .reduce((sum, o) => {
                    const raw = (o.amount_base && o.amount_base.amount)
                        || (o.amount_local && o.amount_local.amount) || 0;
                    return sum + (parseInt(raw, 10) || 0);
                }, 0);

            const updatedShippingCategories = (processedResult.available_shipping_categories || []).map(category => {
                const isSelected = appliedShippingId !== null && category.id === appliedShippingId;
                return {
                    id:       category.id.toString(),
                    type:     'SHIPPING',
                    label:    category.name,
                    selected: isSelected,
                    amount: {
                        // For the selected category, force the value to
                        // the cart's actual shipping_amount so the BE
                        // reconciler always passes. For non-selected
                        // rows we keep the raw category price (PayPal
                        // only displays it as the picker label).
                        value:    isSelected ? cartShippingMinor : parseInt(category.price_base.amount.amount, 10),
                        currency: this.fetchCurrentCurrency(),
                    },
                };
            });

            // Defensive: if for some reason no category matched the
            // applied id but there is a positive cart_shipping_minor,
            // force the cheapest entry into "selected" and rewrite its
            // value so the reconciler still passes.
            if (!updatedShippingCategories.some(c => c.selected) && updatedShippingCategories.length > 0) {
                const target = updatedShippingCategories[0];
                target.selected = true;
                target.amount.value = cartShippingMinor;
                paypalDebug('recalculate_total.no_selected_forced_fallback', { forced: target });
            }

            const result = {
                shippingOptions: updatedShippingCategories,
                amount: {
                    value: this.fetchCurrentAmount(),
                    currency: this.fetchCurrentCurrency()
                }
            };

            paypalDebug('recalculate_total.exit', {
                cart_shipping_minor: cartShippingMinor,
                applied_shipping_id: appliedShippingId,
                return_amount:       result.amount,
                return_options:      result.shippingOptions.map(o => ({ id: o.id, value: o.amount.value, selected: o.selected })),
            });

            return result;
        },
        fetchCurrentAmount: function() {
            return this.getCartAmountMinor();
        },
        fetchCurrentCurrency: function() {
            const thisCart = this.cartState || this.checkoutOffer;
            return thisCart.amount_base ? thisCart.amount_base.currency : thisCart.amount.currency;
        },
        getCartAmountMinor: function(cart) {
            return resolveCartAmountMinor(cart || this.cartState || this.checkoutOffer);
        },
        formatApplePayTotalAmount: function(amountMinor) {
            return (parseInt(amountMinor, 10) / 100).toFixed(2);
        },
        buildApplePayShippingMethods: function(processedResult, selectedCategoryId = null) {
            const categories = processedResult?.available_shipping_categories || [];

            const methods = categories.map(category => {
                const shippingAmountMinor = parseInt(
                    category.price_base?.amount?.amount ?? category.price?.amount?.amount ?? 0,
                    10
                );

                return {
                    id: category.id,
                    label: category.name,
                    amount: this.formatApplePayTotalAmount(shippingAmountMinor),
                    detail: category.description || '',
                    type: 'final',
                    selected: selectedCategoryId !== null && category.id === selectedCategoryId,
                };
            });

            if (selectedCategoryId === null && methods.length > 0) {
                const lowest = methods.reduce((prev, curr) =>
                    parseFloat(curr.amount) < parseFloat(prev.amount) ? curr : prev
                );
                lowest.selected = true;
            }

            return methods;
        },
        assertApplePaySheetAmountMatchesServer: function(serverAmountMinor, contextLabel) {
            const expected = this._applePaySheetTotalMinor;
            if (expected === null || expected === undefined) {
                return true;
            }

            const server = parseInt(serverAmountMinor, 10);
            if (server === parseInt(expected, 10)) {
                return true;
            }

            addDDAction('applepay-amount-mismatch', {
                context: contextLabel,
                sheet_amount_minor: expected,
                server_amount_minor: server,
            });

            return false;
        },
        /**
         * Serialized Apple Pay cart recalculation. Mirrors PayPal recalculateTotal but
         * returns Apple Pay sheet payloads and tracks the minor-unit total shown on
         * the wallet sheet so we can detect drift before /api/cart/pay.
         */
        recalculateApplePayCart: async function(options = {}) {
            const checkoutApp = window.checkoutAppScope || this;

            return window.CheckoutRequestController.runExclusive('apple-pay-cart', async () => {
                checkoutApp.applePayCartSyncing = true;

                try {
                    if (options.runPostalLookup !== false && checkoutApp.form.shipping_address?.postal_code) {
                        const lookupCountry = getAlpha3CountryCode(checkoutApp.form.shipping_address.country);
                        const lookupResult = await window.CheckoutAPI.get(
                            checkoutApp,
                            `/api/location-check/lookup-postal-code?postal_code=${checkoutApp.form.shipping_address.postal_code}&country=${lookupCountry}`,
                            {
                                context: { operation: 'applepay_postal_code_lookup' },
                                headers: getHeaders(),
                            }
                        );

                        if (lookupResult.success && lookupResult.data?.data?.country) {
                            checkoutApp.form.shipping_address.country = getAlpha2CountryCode(lookupResult.data.data.country);
                        }
                    }

                    checkoutApp.form.shipping_address.name = checkoutApp.form.shipping_address.name || checkoutApp.form.user.name;
                    checkoutApp.form.shipping_address.email = checkoutApp.form.shipping_address.email || checkoutApp.form.user.email;
                    if (!checkoutApp.form.shipping_address.phone) {
                        checkoutApp.form.shipping_address.phone = checkoutApp.form.user.phone;
                    }

                    const shippingAddressForAPI = {
                        ...checkoutApp.form.shipping_address,
                        country: getAlpha3CountryCode(checkoutApp.form.shipping_address.country),
                    };

                    const patchPayload = { shipping_address: shippingAddressForAPI };

                    if (options.includeBilling && checkoutApp.form.billing_address?.postal_code) {
                        patchPayload.billing_address = {
                            ...checkoutApp.form.billing_address,
                            country: getAlpha3CountryCode(checkoutApp.form.billing_address.country),
                        };
                    }

                    const cartResult = await window.CheckoutAPI.patch(checkoutApp, '/api/cart', patchPayload, {
                        context: { operation: options.operation || 'applepay_cart_recalculate' },
                        headers: getHeaders(),
                    });

                    if (!cartResult.success) {
                        throw new Error('Failed to update cart for Apple Pay');
                    }

                    let processedResult = checkoutApp.convertCurrency(
                        cartResult.data.data,
                        checkoutApp.countriesList,
                        checkoutApp.form.billing_address.country
                    );
                    checkoutApp.checkoutOffer = processedResult;
                    checkoutApp.cartState = processedResult;
                    checkoutApp.addressSynced = true;

                    const available = processedResult.available_shipping_categories || [];
                    let appliedShippingId = processedResult.shipping_category?.id ?? null;

                    let desiredShippingId = null;
                    if (options.shippingCategoryId != null) {
                        const requested = parseInt(options.shippingCategoryId, 10);
                        if (available.some(c => c.id === requested)) {
                            desiredShippingId = requested;
                        }
                    }
                    if (desiredShippingId === null && available.length > 0) {
                        const cheapest = available.reduce((prev, curr) =>
                            parseInt(curr.price_base.amount.amount, 10) < parseInt(prev.price_base.amount.amount, 10) ? curr : prev
                        );
                        desiredShippingId = cheapest.id;
                    }

                    if (desiredShippingId !== null && appliedShippingId !== desiredShippingId) {
                        const switchResult = await window.CheckoutAPI.post(
                            checkoutApp,
                            `/api/cart/shipping-categories/${desiredShippingId}`,
                            {},
                            {
                                context: { operation: 'applepay_shipping_category_apply' },
                                headers: getHeaders(),
                            }
                        );

                        if (switchResult.success) {
                            processedResult = checkoutApp.convertCurrency(
                                switchResult.data.data,
                                checkoutApp.countriesList,
                                checkoutApp.form.billing_address.country
                            );
                            checkoutApp.checkoutOffer = processedResult;
                            checkoutApp.cartState = processedResult;
                            appliedShippingId = processedResult.shipping_category?.id ?? desiredShippingId;
                        }
                    }

                    checkoutApp.selectedShippingMethodId = appliedShippingId;
                    checkoutApp.selectedShippingOption = appliedShippingId;

                    const amountMinor = checkoutApp.getCartAmountMinor(processedResult);
                    checkoutApp._applePaySheetTotalMinor = amountMinor;
                    checkoutApp._applePayCartSyncGeneration += 1;

                    addDDAction('applepay-cart-sync', {
                        amount_minor: amountMinor,
                        shipping_category_id: appliedShippingId,
                        generation: checkoutApp._applePayCartSyncGeneration,
                        operation: options.operation || 'applepay_cart_recalculate',
                    });

                    return {
                        amountMinor,
                        currency: checkoutApp.fetchCurrentCurrency(),
                        shippingMethods: checkoutApp.buildApplePayShippingMethods(processedResult, appliedShippingId),
                        newTotal: {
                            label: 'Total',
                            amount: checkoutApp.formatApplePayTotalAmount(amountMinor),
                            type: 'final',
                        },
                        processedResult,
                    };
                } finally {
                    checkoutApp.applePayCartSyncing = false;
                }
            });
        },
        /**
         * Complete order for sessions-based PayPal payment.
         * When resultCode is "Authorised", the payment is already done on Adyen's side.
         * We just need to process the order on our backend.
         */
        adyenPayPalCompleteSessionOrder: async function (sessionPaymentData) {
            if (this._adyenPayPalOrderFinalized) {
                return;
            }

            const sessionResult = sessionPaymentData?.sessionResult || sessionPaymentData?.session_result;
            if (!sessionPaymentData || !sessionPaymentData.resultCode || !sessionResult) {
                this._adyenPayPalCheckoutInFlight = false;
                console.error('[Adyen|PayPal] Cannot complete order - session verification data missing:', sessionPaymentData);
                throw new Error('Payment verification data is missing. Please try again.');
            }

            addDDAction('paypal-session-payment-complete', { sessionPaymentData });

            try {
                /** IMPORTANT!!
                 * The request for `stored-payment-methods` has been moved to a backend
                 * request in later revisions of the checkout (security). If this checkout
                 * is using vault+auth then the request to `stored-payment-methods
                 */
                // For session-based payments, the payment is already authorized
                // Set up the form with session data for backend verification
                this.form.payment.gateway = sessionPaymentData.gateway ?? this.gateway;
                this.form.payment.details = {
                    paymentType: 'paypal',
                    sessionBased: true,
                    resultCode: sessionPaymentData.resultCode,
                    sessionData: sessionPaymentData.sessionData,
                    sessionResult: sessionResult,
                    sessionId: this.adyenPayPalSession?.id,
                    paypalAuthorizedEvent: this.paypalAuthorizedEvent,
                };

                // Skip the /api/cart/payment-token PATCH for session-based PayPal —
                // Adyen sessions are one-time-use and the cache entry is consumed
                // on the very first server-side verification call. PATCHing
                // payment-token here would drain the session, then processOrder()'s
                // subsequent /api/cart/pay would re-verify against an empty cache
                // and 500 with a TypeError. The session payload is already on
                // form.payment.details (resultCode/sessionData/sessionResult/sessionId)
                // and the backend recognises it during /api/cart/pay via
                // SessionBasedPaymentException — no separate tokenization step is
                // needed for this flow.
                this.tokenized = true;
                await this.processOrder(true, {
                    fromPaypalSessionComplete: true,
                    preserveErrors: true,
                    suppressPageErrors: true,
                });
            } catch (e) {
                releaseAdyenPayPalCheckoutLock(this);
                addDDAction('paypal-session-payment-fail', { detail: e });
                console.error('[Adyen|PayPal] Session order completion error:', e);
                throw e;
            }
        },
        // Create an Adyen Apple Pay component
        async createAdyenApplePayComponent() {
            // Adyen callbacks can run with a child-scope `this` that lacks
            // root-only methods (syncAddresses, addressSynced). Route cart
            // mutations through the root scope object.
            const checkoutApp = window.checkoutAppScope || defaultCheckoutAppNew;

            // Access payment methods response from the current context or global app instance
            const paymentMethodsResponse = checkoutApp.adyenPaymentMethodsResponse || window.checkoutAppInstance?.adyenPaymentMethodsResponse;
            if (!checkoutApp.adyenCheckout || !paymentMethodsResponse) {
                console.error('[Adyen] Checkout instance or payment methods not initialized', checkoutApp.adyenCheckout, paymentMethodsResponse);
                return;
            }

            // Check if Apple Pay is available
            const applePayMethod = paymentMethodsResponse.paymentMethods?.find(m => m.type === 'applepay');
            if (!applePayMethod) {
                console.warn('[Adyen] Apple Pay payment method not available');
                return;
            }

            try {
                // Destroy existing component if any
                this.destroyAdyenApplePayComponent();

                // Wait for the container to be available
                await new Promise(resolve => setTimeout(resolve, 250));
                // Find the visible container (there may be multiple due to desktop/mobile order summary instances)
                const containers = document.querySelectorAll('.adyen-applepay-container');
                let container = null;
                for (const el of containers) {
                    if (el.offsetParent !== null) {
                        container = el;
                        break;
                    }
                }
                // Fallback to first container if none are visibly detected
                if (!container) container = containers[0] || null;
                if (!container) {
                    console.error('[Adyen] Apple Pay container not found');
                    return;
                }

                const thisCart = this.cartState || this.checkoutOffer;
                const thisAmount = this.getCartAmountMinor(thisCart);
                const thisCurrency = thisCart.amount_local?.currency
                    ?? thisCart.amount?.currency
                    ?? (thisCart.amount_base ? thisCart.amount_base.currency : thisCart.amount.currency);

                // Prepare shipping options similar to PayPal
                const availableShippingCategories = (thisCart.available_shipping_categories || []).map(category => {
                    const shippingAmount = category.price_base?.amount?.decimal || category.price?.amount?.decimal || '0.00';
                    return {
                        id: category.id,
                        label: category.name,
                        amount: typeof shippingAmount === 'number' ? (shippingAmount / 100).toFixed(2) : shippingAmount.toString(),
                        detail: category.description || '',
                        type: 'final'
                    };
                });

                // Set the default selected shipping method
                let selectedShippingMethod = null;
                if (availableShippingCategories.length > 0) {
                    const lowestPriceCategory = availableShippingCategories.reduce((prev, curr) =>
                        parseFloat(curr.amount) < parseFloat(prev.amount) ? curr : prev
                    );
                    lowestPriceCategory.selected = true;
                    selectedShippingMethod = lowestPriceCategory;
                }

                const applePayConfiguration = {
                    showPayButton: true,
                    isExpress: true, // Required for onShippingContactSelected and onShippingMethodSelected callbacks
                    amount: {
                        value: thisAmount,
                        currency: thisCurrency
                    },
                    lineItems: thisCart.line_items?.map(item => {
                        const itemAmount = item.price_base?.amount || item.price?.amount || 0;
                        return {
                            label: item.name || 'Item',
                            amount: typeof itemAmount === 'number' ? (itemAmount / 100).toFixed(2) : itemAmount.toString(),
                            type: 'final'
                        };
                    }) || [],
                    // Request billing contact information from Apple Pay
                    // This tells Apple Pay to request billing address from the user's wallet
                    // Note: 'postalAddress' should include addressLines (street address), but this depends on the following:
                    // 1. Whether the user has a complete address stored in their Apple Wallet
                    // 2. Whether the user confirms/enters their address during the payment flow
                    // If addressLines is missing, check browser console logs to see what properties are available
                    requiredBillingContactFields: ['postalAddress', 'name', 'email'],
                    requiredShippingContactFields: ['postalAddress'],
                    // Optionally request email and phone if needed:
                    // requiredBillingContactFields: ['postalAddress', 'email', 'phone'],
                    // Alternative: If Adyen requires payment request options in a nested object:
                    // paymentRequest: {
                    //     requiredBillingContactFields: ['postalAddress']
                    // },

                    /**
                     * Adyen Web v6: onAuthorized fires before onSubmit and must be
                     * resolved so Apple Pay can continue. Also syncs wallet addresses
                     * to the checkout form (including visual_shipping for the UI).
                     */
                    onAuthorized: async ({ authorizedEvent, billingAddress, deliveryAddress }, actions) => {
                        try {
                            applyApplePayWalletAddressesToForm(checkoutApp.form, {
                                billingAddress,
                                deliveryAddress,
                                shippingContact: authorizedEvent?.shippingContact,
                                billingContact: authorizedEvent?.billingContact,
                            });

                            const walletEmail = authorizedEvent?.shippingContact?.emailAddress
                                || billingAddress?.email;
                            if (walletEmail && (!checkoutApp.form.user.email || checkoutApp.form.user.email !== walletEmail)) {
                                checkoutApp.form.user.email = walletEmail;
                            }

                            if (!checkoutApp.form.user.name?.trim() && checkoutApp.form.shipping_address?.name) {
                                checkoutApp.form.user.name = checkoutApp.form.shipping_address.name;
                            }

                            const sheetTotalBeforeSync = checkoutApp._applePaySheetTotalMinor;
                            checkoutApp.addressSynced = false;

                            const recalc = await checkoutApp.recalculateApplePayCart({
                                includeBilling: true,
                                runPostalLookup: false,
                                operation: 'applepay_on_authorized',
                            });

                            if (
                                sheetTotalBeforeSync !== null
                                && recalc.amountMinor !== sheetTotalBeforeSync
                            ) {
                                checkoutApp.checkoutErrors = [
                                    'Your order total changed while confirming Apple Pay. Please close Apple Pay and try again.',
                                ];
                                actions.reject();
                                return;
                            }

                            if (!checkoutApp.addressSynced) {
                                checkoutApp.checkoutErrors = [
                                    'We could not save your shipping address from Apple Pay. Please try again or use a different payment method.',
                                ];
                                actions.reject();
                                return;
                            }

                            actions.resolve();
                        } catch (error) {
                            console.error('[Adyen] Apple Pay onAuthorized error:', error);
                            actions.reject();
                        }
                    },

                    /**
                     * Validate contact fields before opening the Apple Pay modal.
                     */
                    onClick: (resolve, reject) => {
                        try {
                            this.validateContact();
                        } catch (e) {
                            console.error('Error in validateContact:', e);
                        }

                        if (this.contactErrors.length > 0) {
                            requestAnimationFrame(() => {
                                const firstInvalidElement = document.querySelector('[aria-invalid="true"]');
                                if (firstInvalidElement) {
                                    firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                            });
                            reject();
                            return;
                        }

                        checkoutApp._applePaySheetTotalMinor = checkoutApp.getCartAmountMinor();
                        resolve();
                    },

                    onShippingContactSelected: async (resolve, reject, event) => {
                        try {
                            const shippingContact = event.shippingContact;
                            if (!shippingContact) {
                                reject(new Error('No shipping contact provided'));
                                return;
                            }

                            const postalCode = shippingContact.postalCode;
                            if (!postalCode) {
                                const currentAmount = checkoutApp.getCartAmountMinor();
                                resolve({
                                    shippingMethods: checkoutApp.buildApplePayShippingMethods(
                                        checkoutApp.cartState || checkoutApp.checkoutOffer
                                    ),
                                    newTotal: {
                                        label: 'Total',
                                        amount: checkoutApp.formatApplePayTotalAmount(currentAmount),
                                        type: 'final',
                                    },
                                });
                                return;
                            }

                            const addressLine1 = shippingContact.addressLines?.[0]
                                || shippingContact.street
                                || shippingContact.addressLine1
                                || '';
                            const addressLine2 = shippingContact.addressLines?.[1]
                                || shippingContact.addressLine2
                                || '';

                            checkoutApp.form.shipping_address = {
                                name: `${shippingContact.givenName || ''} ${shippingContact.familyName || ''}`.trim(),
                                first_name: shippingContact.givenName || '',
                                last_name: shippingContact.familyName || '',
                                company_name: '',
                                country: shippingContact.countryCode || 'US',
                                address: addressLine1,
                                address2: addressLine2,
                                city: shippingContact.locality || shippingContact.city || '',
                                state: shippingContact.administrativeArea || shippingContact.state || '',
                                postal_code: postalCode,
                                phone: shippingContact.phoneNumber || checkoutApp.form.user.phone || '',
                            };
                            checkoutApp.form.shipping_same = false;
                            syncApplePayVisualShipping(checkoutApp.form);

                            const recalc = await checkoutApp.recalculateApplePayCart({
                                operation: 'applepay_shipping_contact_selected',
                            });

                            resolve({
                                shippingMethods: recalc.shippingMethods,
                                newTotal: recalc.newTotal,
                            });
                        } catch (error) {
                            console.error('[Adyen] Error handling shipping contact selection:', error);
                            reject(error);
                        }
                    },
                    onShippingMethodSelected: async (resolve, reject, event) => {
                        try {
                            const selectedMethod = event.shippingMethod;
                            if (!selectedMethod) {
                                reject(new Error('No shipping method selected'));
                                return;
                            }

                            const recalc = await checkoutApp.recalculateApplePayCart({
                                shippingCategoryId: selectedMethod.id,
                                runPostalLookup: false,
                                operation: 'applepay_shipping_method_selected',
                            });

                            resolve({
                                newTotal: recalc.newTotal,
                            });
                        } catch (error) {
                            console.error('[Adyen] Error handling shipping method selection:', error);
                            reject(error);
                        }
                    },
                    onSubmit: async (state, component, actions) => {
                        if (!state.isValid || !state.data) {
                            if (actions?.reject) {
                                try { actions.reject(); } catch (e) { console.warn('[Apple Pay] actions.reject() threw:', e); }
                            }
                            return;
                        }

                        console.log(`[Adyen] ApplePay State:`, state);
                        this.adyenApplePayState = state;
                        this.adyenApplePayActiveComponent = component;

                        try {
                            this.phoneStateSnapshot('applepay-onsubmit');
                            addDDAction(`applepay-payment-init`, { state: state });

                            const shippingContact = state.data.shippingContact;
                            const billingContact = state.data.billingContact
                                || state.data.paymentRequest?.billingContact
                                || state.billingContact
                                || shippingContact;

                            applyApplePayWalletAddressesToForm(this.form, {
                                shippingContact,
                                billingContact,
                            });

                            if (shippingContact?.emailAddress) {
                                if (!this.form.user.email || this.form.user.email !== shippingContact.emailAddress) {
                                    this.form.user.email = shippingContact.emailAddress;
                                }
                            }

                            if (!this.form.user.name && this.form.shipping_address.name) {
                                this.form.user.name = this.form.shipping_address.name;
                            }

                            this.adyenApplePayState = state;

                            await this.applePayCompleteOrder(state.data, component);

                            this.completeApplePayWalletSuccess(component);
                            if (actions?.resolve) {
                                try {
                                    actions.resolve({ resultCode: 'Authorised' });
                                } catch (e) {
                                    console.warn('[Apple Pay] actions.resolve() threw:', e);
                                }
                            }
                        } catch (error) {
                            console.error('[Adyen] Apple Pay onSubmit caught:', error);
                            if (actions?.reject) {
                                try { actions.reject(); } catch (e) { console.warn('[Apple Pay] actions.reject() threw:', e); }
                            }
                            await this.handleApplePayFlowFailure(error, component);
                        }
                    },
                    onError: async (error) => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        const isCancellation = error?.message?.includes('CANCEL') ||
                            error?.message?.includes('cancelled') ||
                            error?.message?.includes('dismissed') ||
                            error?.code === 'CANCEL';

                        if (isCancellation) {
                            this.dismissApplePayWallet(this.adyenApplePayActiveComponent);
                            return;
                        }

                        console.error('[Adyen] Apple Pay onError:', error);
                        await this.handleApplePayFlowFailure(error, this.adyenApplePayActiveComponent);
                    },
                    onApplePayCodeClose: () => {
                        this.dismissApplePayWallet(this.adyenApplePayActiveComponent);
                    }
                };

                this.adyenApplePayComponent = new window.AdyenWeb.ApplePay(this.adyenCheckout, applePayConfiguration);
                this.adyenApplePayComponent.mount(container);
            } catch (error) {
                console.error('[Adyen] Error creating Apple Pay component:', error);
                this.checkoutErrors = [error.message || 'Failed to initialize Apple Pay'];
            }
        },
        applePayCompleteOrder: async function (applePayData, component = null) {
            // Honor the user-typed phone (FET-2669 / Klaviyo SMS null-phone
            // crash): if anything in the wallet flow blanked out
            // form.user.phone, restore from the Scene 1 snapshot before
            // we hit /api/cart/pay. Emit a state snapshot for DataDog
            // either way so we can see what we actually shipped.
            this.enforcePhoneLock();
            this.phoneStateSnapshot('applepay-complete-entry');

            // Only process if we have actual payment data
            if (!applePayData || !applePayData.paymentMethod) {
                console.error('[Adyen] Cannot complete order - no paymentMethod available:', applePayData);
                throw new Error('Apple Pay payment data is missing. Please try again.');
            }
            console.log(`applePayData: ${JSON.stringify(applePayData)}`);

            addDDAction(`applepay-payment-init`, { applePayData: applePayData });
            try {
                // Extract billing address from Apple Pay data if available
                // Check multiple possible locations for billing contact
                const billingContact = applePayData.billingContact
                    || applePayData.billingAddress
                    || (this.adyenApplePayState?.data?.billingContact)
                    || (this.adyenApplePayState?.data?.paymentRequest?.billingContact)
                    || (applePayData.paymentMethod?.billingContact)
                    || null;

                // Also check payment token data structure
                const paymentTokenBillingContact = applePayData.paymentMethod?.paymentData?.billingContact
                    || applePayData.paymentMethod?.token?.paymentData?.billingContact
                    || null;

                // Use payment token billing contact if available and more complete
                const finalBillingContact = (paymentTokenBillingContact?.addressLines?.length > 0)
                    ? paymentTokenBillingContact
                    : billingContact;

                // Extract shipping address from multiple sources
                const shippingContact = applePayData.deliveryAddress
                    || applePayData.shippingContact
                    || applePayData.shippingAddress
                    || (this.adyenApplePayState?.data?.shippingContact)
                    || (applePayData.paymentMethod?.shippingContact)
                    || null;

                // Also check payment token data structure for shipping address
                const paymentTokenShippingContact = applePayData.paymentMethod?.paymentData?.shippingContact
                    || applePayData.paymentMethod?.token?.paymentData?.shippingContact
                    || null;

                // Use payment token shipping contact if available and more complete
                const finalShippingContact = (paymentTokenShippingContact?.addressLines?.length > 0)
                    ? paymentTokenShippingContact
                    : shippingContact;

                // Set a billing address from Apple Pay data (or keep existing if not available)
                if (finalBillingContact) {
                    this.form.billing_address = transformApplePayContactToFormAddress(
                        finalBillingContact,
                        this.form.billing_address
                    );
                }

                // Set a shipping address from Apple Pay data (or keep existing if not available)
                if (finalShippingContact) {
                    this.form.shipping_address = transformApplePayContactToFormAddress(
                        finalShippingContact,
                        this.form.shipping_address
                    );
                } else if (finalBillingContact) {
                    // If no shipping contact, but we have billing, use billing as shipping
                    this.form.shipping_address = transformApplePayContactToFormAddress(
                        finalBillingContact,
                        this.form.shipping_address
                    );
                }

                syncApplePayVisualShipping(this.form);

                // Ensure we have at least a postal code for the shipping address
                if (!this.form.shipping_address?.postal_code) {
                    if (this.form.billing_address?.postal_code) {
                        this.form.shipping_address.postal_code = this.form.billing_address.postal_code;
                    } else {
                        console.error('[Adyen] No postal code available for shipping address');
                        throw new Error('Shipping address requires a postal code. Please provide a complete address.');
                    }
                }

                // Ensure we have at least a city for shipping address
                if (!this.form.shipping_address?.city && this.form.billing_address?.city) {
                    this.form.shipping_address.city = this.form.billing_address.city;
                }

                // Adyen requires street field - use city as fallback if no address line provided
                if (!this.form.shipping_address?.address) {
                    if (this.form.shipping_address?.city) {
                        this.form.shipping_address.address = this.form.shipping_address.city;
                    } else if (this.form.billing_address?.address) {
                        this.form.shipping_address.address = this.form.billing_address.address;
                    } else if (this.form.billing_address?.city) {
                        this.form.shipping_address.address = this.form.billing_address.city;
                    } else {
                        // Last resort: use postal code as address
                        this.form.shipping_address.address = `Address in ${this.form.shipping_address.postal_code}`;
                    }
                }

                // Ensure cart has shipping address before tokenization
                const hasMinimumAddress = this.form.shipping_address?.postal_code &&
                    (this.form.shipping_address?.city || this.form.shipping_address?.address);

                const sheetTotalBeforePay = this._applePaySheetTotalMinor;
                const finalRecalc = await this.recalculateApplePayCart({
                    includeBilling: true,
                    runPostalLookup: false,
                    operation: 'applepay_pre_pay_final',
                });

                if (
                    sheetTotalBeforePay !== null
                    && finalRecalc.amountMinor !== sheetTotalBeforePay
                ) {
                    throw new Error('Your order total changed during Apple Pay checkout. Please close Apple Pay and try again.');
                }

                this.addressSynced = true;

                // Final validation: ensure cart has shipping address before tokenization
                if (!this.cartState?.shipping_address_id && !hasMinimumAddress) {
                    console.error('[Adyen] Cannot tokenize: No shipping address available');
                    throw new Error('Shipping address is required. Please provide a complete shipping address.');
                }

                // Sync guest identity from wallet before combined vault+auth pay
                const walletNameSource = finalBillingContact || finalShippingContact;
                if (!this.form.user.name?.trim() && walletNameSource) {
                    this.form.user.name = `${walletNameSource.givenName || ''} ${walletNameSource.familyName || ''}`.trim();
                }
                const walletEmail = finalShippingContact?.emailAddress
                    || shippingContact?.emailAddress
                    || applePayData.shippingContact?.emailAddress
                    || this.adyenApplePayState?.data?.shippingContact?.emailAddress;
                if (!this.form.user.email?.trim() && walletEmail) {
                    this.form.user.email = walletEmail;
                }

                delete this.form.payment.preAuth;
                ADYEN_SCHEME_ENCRYPTED_KEYS.forEach((key) => {
                    if (this.form.payment?.details?.paymentMethod?.[key]) {
                        delete this.form.payment.details.paymentMethod[key];
                    }
                });

                this.form.payment.gateway = applePayData.gateway ?? this.gateway;
                this.form.payment.apple_pay_authorized_amount_minor = finalRecalc.amountMinor;

                const sanitizedPaymentDetails = buildApplePayPaymentDetails(
                    applePayData,
                    finalBillingContact,
                    finalShippingContact,
                    this.form.billing_address,
                    this.form.shipping_address
                );

                this.form.payment.token = await this.computeHash(
                    JSON.stringify(sanitizedPaymentDetails.paymentMethod)
                ).then(hashValue => { return hashValue });

                this.form.payment.details = {
                    ...sanitizedPaymentDetails,
                    browserInfo: applePayData.browserInfo ?? sanitizedPaymentDetails.browserInfo,
                };

                this.tokenized = true;
                await this.processOrder(true, { preserveErrors: true, suppressPageErrors: true });
            } catch (e) {
                throw e;
            }
        },
        // Pull every useful detail out of an error thrown during the Apple Pay
        // submission flow, including the HTTP response body, so we can show a
        // helpful message and have something to attach to Datadog / pass to QA.
        async captureApplePayError(e) {
            const details = {
                timestamp: new Date().toISOString(),
                url: typeof window !== 'undefined' ? window.location?.href : null,
                message: e?.message || '(no message)',
                name: e?.name,
                code: e?.code,
                stack: e?.stack,
                status: e?.status,
                statusText: e?.statusText,
                errors: e?.errors,
                responseStatus: null,
                responseStatusText: null,
                responseUrl: null,
                responseBody: null,
            };

            if (e?.data !== undefined && e?.data !== null) {
                details.responseBody = e.data;
                if (e.status && !details.responseStatus) {
                    details.responseStatus = e.status;
                }
            }

            if (e?.response) {
                details.responseStatus = details.responseStatus || e.response.status;
                details.responseStatusText = e.response.statusText;
                details.responseUrl = e.response.url;

                if (!details.responseBody) {
                    try {
                        const cloned = typeof e.response.clone === 'function' ? e.response.clone() : null;
                        if (cloned) {
                            const text = await cloned.text();
                            try {
                                details.responseBody = JSON.parse(text);
                            } catch (_) {
                                details.responseBody = text;
                            }
                        }
                    } catch (parseErr) {
                        console.warn('[Apple Pay] Could not read error response body:', parseErr);
                    }
                }
            }

            return details;
        },
        formatApplePayErrorMessage(errorDetails) {
            const gatewayFromErrors = errorDetails?.errors?.gateway;
            if (Array.isArray(gatewayFromErrors) && gatewayFromErrors.length > 0) {
                const gatewayMessage = gatewayFromErrors
                    .map(item => (typeof item === 'string' ? item : JSON.stringify(item)))
                    .filter(Boolean)
                    .join(' ');
                if (gatewayMessage.trim()) {
                    return gatewayMessage.trim();
                }
            }
            if (typeof gatewayFromErrors === 'string' && gatewayFromErrors.trim()) {
                return gatewayFromErrors.trim();
            }

            const body = errorDetails?.responseBody;
            if (body && typeof body === 'object') {
                const gatewayErrors = body.errors?.gateway;
                if (Array.isArray(gatewayErrors) && gatewayErrors.length > 0) {
                    const gatewayMessage = gatewayErrors.map(item => (typeof item === 'string' ? item : JSON.stringify(item))).filter(Boolean).join(' ');
                    if (gatewayMessage.trim()) {
                        return gatewayMessage.trim();
                    }
                }
                if (typeof gatewayErrors === 'string' && gatewayErrors.trim()) {
                    return gatewayErrors.trim();
                }

                if (typeof body.message === 'string' && body.message.trim() && body.message.trim() !== 'The given data was invalid.') {
                    return body.message.trim();
                }
                const errs = body.errors || body.data?.errors;
                if (errs && typeof errs === 'object') {
                    const flat = [];
                    Object.values(errs).forEach(v => {
                        if (Array.isArray(v)) {
                            v.forEach(item => flat.push(typeof item === 'string' ? item : JSON.stringify(item)));
                        } else if (typeof v === 'string') {
                            flat.push(v);
                        } else if (v != null) {
                            flat.push(JSON.stringify(v));
                        }
                    });
                    if (flat.length) return flat.join(' ');
                }
            }
            if (typeof body === 'string' && body.trim().length) {
                const trimmed = body.trim();
                if (!trimmed.startsWith('(failed to parse response:')) {
                    return trimmed.substring(0, 300);
                }
            }
            if (errorDetails?.message && errorDetails.message !== 'The given data was invalid.') {
                return errorDetails.message;
            }
            return 'Apple Pay payment failed';
        },
        // Render a high-z-index, fixed-position banner that survives the Apple
        // Pay sheet dismissing. This is the only way to make sure the user (or
        // QA) actually sees what failed — the native wallet sheet covers the
        // whole page-level checkout UI while it's open.
        showApplePayErrorBanner(message, errorDetails) {
            try {
                const existing = document.getElementById('applepay-error-banner');
                if (existing) existing.remove();

                const safe = (s) => String(s ?? '').replace(/[&<>"']/g, c => (
                    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
                ));
                const httpInfo = errorDetails?.responseStatus
                    ? ` (HTTP ${safe(errorDetails.responseStatus)})`
                    : '';

                const banner = document.createElement('div');
                banner.id = 'applepay-error-banner';
                banner.setAttribute('role', 'alert');
                banner.style.cssText = [
                    'position:fixed',
                    'top:0',
                    'left:0',
                    'right:0',
                    'z-index:2147483647',
                    'background:#ffebee',
                    'color:#b71c1c',
                    'padding:14px 44px 14px 16px',
                    'border-bottom:3px solid #c62828',
                    'font-family:-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    'font-size:14px',
                    'line-height:1.4',
                    'box-shadow:0 4px 12px rgba(0,0,0,0.25)',
                    'word-wrap:break-word',
                    'max-height:60vh',
                    'overflow-y:auto',
                ].join(';') + ';';

                banner.innerHTML = [
                    `<strong>Apple Pay payment failed${httpInfo}:</strong>`,
                    `<div style="margin-top:4px;">${safe(message)}</div>`,
                    `<button type="button" aria-label="Dismiss" style="position:absolute;top:6px;right:8px;background:transparent;border:0;font-size:24px;line-height:1;color:#b71c1c;cursor:pointer;padding:4px 8px;">×</button>`,
                ].join('');

                banner.querySelector('button').addEventListener('click', () => banner.remove());
                document.body.appendChild(banner);

                setTimeout(() => { if (banner.isConnected) banner.remove(); }, 45000);
            } catch (bannerErr) {
                console.error('[Apple Pay] Failed to render error banner:', bannerErr);
            }
        },
        dismissApplePayWallet(component = null, status = 'error') {
            try {
                const applePayComponent = component || this.adyenApplePayActiveComponent || this.adyenApplePayComponent;

                if (applePayComponent) {
                    if (typeof applePayComponent.setStatus === 'function') {
                        try { applePayComponent.setStatus(status); } catch (e) { console.warn('[Apple Pay] setStatus() threw:', e); }
                    }
                    if (typeof applePayComponent.close === 'function') {
                        try { applePayComponent.close(); } catch (e) { console.warn('[Apple Pay] close() threw:', e); }
                    }
                }

                this.adyenApplePayActiveComponent = null;
            } catch (error) {
                console.error('[Adyen] Error dismissing Apple Pay wallet:', error);
            }
        },
        completeApplePayWalletSuccess(component = null) {
            this.dismissApplePayWallet(component, 'success');
        },
        async remountAdyenApplePayComponent() {
            if (this.currentScene !== 2 || this.payment_processor !== 'apple_pay' || !this.isAdyenGateway) {
                return;
            }

            try {
                this.destroyAdyenApplePayComponent();
                await new Promise(resolve => setTimeout(resolve, 300));
                await this.createAdyenApplePayComponent();
            } catch (err) {
                console.error('[Apple Pay] Failed to re-mount component after error:', err);
            }
        },
        async handleAdyenPayPalFlowFailure(error, options = {}) {
            const reload = options.reload !== false;
            const preserveErrors = options.preserveErrors === true;
            const checkoutApp = window.checkoutAppScope || defaultCheckoutAppNew;

            checkoutApp.processing = false;
            checkoutApp.full_processing = false;
            checkoutApp.checkingOut = false;

            resetAdyenPayPalCheckoutGuards(checkoutApp);

            checkoutApp.adyenPayPalState = null;
            checkoutApp.tokenized = false;

            // Adyen PayPal sessions are one-time; after a failed finalize we need a fresh session.
            checkoutApp.adyenPayPalSession = null;

            if (checkoutApp.form?.payment?.details) {
                const details = checkoutApp.form.payment.details;
                delete details.sessionResult;
                delete details.sessionData;
                delete details.sessionId;
                delete details.sessionBased;
                delete details.resultCode;
            }

            checkoutApp.destroyAdyenPayPalComponent();

            addDDAction('paypal-payment-fail-recover', {
                message: error?.message || String(error || ''),
                reload,
            });

            if (!preserveErrors && error) {
                checkoutApp.clearAllErrors();
                const gateway = error.errors?.gateway;
                if (Array.isArray(gateway) && gateway.length) {
                    checkoutApp.paymentErrors = gateway;
                } else if (typeof gateway === 'string' && gateway.trim()) {
                    checkoutApp.paymentErrors = [gateway.trim()];
                } else if (error.message) {
                    checkoutApp.paymentErrors = [error.message];
                }
            }

            if (reload && checkoutApp.isAdyenGateway && checkoutApp.payment_processor === 'paypal') {
                try {
                    await checkoutApp.createAdyenPayPalComponent();
                } catch (remountError) {
                    console.error('[Adyen|PayPal] Failed to remount PayPal after checkout error:', remountError);
                    if (!preserveErrors) {
                        checkoutApp.paymentErrors = [
                            remountError.message || 'PayPal could not be reloaded. Please refresh the page and try again.',
                        ];
                    }
                }
            }
        },
        async handleApplePayFlowFailure(error, component = null) {
            const errorDetails = await this.captureApplePayError(error);
            addDDAction(`applepay-payment-fail`, { detail: errorDetails });
            console.error('[Apple Pay] Payment failed:', errorDetails);
            console.error('[Apple Pay] Raw error object:', error);
            window.__lastApplePayError = errorDetails;

            const userMessage = this.formatApplePayErrorMessage(errorDetails);
            this.clearAllErrors();
            if (userMessage && userMessage.trim()) {
                this.checkoutErrors = [userMessage];
            }

            this.dismissApplePayWallet(component, 'error');
            this.showApplePayErrorBanner(userMessage, errorDetails);
            await this.remountAdyenApplePayComponent();
        },
        closeApplePayUI(component = null) {
            try {
                this.dismissApplePayWallet(component);

                const applePayComponent = component || this.adyenApplePayComponent;
                if (applePayComponent && typeof applePayComponent.unmount === 'function') {
                    try { applePayComponent.unmount(); } catch (e) { console.warn('[Apple Pay] unmount() threw:', e); }
                }

                this.adyenApplePayComponent = null;
            } catch (error) {
                console.error('[Adyen] Error closing Apple Pay UI:', error);
            }
        },
        // NOTE: The former updateAdyenPayPalComponent()/updatePaypalPaymentAmount()
        // helpers were removed. They refreshed only the PayPal *amount* via
        // /api/adyen/paypal/update, which does not re-send lineItems — so a
        // cart change (e.g. adding a CDP/bump) updated the total but never the
        // line-item breakdown shown in the PayPal lightbox. reloadComponents()
        // now always calls createAdyenPayPalComponent() to rebuild against a
        // fresh session whose lineItems reflect the current cart. The
        // /api/adyen/paypal/update endpoint is still used directly inside the
        // lightbox shipping callbacks (onShippingAddressChange), where only the
        // amount/delivery needs to change mid-flow.
        // Update Adyen Apple Pay component (recreate without session refresh)
        // Note: Adyen sessions don't need to be refreshed when amounts change.
        // The amount is sent when submitting the payment, not when creating the session.
        async updateAdyenApplePayComponent() {
            if (!this.isAdyenGateway || this.payment_processor !== 'apple_pay') {
                return;
            }

            try {
                // Simply destroy and recreate the component - no session refresh needed
                this.destroyAdyenApplePayComponent();

                await this.createAdyenApplePayComponent();
            } catch (error) {
                console.error('[Adyen] Error updating Apple Pay component:', error);
            }
        },

        syncCheckoutRootLayout() {
            syncNrWelcomeRootClass(this.customCheckoutOfferData);
        },

        async reloadComponents(){
            switch(this.gateway) {
                case 'Braintree':
                case 'Braintree_Testing':
                    if(this.payment_processor === 'paypal') {
                        await this.createPaypalInstance();
                    }
                    break;
                case 'Adyen':
                case 'Adyen_Testing':
                    if(this.payment_processor === 'apple_pay') {
                        await this.updateAdyenApplePayComponent();
                    }
                    if(this.payment_processor === 'paypal') {
                        // Always fully re-initialize PayPal whenever the cart
                        // changes (CDP/bump toggle, shipping selection, address
                        // sync). The PayPal lightbox line items + breakdown are
                        // baked into the Adyen session at creation time on the BE
                        // (buildPaypalLineItems in AdyenSessionController) and are
                        // NOT mutated by the /paypal/update amount endpoint. The
                        // previous code only refreshed the amount when a
                        // paymentData already existed and otherwise just staged a
                        // flag — so adding a CDP after the button mounted left the
                        // stale session in place and the CDP never appeared as a
                        // line item. createAdyenPayPalComponent() destroys the old
                        // component and builds a fresh session, guaranteeing the
                        // line items always match the current cart.
                        await this.createAdyenPayPalComponent();
                    }
                    break;
            }
            this.syncCheckoutRootLayout();
        },
        async toggleSpecialOffer(offerId) {
            if (!this.processing) {
                this.clearAllErrors();
                this.processing = true;
                try {
                    if (this.specialOffersSelected.includes(offerId)) {
                        const found = this.cartState.order_offers.find(item => item.offer && item.offer.id === offerId);
                        if (!found) {
                            throw new Error('Offer not found in cart');
                        }


                        const removeResult = await window.CheckoutAPI.delete(this, `/api/cart/offer/${found.id}`, {
                            context: { operation: 'remove_offer' },
                            headers: getHeaders(),
                            timeout: 15000
                        });

                        if (!removeResult.success) {
                            if (!removeResult.handled) {
                                throw new Error(removeResult.message || 'Failed to remove offer');
                            }
                            return; // Our system handled error
                        }

                        const data = removeResult.data;

                        this.specialOfferCartId = null;
                        this.specialOffersSelected = this.specialOffersSelected.filter(id => id !== offerId);

                        const result = this.convertCurrency(data.data, this.countriesList, this.form.billing_address.country);
                        this.checkoutOffer = result;
                        this.cartState = result;
                        await this.reloadComponents();
                    } else {
                        const addResult = await window.CheckoutAPI.post(this, '/api/cart/offer', {
                            offer_id: offerId,
                            quantity: 1
                        }, {
                            context: { operation: 'add_offer' },
                            headers: getHeaders()
                        });

                        if (!addResult.success) {
                            if (!addResult.handled) {
                                throw new Error(addResult.message || 'Failed to add offer');
                            }
                            return; // Our system handled error
                        }

                        const data = addResult.data;

                        this.specialOffersSelected.push(offerId);
                        const result = this.convertCurrency(data.data, this.countriesList, this.form.billing_address.country);
                        this.checkoutOffer = result;
                        this.cartState = result;
                        await this.reloadComponents();
                    }
                } catch (error) {
                    console.error('Error toggling special offer:', error);
                    this.clearAllErrors();
                    this.checkoutErrors = [error.message || 'Failed to update offer. Please try again.'];
                    const errorElement = document.querySelector('.checkout-errors') || document.querySelector('#checkout-app-new');
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth' });
                    }
                } finally {
                    this.processing = false;
                }
            }
        },
        async computeHash(inputString) {
            const encoder = new TextEncoder();
            const data = encoder.encode(inputString);
            const hashBuffer = await crypto.subtle.digest('SHA-1', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
        },
    };

    window.countdownTimerEnabled = isCountdownTimerEnabled(
        resolveCustomCheckoutOfferDataForCartSticky(checkoutOffer),
        skin,
    );
    if (typeof window.startCheckoutCountdownTimer === 'function') {
        window.startCheckoutCountdownTimer();
    }

    syncNrWelcomeRootClass(
        resolveCustomCheckoutOfferDataForCartSticky(
            typeof cartHydration !== 'undefined' ? cartHydration : (typeof checkoutOffer !== 'undefined' ? checkoutOffer : null),
        ),
    );

    // Create an app instance
    const checkoutApp = createApp(defaultCheckoutAppNew);
    // Petite-Vue createApp() returns a mount wrapper; the reactive checkout
    // scope (form, syncAddresses, addressSynced, etc.) lives on
    // defaultCheckoutAppNew. Expose both for callbacks that need root state.
    window.checkoutAppInstance = checkoutApp;
    window.checkoutAppScope = defaultCheckoutAppNew;
    if (window.__pendingCheckoutAdyenSdkError) {
        defaultCheckoutAppNew.checkoutErrors = [window.__pendingCheckoutAdyenSdkError];
        delete window.__pendingCheckoutAdyenSdkError;
    }
    // Mount the app
    checkoutApp.mount('#checkout-app-new');

    if (defaultCheckoutAppNew.customCheckoutOfferData?.funnel_override) {
        defaultCheckoutAppNew.primeFunnelOverride().catch((error) => {
            console.warn('Failed to prime funnel override on init:', error);
        });
    }
})();

